import { getMemberApprovedOnDate } from "../memberApprovalDate";
import { googleAddressToMemberFields } from "../../../../utils/googleAddressAutocomplete";
import { useCmpOrganizationNoun } from "../../../../contexts/CmpOrganizationNounContext";
import {
  applyCmpOrganizationNoun,
  getCmpBoardWording,
} from "../../../../utils/cmpOrganizationNoun";
import { memberHearAboutOptions as hearAboutOptions, memberReasonForJoiningOptions as reasonForJoiningOptions, updateMemberSocialPlatforms } from "../memberInsightOptions";
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getMemberInvoiceRecipient } from "./memberInvoiceRecipient";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { DateTime } from "luxon";
import { normalizeCmpTimeZone } from "../../../../utils/cmpTimeZones";
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import {
  AffinityProgramPartnerActivationStatusEnum,
  ChamberMemberCategory,
  ChamberMemberInput,
  ChamberMemberProMembershipModelPaymentResponsibilityEnum,
  ChamberMemberProMembershipPricingModelEnum,
  ChamberMemberProPaymentScheduleEnum,
  ChangeCmpMemberMembershipInput,
  CmpExternalMembershipStandingEnum,
  CmpMemberSectionEnum,
  CmpMemberRenewalStepStateEnum,
  CmpStaffNoteFieldsFragment,
  IdaGenerateMode,
  useAddCmpMemberStaffNoteMutation,
  CustomContactInput,
  CmpMemberApplicationSourceEnum,
  CmpMemberDetailFieldsFragment,
  CmpMemberLifecycleStatusEnum,
  CmpPaymentRequestStateEnum,
  CmpPaymentChoiceTypeEnum,
  GetCmpMemberDetailQuery,
  LoginInfoEmailTypes,
  useActivateCmpMemberMutation,
  useApproveCmpMemberBoardMutation,
  useArchiveCmpMemberMutation,
  useChamberMemberChangeAdminConsoleLoginEmailMutation,
  useGetAllAffinityProgramPartnersForChamberQuery,
  useGetAllCategoriesQuery,
  useGetCmpManageMembersQuery,
  useGetCmpMemberDetailQuery,
  useGetMemberDashboardLoginAvailabilityLazyQuery,
  useGetSampleLoginInfoEmailQuery,
  useResendCmpInvoicePaymentRequestMutation,
  useRecordCmpMemberManualPaymentMutation,
  useSendCmpInvoicePaymentRequestMutation,
  useSendLoginInfoEmailToChamberMemberMutation,
  useSetNewTemporaryChamberMemberPasswordMutation,
  useStartCmpMemberApplicationFromExistingMutation,
  useSuspendCmpMemberMutation,
  useCancelCmpMemberRenewalMutation,
  useUncancelCmpMemberRenewalMutation,
  useUpdateCmpMemberMutation,
  UserType,
  CmpMemberStatusEnum,
} from "../../../../graphql/types";
import { CMP_PERMISSION_VALUES } from "../../MyChamber/ChamberSettings/usersPermissions/permissionCatalogue";
import SearchableSelectField from "../../../common/dashboard/SearchableSelectField";
import {
  MEMBER_CHARITY_REGISTRATION_NUMBER_FIELD_ID,
  MEMBER_CHARITY_REGISTRATION_NUMBER_LABEL,
  MEMBER_CHARITY_REGISTRATION_NUMBER_PLACEHOLDER,
  shouldShowMemberCharityRegistrationNumber,
} from "../memberCharityRegistration";
import TagInputField, { memberSeoTagSuggestions } from "../../../common/dashboard/TagInputField";
import IdaGenerateDialog from "../../../common/dashboard/IdaGenerateDialog";
import MembershipRenewalStep from "./MembershipRenewalStep";
import { getLatestMemberRenewalDetail, getMemberRenewalFormDetail, getMemberRenewalStatusCard } from "./memberRenewalScenario";
import { isTierMembershipModel } from "../../MyChamber/ChamberSettings/settingsMappers";
import { getDisplayedMembershipAddOns } from "./memberDisplayedAddOns";
import SendCredentialsDialog, { resolveMemberCredentialEmailPreview } from "./SendCredentialsDialog";
import CustomContactCards from "./CustomContactCards";
import { getChangedCustomContactErrors, getMemberContactCopySource, normalizeCustomContact } from "./memberContactFields";
import { getMemberBillingSnapshot } from "./memberBillingSnapshot";
import {
  getMemberBillingFormAddress,
  getStoredBillingFormAddress,
  keepsStoredBlankBillingAddress,
  MemberFormAddress,
} from "./memberBillingAddressFallback";
import MemberStaffNotesCard from "../MemberStaffNotesCard";
import useMemberContactAccounts, { splitContactAccessDraftIds } from "./useMemberContactAccounts";
import MemberActivitySections from "../MemberActivitySections";
import GovernanceInvolvementCard from "../GovernanceInvolvementCard";
import { getMemberUrlFieldErrors } from "../memberUrlValidation";
import SectionSaveReset from "../../../common/dashboard/SectionSaveReset";
import DesignDialog, { DesignDialogButton } from "../../../common/dashboard/DesignDialog";
import { applySavedMemberForm, applySavedMemberSection, confirmMemberIdentityRevision, isCurrentMemberReadback, isMemberSectionDirty, memberSectionSaveOrder, mergeMemberSection, memberSectionErrorKey, MemberContactSaveTarget } from "./memberSectionSave";
import { provinceOptions as memberProvinceOptions } from "../../../common/utilities/ProvincePicker";
import { RoutePaths } from "../../../../enumStrings";
import useGetChamberFromUserIdInAuth from "../../../../hooks/useGetChamberFromUserIdInAuth";
import { useGetUserFromUserIdInAuthState } from "../../../../hooks/useGetUserFromUserIdInAuth";
import { PRESERVE_ERROR_FOR_CALLER_CONTEXT } from "../../../../utils/apolloErrorPolicy";
import {
  buildStaffCheckFirstFallback,
  buildStaffErrorFallback,
  getUserFacingErrorText,
  UserFacingError,
} from "../../../../utils/userFacingError";
import {
  useCmpGlobalSettings,
  useCmpGovernanceSettings,
  useCmpMembershipSettings,
} from "../../../../hooks/useCmpSettingsSection";
import { getCmpCreditCardPaymentsReady } from "../../../../utils/cmpCreditCardPayments";
import { usePageHeaderOverride } from "../../../Layout/PageContainer";
import UnsavedChangesPrompt from "../../../common/UnsavedChangesPrompt";
import { hasUnsavedFormChanges } from "../../../common/formStateDirty";
import { downloadTextFile } from "../../Invoicing/invoiceFileDownloads";
import {
  getOutboundEmailFailureMessage,
  getOutboundEmailSuccessMessage,
  wasOutboundEmailDeliveryConfirmed,
} from "../../Invoicing/invoiceDeliveryFeedback";
import MemberBillingStep, {
  chamberGroupInsuranceExistingInvoiceDescription,
  MemberBillingErrors,
  MemberBillingOption,
} from "../MemberBillingStep";
import UpdateCreditCardDialog, { getUpdateCreditCardSuccessDialog } from "./UpdateCreditCardDialog";
import MemberActionSuccessDialog, {
  MemberActionSuccessDialogState,
} from "../MemberActionSuccessDialog";
import MemberLoginEmailConflictDialog from "../MemberLoginEmailConflictDialog";
import MemberPastTransitionPaymentSurfaceFeedback, {
  getMemberPastTransitionRefundEvidence,
  getMemberPastTransitionPaymentSurfaceFeedback,
} from "../MemberPastTransitionPaymentSurfaceFeedback";
import MemberPastTransitionCleanupReplay, {
  PAYMENT_CLEANUP_PAGE_NOT_UPDATED_TEXT,
} from "../MemberPastTransitionCleanupReplay";
import MemberRestartApplicationAction from "../MemberRestartApplicationAction";
import MemberStatusModelPanel from "./MemberStatusModelPanel";
import { canManageCmpMembers } from "../memberApplicationPermissions";
import { memberPastTransitionApolloContext } from "../memberPastTransitionErrors";
import {
  committedMemberActionRefreshWarning,
  memberDeliveryRefreshWarning,
  refreshAfterCommittedMemberAction,
  runConfirmedMemberDelivery,
} from "../memberInvoiceDelivery";
import {
  getMembershipBasePrice,
  getMembershipInvoiceTaxRatePercentage,
} from "../memberMembershipPricing";
import { runCmpMemberManualPaymentOperation } from "../../Invoicing/invoiceFinancialOperation";
import {
  cmpLongDateFormatter,
  formatUtcCalendarDate,
  formatLongDate,
  getUtcCalendarDateStartUtcMilli,
} from "../../../common/dashboard/formatters";
import {
  buildProfileUpdateMembershipInput,
  getMemberMembershipPresentation,
} from "../memberProfileMembership";
import {
  gateUpdateMemberPaymentScheduleMutation,
  getMemberPaymentScheduleDisplayError,
  getMemberSelectablePaymentSchedules,
  normalizeMemberPaymentSchedule,
  shouldShowMemberPaymentScheduleSelection,
} from "../memberPaymentSchedule";
import { wizardGoldButtonSx } from "../../../common/dashboard/formStyles";
import {
  checkboxControlSx,
  choiceControlLabelSx,
  consumeUpdateMemberNavigationMemberId,
  cpaGold,
  FormCalendarDateField,
  FormCountryField,
  FormPhoneField,
  FormSelectField,
  FormTextField,
  getUpdateMemberNavigationMemberId,
  getLogoInputValue,
  LogoUploadField,
  MemberStepProgress,
  memberRequestApolloContext,
  updateMemberWorkflowSteps,
  MemberDesignSurface,
  IdaGenerateControl,
  useMemberIdaGeneration,
  getMemberEstablishedYearOptions,
  MultiOptionGroup,
  radioControlSx,
  SectionShell,
} from "../memberWorkflowShared";
import {
  ContactRole,
  MemberContactInfo,
  MemberCustomField,
  MemberStatus,
} from "../mockMemberData";
import { sortMemberCategoryOptions } from "../memberCategories";
import {
  chamberGroupInsurancePaymentMarker,
  emptyMemberThirdPartyBillingSnapshot,
  getAffiliatedPrimaryCompanyBillingSnapshot,
  getMemberBillingResponsibility,
  getMemberThirdPartyBillingFields,
  getMemberThirdPartyBillingSnapshotErrors,
  MemberBillingResponsibilityOption,
  MemberThirdPartyBillingSnapshot,
  getNoAffiliatedMemberMessage,
  getAffiliatedPrimaryCompanyConfirmMessage,
  getMemberBillingGroupInsuranceNotSetUpMessage,
  memberBillingPrimaryCompanyNotSelectedMessage,
  memberBillingThirdPartyPayerMissingMessage,
} from "../memberThirdPartyBilling";
import { EMAIL_FORMAT_MESSAGE, enterFieldMessage, selectFieldMessage } from "../../../../utils/fieldMessages";
import { formatStaffFaxInput, formatStaffTollFreeInput, getMemberPhoneDigits, getMemberTollFreeFaxErrors } from "../../../../utils/memberTollFreeFax";
import { getInvoicePartyFieldErrors } from "../../Invoicing/InvoicePartyFields";
import CalendarDateInput from "../../../common/dashboard/CalendarDateInput";
import {
  getMemberManualPaymentDateError,
  getMemberManualPaymentLatestDate,
  memberManualPaymentDateErrorMessage,
} from "./memberManualPaymentDate";
import {
  getMemberUnpaidAddOnPaymentRequestRows,
  MemberInvoicePaymentRequestCapability,
} from "../memberInvoicePaymentRequestCapability";
import {
  credentialFollowUpNote,
  getCredentialRefreshWarning,
  getMemberCredentialLifecycleBlock,
  getLatestCredentialSentAtUtcMilli,
  getMemberCredentialErrorMessage,
  getPersistedMemberLoginEmail,
  hasUnsavedMemberLoginEmailChange,
  getWebsiteApprovalLoginEmail,
  normalizeMemberLoginEmail,
  refreshMemberDetailAfterConfirmedCredentialAction,
} from "./memberCredentialActions";
import {
  getPendingMemberPaymentActionState,
  getPendingMemberPaymentAmountInCents,
} from "./memberPendingPaymentActions";
import { hasUnsavedMemberFormChanges, haveSameMemberSelectionValues } from "../memberFormDirtyState";
import { getMemberOwnershipDiversityInput, MemberOwnershipDiversityField } from "../MemberOwnershipDiversityField";
import {
  continueMemberLoginCollisionAction,
  createMemberLoginCollisionSingleFlight,
  getConfirmedMemberLoginEmail,
  getMemberLoginAvailabilityDecision,
  getMemberLoginChangeAvailabilityDecision,
  MemberLoginCollisionAction,
  memberLoginAvailabilityStaleError,
} from "./memberLoginCollisionFlow";
import { getMemberRestartApplicationGuard } from "../memberRestartApplicationGuard";
import MissingRequiredFieldsDialog, { MissingRequiredFieldsRow } from "../../../common/dashboard/MissingRequiredFieldsDialog";
import { getMemberBillingAddressRows, getMemberContactRows, isContactAccessDetailsMissing, MemberContactValidationInput } from "../memberContactValidation";
import MemberStatusReasonField, { MemberStatusReasonKind } from "../MemberStatusReasonField";
import { AutoRenewDeleteWarning, DeleteMemberBlockedReason, DeleteMemberMessage, shouldWarnAutoRenewOnDelete } from "../MemberDeleteNotices";
import { getMemberSizeDisplayValues } from "./memberSizeDisplay";

type BackendMemberDetail = GetCmpMemberDetailQuery["getCmpMemberDetail"];

type BillingResponsibilityOption = MemberBillingResponsibilityOption;

type PaymentMethodType =
  | "creditCard"
  | "free"
  | "invoice"
  | "cash"
  | "cheque"
  | "bankTransfer"
  | "eTransfer";

type MemberManualPaymentDraft = {
  manualPaymentMethodId: string;
  receivedDate: string;
  reference: string;
};

type ReferralTrackingInfo = {
  id: string;
  referralDate: string;
  becameMemberDate: string;
  companyReferralName: string;
  contactName: string;
  email: string;
  contactNumber: string;
  contactNumberExtension: string;
};

type AffinityProgramEnrollment = {
  id: string;
  affinityProgramPartnerId: string;
  affinityProgramPartnerName: string;
  idNumber: string;
  startDate: string;
  billingOption: string;
  status: string;
  notes: string;
};

const createEmptyAffinityDraft = (): Omit<AffinityProgramEnrollment, "id" | "affinityProgramPartnerName"> => ({
  affinityProgramPartnerId: "", idNumber: "", startDate: "", billingOption: "", status: "Active", notes: "",
});

export type UpdateMemberFormState = {
  administrative: {
    dateJoined: string;
    nextRenewalDate: string;
    externalMembershipStanding: CmpExternalMembershipStandingEnum;
  };
  association: {
    isAssociateCompanyMember: boolean;
    primaryMemberCompanyId: string;
  };
  membership: {
    membershipTypeId: string;
    fullTimeEmployees: string;
    partTimeEmployees: string;
    paymentSchedule: ChamberMemberProPaymentScheduleEnum | "";
    billingResponsibility: BillingResponsibilityOption;
    thirdPartyPayerName: string;
    thirdPartyPaymentNotes: string;
    thirdPartyBillingSnapshot: MemberThirdPartyBillingSnapshot;
    paymentMethod: PaymentMethodType | "";
    selectedAdditionalCostIds: string[];
    membershipDiscount: {
      enabled: boolean;
      code: string;
      description: string;
      amount: string;
    };
    autoRenew: boolean;
    emailCommunicationConsentConfirmed: boolean;
  };
  companyDetails: {
    charityRegistrationNumber: string;
    numberedCompany: string;
    yearEstablished: string;
    memberOwnershipDiversity: string[];
    howDidYouHearAboutUs: string[];
    howDidYouHearAboutUsOther: string;
    primaryReasonsForJoining: string[];
    primaryReasonForJoiningOther: string;
    activeSocialPlatforms: string[];
    boardOrCommitteeInterest: string;
  };
  insights: {
    chamberPerksAppHeardAbout: string;
    chamberPerksAppDownloaded: string;
    boardDirectorInterest: string;
    committeeInterest: string;
    governanceGroupIds: string[];
    communicationPreferences: string[];
  };
  referralTracking: ReferralTrackingInfo[];
  affiliatedChamberMember: {
    isAffiliatedChamberMember: boolean;
    chamberName: string;
  };
  affinityProgramEnrollments: AffinityProgramEnrollment[];
  directory: {
    companyName: string;
    companyDescription: string;
    companyWebsite: string;
    googleReviewUrl: string;
    mainCompanyContactName: string;
    mainCompanyContactEmail: string;
    mainCompanyPhone: string;
    mainCompanyPhoneExt: string;
    tollFreePhone: string;
    faxNumber: string;
    directoryCategoryIds: ChamberMemberCategory[];
    showInDirectory: boolean;
    seoTags: string[];
  };
  address: {
    hideStreetAddressInDirectories: boolean;
    streetAddress: string;
    city: string;
    provinceOrState: string;
    postalOrZipCode: string;
    country: string;
  };
  socialLinks: {
    facebookUrl: string;
    instagramUrl: string;
    threadsUrl: string;
    linkedinUrl: string;
    xTwitterUrl: string;
    tikTokUrl: string;
    youtubeUrl: string;
  };
  contacts: Record<ContactRole, MemberContactInfo & { mobilePhoneNumber: string }>;
  customContacts: CustomContactInput[];
  // Persisted "same as" checkbox state for each contact card. Stored in
  // extension.applicationMetadata so the checkboxes stay checked after
  // save/reload (MEM-UPDATE-005).
  contactSameAs: Record<ContactRole, boolean>;
  billingAddress: {
    streetAddress: string;
    city: string;
    provinceOrState: string;
    postalOrZipCode: string;
    country: string;
  };
  profile: {
    internalNotes: string;
    customFields: MemberCustomField[];
  };
  logo: {
    fileName: string;
    previewUrl: string;
  };
};

type MembershipFormState = UpdateMemberFormState["membership"];

export type UpdateMemberMembershipAdjustmentInput = {
  chamberId: string;
  memberId: string;
  membership: Omit<
    MembershipFormState,
    | "selectedAdditionalCostIds"
    | "emailCommunicationConsentConfirmed"
    | "paymentSchedule"
  > & {
    paymentSchedule: ChamberMemberProPaymentScheduleEnum;
  };
  operationId: string;
  expectedMemberUpdatedAtUtcMilli: number;
  expectedCurrentInvoiceId?: string;
};

type ConfirmationModal =
  | "resetPassword"
  | "approveBoard"
  | "suspend"
  | "activate"
  | "unsuspend"
  | "delete"
  | "restartApplication"
  | "cancelRenewal"
  | "uncancelRenewal"
  | null;

type CredentialModalMode = "sendCredentials" | "resetPassword" | null;

type PendingMemberLoginCollision = {
  id: number;
  action: MemberLoginCollisionAction;
  memberId: string;
  currentLoginEmail: string;
  actionLoginEmail: string;
  checkedLoginEmail: string;
  suggestedLoginEmail: string;
  submittedFormState?: UpdateMemberFormState;
  section?: CmpMemberSectionEnum;
  contactTarget?: MemberContactSaveTarget;
  credentialMode?: Exclude<CredentialModalMode, null>;
  credentialFormat?: "csv" | "html";
};

type StatusMessage = {
  severity: "success" | "info" | "warning" | "error";
  message: string;
};

const shouldMarkCmpModuleTestData = process.env.NODE_ENV === "development";
const descriptionMaxLength = 2000;
const updateMemberSeoTagSuggestions = memberSeoTagSuggestions.filter((tag) => tag !== "Canadian Company");

const socialPlatformOptions = [
  "LinkedIn",
  "Twitter/X",
  "Facebook",
  "Instagram",
  "TikTok",
  "YouTube",
  "None",
];

const yesNoOptions = ["Yes", "No"];
// Keep the existing persisted affirmative value while showing the design label.
const boardDirectorInterestAffirmative =
  "Interested in becoming a chamber board director";
const committeeInterestAffirmative = "Interested in joining a committee";

// Legacy records saved "Yes" from the old dropdown control; map it onto the
// affirmative radio value so the selection still shows.
const normalizeInterestValue = (
  value: string | null | undefined,
  affirmativeValue: string
) => {
  const storedValue = value || "";

  if (!storedValue) {
    return "";
  }

  return storedValue === "No" || storedValue === "Maybe"
    ? storedValue
    : affirmativeValue;
};

// Year established is intentionally year-only. Older dashboard builds stored
// the value as yyyy-01-01, so extract the year while hydrating those records.
const toEstablishedYearValue = (value?: string | null) => {
  const trimmedValue = (value || "").trim();
  const yearMatch = trimmedValue.match(/\d{4}/);
  return yearMatch ? yearMatch[0] : "";
};

const isValidEstablishedYear = (value: string) => {
  if (!value) {
    return true;
  }

  const year = Number(value);
  return /^\d{4}$/.test(value) && year >= 1000 && year <= new Date().getFullYear();
};

// Display formatter for native date-input values ("yyyy-mm-dd"). Parse the
// parts locally instead of new Date(string) so the rendered day never shifts
// a day backwards in negative-UTC-offset timezones.
const formatDateInputValueLong = (value?: string) => {
  if (!value) {
    return "-";
  }

  const [year, month, day] = value.split("-").map(Number);

  if (!year || !month || !day) {
    return value;
  }

  return cmpLongDateFormatter.format(new Date(year, month - 1, day));
};

const communicationPreferenceOptions = [
  "I prefer communication from the chamber to be done via phone",
  "I prefer communication from the chamber to be done via email. Member is aware they can unsubscribe at any time.",
];

const affinityBillingOptions = [
  "(DO NOT INVOICE MEMBER) Affinity Partner pays chamber monthly for member membership",
  "Affinity Partner invoices member separately",
  "Included with chamber membership",
  "Chamber invoices member",
];

const affinityDoNotInvoicePrefix = "(DO NOT INVOICE MEMBER)";

const getMemberProvinceOptions = (currentValue: string) => {
  const options = memberProvinceOptions.map((province) => ({
    value: province.label,
    label: province.label,
  }));
  if (currentValue && !options.some((option) => option.value === currentValue)) {
    const match = memberProvinceOptions.find((province) => province.value === currentValue);
    options.unshift({ value: currentValue, label: match?.label || currentValue });
  }
  return options;
};

const changedChoiceControlSx = (dirty: boolean) => dirty
  ? { outline: "2px solid #E1A731", outlineOffset: "-1px" }
  : {};

const affinityTableColumns = "grid grid-cols-[minmax(160px,1.2fr)_minmax(100px,.7fr)_minmax(95px,.7fr)_minmax(180px,1.3fr)_minmax(90px,.6fr)_minmax(150px,1fr)_90px]";

const AffinityDraftField = ({ label, description, children }: {
  label: string;
  description: string;
  children: React.ReactNode;
}) => <div className="mb-4 flex min-w-0 flex-col gap-[5px] lg:row-span-3 lg:mb-0 lg:grid lg:[grid-template-rows:subgrid]">
  <p className="text-sm font-bold leading-5">{label}</p>
  <p className="text-sm leading-[1.5] text-[#727272]">{description}</p>
  <div className="[&_label]:!hidden">{children}</div>
</div>;

// Local mirror of CreateMember's renderAffinityBillingOptionLabel — keep the
// two in sync. The step 3 mock bolds the "(DO NOT INVOICE MEMBER)"
// parenthetical wherever this billing option appears (draft Billing Option
// select, Enrolled Affinity Programs table cell, Update-modal radios).
const renderAffinityBillingOptionLabel = (option: string) =>
  option.startsWith(affinityDoNotInvoicePrefix) ? (
    <>
      <strong>{affinityDoNotInvoicePrefix}</strong>
      {option.slice(affinityDoNotInvoicePrefix.length)}
    </>
  ) : (
    option
  );

const affinityEnrollmentStatusOptions = ["Active", "Inactive"];
const membershipBillingOptions: {
  value: BillingResponsibilityOption;
  title: string;
  description: string;
  descriptionBold?: boolean;
  disabled?: boolean;
}[] = [
  {
    value: "memberPays",
    title: "Member will pay",
    description: "Invoice to be sent to member for payment",
  },
  {
    value: "affiliatedPrimaryCompany",
    title: "Invoice Affiliated Primary Company",
    description:
      "Member has requested that the invoice be sent to the primary company for payment. The affiliated company can be found in step 3 Member Insight",
  },
  {
    value: "otherThirdParty",
    title: "Other Third-Party Payer (Existing)",
    description:
      "Preserves the existing payer identity. Select another billing option to replace it.",
    disabled: true,
  },
  // Step-4 spec marks the three internal-use sub-lines below as bold.
  {
    value: "chamberGroupInsurance",
    title: chamberGroupInsurancePaymentMarker,
    description: chamberGroupInsuranceExistingInvoiceDescription,
    descriptionBold: true,
  },
  {
    value: "complimentary",
    title: "Complimentary No Charge Membership (For promotion/contra)",
    description: "Invoice created for Internal Use Not Sent to Member",
    descriptionBold: true,
  },
  {
    value: "freeLifetime",
    title: "Free Lifetime Membership",
    description: "Invoice created for Internal Use Not Sent to Member",
    descriptionBold: true,
  },
];



// CMP Dashboard (19) Update Member Form L1677-1685 (Monday 13304072079).
// The design has no final period.
const contactSameAsPrimaryLabel = "If you do not have the contact name and email below, check to add the primary contact";

const contactSections: Array<{
  role: ContactRole;
  title: string;
  /** Update Member Form intro copy. Each line break starts a new paragraph. */
  description: string;
  sameAsLabel: string;
  nameLabel: string;
  phoneLabel: string;
  requiredFields: Array<"name" | "email">;
}> = [
  {
    role: "primaryVoting",
    title: "Member Primary Contact (Voting Member)",
    description:
      "Update the member’s Primary Contact/Voting Member details below.\nThe Primary Contact can grant Member Dashboard access to other company contacts during registration or from their Member Dashboard. Contacts who have been granted access will have the access checkbox selected, and their login credentials will be sent automatically.\nYou can view each contact’s Member Dashboard access details below.",
    sameAsLabel: "Check here if same as Main Contact",
    nameLabel: "Primary Contact Name",
    phoneLabel: "Contact Number",
    requiredFields: ["name", "email"],
  },
  {
    role: "accountsPayable",
    title: "Accounts Payable Contact and Billing/Mailing Address",
    description:
      "Update the contact responsible for managing payments for this member, and the billing/mailing address",
    sameAsLabel: contactSameAsPrimaryLabel,
    nameLabel: "Billing Contact Name",
    phoneLabel: "Contact Number",
    requiredFields: ["name", "email"],
  },
  {
    role: "administrator",
    title: "Member Administrator Contact",
    description: "Update the Administrator Contact responsible for an additional point of contact for this member.",
    sameAsLabel: contactSameAsPrimaryLabel,
    nameLabel: "Administrator Contact Name",
    phoneLabel: "Contact Number",
    // Optional for every member, including a member on the old company login
    // (B10, Monday 13385035728; Update Member Form (24) L1754-1755).
    requiredFields: [],
  },
  {
    role: "marketingCommunication",
    title: "Marketing and Communication Contact",
    description:
      "Update the contact responsible for marketing and communications. This person will receive promotions, sponsorship opportunities, and other Chamber communications to help the member maximize their membership value.",
    sameAsLabel: contactSameAsPrimaryLabel,
    nameLabel: "Contact Name",
    phoneLabel: "Company Contact Number",
    requiredFields: [],
  },
  {
    role: "hrEmployeeEngagement",
    title: "HR/Employee Engagement Contact",
    description:
      "Update the contact responsible for sharing membership benefits and opportunities with employees. This person will help promote cost-saving programs, events, resources, and the Chamber Perks App™ for access to local and national discounts.",
    sameAsLabel: contactSameAsPrimaryLabel,
    nameLabel: "Contact Name",
    phoneLabel: "Contact Number",
    requiredFields: [],
  },
  {
    role: "sales",
    title: "Sales/Business Development Contact",
    description:
      "Update the contact responsible for business development, growth, and sales opportunities. This person will receive information about networking, partnerships, events, and the initiatives designed to increase visibility, build relationships, and support the member’s growth.",
    sameAsLabel: contactSameAsPrimaryLabel,
    nameLabel: "Contact Name",
    phoneLabel: "Contact Number",
    requiredFields: [],
  },
];

const getMemberStatusReasonKind = (modal: ConfirmationModal): MemberStatusReasonKind | null =>
  modal === "suspend" || modal === "unsuspend" || modal === "delete" ||
  modal === "cancelRenewal" || modal === "uncancelRenewal" ? modal : null;

/** "YYYY-MM-DD" from the member status read, shown as a long date. */
const formatStatusDateKey = (dateKey?: string | null) =>
  dateKey ? formatUtcCalendarDate(Date.parse(`${dateKey}T00:00:00.000Z`)) : "";

const accountsPayableSectionTitle = contactSections.find(({ role }) => role === "accountsPayable")?.title || "";

const statusLabels: Record<CmpMemberLifecycleStatusEnum, MemberStatus> = {
  [CmpMemberLifecycleStatusEnum.Active]: "Active",
  [CmpMemberLifecycleStatusEnum.Deleted]: "Past",
  [CmpMemberLifecycleStatusEnum.Past]: "Past",
  [CmpMemberLifecycleStatusEnum.Pending]: "Pending",
  [CmpMemberLifecycleStatusEnum.Prospect]: "Prospect",
  [CmpMemberLifecycleStatusEnum.Suspended]: "Suspended",
};

const lifecycleStatusByLabel: Record<MemberStatus, CmpMemberLifecycleStatusEnum> = {
  Active: CmpMemberLifecycleStatusEnum.Active,
  Past: CmpMemberLifecycleStatusEnum.Past,
  Pending: CmpMemberLifecycleStatusEnum.Pending,
  Prospect: CmpMemberLifecycleStatusEnum.Prospect,
  Suspended: CmpMemberLifecycleStatusEnum.Suspended,
};

const createLocalId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const createEmptyContact = (): MemberContactInfo & { mobilePhoneNumber: string } => ({
  name: "",
  title: "",
  email: "",
  companyPhone: "",
  ext: "",
  mobilePhoneNumber: "",
});

const createEmptyReferralTracking = (): ReferralTrackingInfo => ({
  id: createLocalId("referral"),
  referralDate: "",
  becameMemberDate: "",
  companyReferralName: "",
  contactName: "",
  email: "",
  contactNumber: "",
  contactNumberExtension: "",
});

const emptyFormState = (): UpdateMemberFormState => ({
  administrative: {
    dateJoined: "",
    nextRenewalDate: "",
    externalMembershipStanding:
      CmpExternalMembershipStandingEnum.NotTracked,
  },
  association: {
    isAssociateCompanyMember: false,
    primaryMemberCompanyId: "",
  },
  membership: {
    membershipTypeId: "",
    fullTimeEmployees: "",
    partTimeEmployees: "",
    paymentSchedule: "",
    billingResponsibility: "memberPays",
    thirdPartyPayerName: "",
    thirdPartyPaymentNotes: "",
    thirdPartyBillingSnapshot: emptyMemberThirdPartyBillingSnapshot(),
    paymentMethod: "",
    selectedAdditionalCostIds: [],
    membershipDiscount: {
      enabled: false,
      code: "",
      description: "",
      amount: "",
    },
    autoRenew: false,
    emailCommunicationConsentConfirmed: false,
  },
  companyDetails: {
    charityRegistrationNumber: "",
    numberedCompany: "",
    yearEstablished: "",
    memberOwnershipDiversity: [],
    howDidYouHearAboutUs: [],
    howDidYouHearAboutUsOther: "",
    primaryReasonsForJoining: [],
    primaryReasonForJoiningOther: "",
    activeSocialPlatforms: [],
    boardOrCommitteeInterest: "",
  },
  insights: {
    chamberPerksAppHeardAbout: "",
    chamberPerksAppDownloaded: "",
    boardDirectorInterest: "",
    committeeInterest: "",
    governanceGroupIds: [],
    communicationPreferences: [],
  },
  referralTracking: [createEmptyReferralTracking()],
  affiliatedChamberMember: {
    isAffiliatedChamberMember: false,
    chamberName: "",
  },
  affinityProgramEnrollments: [],
  directory: {
    companyName: "",
    companyDescription: "",
    companyWebsite: "",
    googleReviewUrl: "",
    mainCompanyContactName: "",
    mainCompanyContactEmail: "",
    mainCompanyPhone: "",
    mainCompanyPhoneExt: "",
    tollFreePhone: "",
    faxNumber: "",
    directoryCategoryIds: [],
    showInDirectory: true,
    seoTags: [],
  },
  address: {
    hideStreetAddressInDirectories: false,
    streetAddress: "",
    city: "",
    provinceOrState: "",
    postalOrZipCode: "",
    country: "Canada",
  },
  socialLinks: {
    facebookUrl: "",
    instagramUrl: "",
    threadsUrl: "",
    linkedinUrl: "",
    xTwitterUrl: "",
    tikTokUrl: "",
    youtubeUrl: "",
  },
  customContacts: [],
  contacts: {
    primaryVoting: createEmptyContact(),
    administrator: createEmptyContact(),
    accountsPayable: createEmptyContact(),
    employeeAdoption: createEmptyContact(),
    marketingCommunication: createEmptyContact(),
    hrEmployeeEngagement: createEmptyContact(),
    sales: createEmptyContact(),
  },
  contactSameAs: {
    primaryVoting: false,
    administrator: false,
    accountsPayable: false,
    employeeAdoption: false,
    marketingCommunication: false,
    hrEmployeeEngagement: false,
    sales: false,
  },
  billingAddress: {
    streetAddress: "",
    city: "",
    provinceOrState: "",
    postalOrZipCode: "",
    country: "Canada",
  },
  profile: {
    internalNotes: "",
    customFields: [],
  },
  logo: {
    fileName: "",
    previewUrl: "",
  },
});

const cloneMembershipFormState = (
  membership: MembershipFormState
): MembershipFormState => ({
  ...membership,
  selectedAdditionalCostIds: [...membership.selectedAdditionalCostIds],
  membershipDiscount: { ...membership.membershipDiscount },
  thirdPartyBillingSnapshot: { ...membership.thirdPartyBillingSnapshot },
});

const getMembershipAdjustmentFingerprint = (
  membership: MembershipFormState
) =>
  JSON.stringify({
    membershipTypeId: membership.membershipTypeId,
    fullTimeEmployees: membership.fullTimeEmployees,
    partTimeEmployees: membership.partTimeEmployees,
    paymentSchedule: membership.paymentSchedule,
    billingResponsibility: membership.billingResponsibility,
    thirdPartyPayerName: membership.thirdPartyPayerName,
    thirdPartyPaymentNotes: membership.thirdPartyPaymentNotes,
    thirdPartyBillingSnapshot: membership.thirdPartyBillingSnapshot,
    paymentMethod: membership.paymentMethod,
    membershipDiscount: membership.membershipDiscount,
    autoRenew: membership.autoRenew,
  });

const hasMembershipAdjustmentChanges = ({
  draft,
  persisted,
}: {
  draft: MembershipFormState | null;
  persisted: MembershipFormState;
}) =>
  !!draft &&
  getMembershipAdjustmentFingerprint(draft) !==
    getMembershipAdjustmentFingerprint(persisted);

const currencyFormatter = new Intl.NumberFormat("en-CA", {
  style: "currency",
  currency: "CAD",
});

const formatCurrency = (amountInCents?: number | null) =>
  currencyFormatter.format((amountInCents || 0) / 100);

const toOptionalString = (value?: string | null) => {
  const trimmedValue = (value || "").trim();
  return trimmedValue ? trimmedValue : undefined;
};

// Explicit null clears a saved contact field; omitted fields retain their old value.
const toOptionalContactString = (value?: string | null) =>
  toOptionalString(value) ?? null;

const toUtcCalendarDateInputValue = (value?: number | null) =>
  value ? new Date(value).toISOString().slice(0, 10) : "";

const getNumber = (value: string) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const hasEmailShape = (value: string) => value.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const getPaymentMethodFromChoice = (
  paymentChoice?: {
    type?: CmpPaymentChoiceTypeEnum | null;
    manualPaymentMethodId?: string | null;
  } | null
): PaymentMethodType | "" => {
  if (!paymentChoice) {
    return "";
  }

  if (paymentChoice.type === CmpPaymentChoiceTypeEnum.CreditCard) {
    return "creditCard";
  }

  if (paymentChoice.type === CmpPaymentChoiceTypeEnum.Free) {
    return "free";
  }

  const manualId = paymentChoice.manualPaymentMethodId || "";

  if (manualId === "INVOICE_ME") {
    return "invoice";
  }

  if (manualId === "CASH") {
    return "cash";
  }

  if (manualId === "DIRECT_DEPOSIT") {
    return "bankTransfer";
  }

  if (manualId === "E_TRANSFER") {
    return "eTransfer";
  }

  if (manualId === "CHEQUE") {
    return "cheque";
  }

  return "";
};

const getBillingResponsibilityFromMembership = ({
  configuredInsurancePayerName,
  insuranceOptionAvailable,
  membership,
}: {
  configuredInsurancePayerName?: string | null;
  insuranceOptionAvailable: boolean;
  membership?: {
    paymentResponsibility?:
      | ChamberMemberProMembershipModelPaymentResponsibilityEnum
      | null;
    thirdPartyPayerName?: string | null;
    thirdPartyPaymentNotes?: string | null;
    isFreeLifetime?: boolean | null;
  } | null;
}): BillingResponsibilityOption =>
  getMemberBillingResponsibility({
    configuredInsurancePayerName,
    insuranceOptionAvailable,
    membership,
  });

const getManualPaymentMethodId = (paymentMethod: PaymentMethodType | "") => {
  switch (paymentMethod) {
    case "invoice":
      return "INVOICE_ME";
    case "cash":
      return "CASH";
    case "bankTransfer":
      return "DIRECT_DEPOSIT";
    case "eTransfer":
      return "E_TRANSFER";
    case "cheque":
      return "CHEQUE";
    default:
      return undefined;
  }
};

const getPaymentChoiceType = (
  billingResponsibility: BillingResponsibilityOption,
  paymentMethod: PaymentMethodType | ""
) => {
  if (
    billingResponsibility === "complimentary" ||
    billingResponsibility === "freeLifetime"
  ) {
    return {
      type: CmpPaymentChoiceTypeEnum.Free,
    };
  }

  if (
    billingResponsibility === "affiliatedPrimaryCompany" ||
    billingResponsibility === "otherThirdParty" ||
    billingResponsibility === "chamberGroupInsurance"
  ) {
    return {
      type: CmpPaymentChoiceTypeEnum.ThirdParty,
    };
  }

  if (paymentMethod === "creditCard") {
    return {
      type: CmpPaymentChoiceTypeEnum.CreditCard,
    };
  }

  if (paymentMethod === "free") {
    return {
      type: CmpPaymentChoiceTypeEnum.Free,
    };
  }

  return {
    type: CmpPaymentChoiceTypeEnum.Manual,
    manualPaymentMethodId: getManualPaymentMethodId(paymentMethod),
  };
};

const getEffectiveUpdatePaymentMethod = ({
  creditCardPaymentsReady,
  manualPaymentMethods,
  paymentMethod,
}: {
  creditCardPaymentsReady: boolean;
  manualPaymentMethods: Array<{ enabled: boolean; id: string }>;
  paymentMethod: PaymentMethodType | "";
}): PaymentMethodType | "" => {
  if (paymentMethod !== "creditCard" || creditCardPaymentsReady) {
    return paymentMethod;
  }

  const firstEnabledManualPaymentMethod = manualPaymentMethods.find(
    (method) => method.enabled
  );

  if (!firstEnabledManualPaymentMethod) {
    return "";
  }

  return getPaymentMethodFromChoice({
    type: CmpPaymentChoiceTypeEnum.Manual,
    manualPaymentMethodId: firstEnabledManualPaymentMethod.id,
  });
};

const getPaymentResponsibility = (
  billingResponsibility: BillingResponsibilityOption
) => {
  if (
    billingResponsibility === "complimentary" ||
    billingResponsibility === "freeLifetime"
  ) {
    return ChamberMemberProMembershipModelPaymentResponsibilityEnum.NoCharge;
  }

  if (
    billingResponsibility === "affiliatedPrimaryCompany" ||
    billingResponsibility === "otherThirdParty" ||
    billingResponsibility === "chamberGroupInsurance"
  ) {
    return ChamberMemberProMembershipModelPaymentResponsibilityEnum.ThirdPartyPays;
  }

  return ChamberMemberProMembershipModelPaymentResponsibilityEnum.MemberPays;
};

const getCompanyFormAddress = (
  detail: BackendMemberDetail | CmpMemberDetailFieldsFragment
): MemberFormAddress => ({
  streetAddress: detail.member.address?.streetAddress || "",
  city: detail.member.address?.city || "",
  provinceOrState: detail.member.address?.province || "",
  postalOrZipCode: detail.member.address?.postalCode || "",
  country: detail.member.cmpExtension?.applicationMetadata?.country || "Canada",
});

const mapDetailToFormState = (
  detail: BackendMemberDetail | CmpMemberDetailFieldsFragment,
  {
    configuredInsurancePayerName,
    insuranceOptionAvailable,
  }: {
    configuredInsurancePayerName?: string | null;
    insuranceOptionAvailable: boolean;
  }
): UpdateMemberFormState => {
  const formState = emptyFormState();
  const member = detail.member;
  const extension = member.cmpExtension;
  const membership = extension?.membership;
  const companyDetails = extension?.companyDetails;
  const applicationMetadata = extension?.applicationMetadata;
  const paymentChoice = membership?.paymentChoice || detail.currentInvoice?.paymentChoice;
  const thirdPartyBillingSnapshot = (
    detail as BackendMemberDetail & {
      thirdPartyBillingSnapshot?: MemberThirdPartyBillingSnapshot | null;
    }
  ).thirdPartyBillingSnapshot;

  return {
    ...formState,
    administrative: {
      dateJoined: toUtcCalendarDateInputValue(detail.dateJoinedUtcMilli),
      nextRenewalDate: toUtcCalendarDateInputValue(
        membership?.pricingSnapshot.renewalDateUtcMilli
      ),
      externalMembershipStanding:
        extension?.externalMembershipStanding ||
        CmpExternalMembershipStandingEnum.NotTracked,
    },
    association: {
      isAssociateCompanyMember:
        !!extension?.association?.isAssociateCompanyMember,
      primaryMemberCompanyId: extension?.association?.primaryMemberCompanyId || "",
    },
    membership: {
      membershipTypeId: membership?.membershipTypeId || "",
      fullTimeEmployees: String(membership?.fullTimeEmployees ?? ""),
      partTimeEmployees: String(membership?.partTimeEmployees ?? ""),
      paymentSchedule: membership?.paymentSchedule || "",
      billingResponsibility: getBillingResponsibilityFromMembership({
        configuredInsurancePayerName,
        insuranceOptionAvailable,
        membership,
      }),
      thirdPartyPayerName: membership?.thirdPartyPayerName || "",
      thirdPartyPaymentNotes: membership?.thirdPartyPaymentNotes || "",
      thirdPartyBillingSnapshot: thirdPartyBillingSnapshot
        ? {
            ...emptyMemberThirdPartyBillingSnapshot(),
            ...thirdPartyBillingSnapshot,
          }
        : emptyMemberThirdPartyBillingSnapshot(),
      paymentMethod: getPaymentMethodFromChoice(paymentChoice),
      selectedAdditionalCostIds: membership?.selectedAdditionalCostIds || [],
      membershipDiscount: {
        enabled: !!membership?.membershipDiscount?.enabled,
        code: membership?.membershipDiscount?.code || "",
        description: membership?.membershipDiscount?.description || "",
        amount:
          membership?.membershipDiscount?.amountInCents === undefined ||
          membership?.membershipDiscount?.amountInCents === null
            ? ""
            : String(membership.membershipDiscount.amountInCents / 100),
      },
      autoRenew: !!paymentChoice?.autoRenew,
      emailCommunicationConsentConfirmed:
        !!paymentChoice?.emailCommunicationConsentConfirmed,
    },
    companyDetails: {
      charityRegistrationNumber: companyDetails?.charityRegistrationNumber || "",
      numberedCompany: companyDetails?.numberedCompany || "",
      yearEstablished: toEstablishedYearValue(companyDetails?.yearEstablished),
      // Older members have no saved list: no box is ticked.
      memberOwnershipDiversity: getMemberOwnershipDiversityInput(companyDetails?.memberOwnershipDiversity),
      howDidYouHearAboutUs: companyDetails?.howDidYouHearAboutUs || [],
      howDidYouHearAboutUsOther: companyDetails?.howDidYouHearAboutUsOther || "",
      primaryReasonsForJoining:
        companyDetails?.primaryReasonsForJoining || [],
      primaryReasonForJoiningOther:
        companyDetails?.primaryReasonForJoiningOther || "",
      activeSocialPlatforms: companyDetails?.activeSocialPlatforms || [],
      boardOrCommitteeInterest:
        companyDetails?.boardOrCommitteeInterest || "",
    },
    insights: {
      chamberPerksAppHeardAbout: extension?.chamberPerksAppHeardAbout || "",
      chamberPerksAppDownloaded: extension?.chamberPerksAppDownloaded || "",
      boardDirectorInterest: normalizeInterestValue(
        extension?.boardDirectorInterest,
        boardDirectorInterestAffirmative
      ),
      committeeInterest: normalizeInterestValue(
        extension?.committeeInterest,
        committeeInterestAffirmative
      ),
      governanceGroupIds: extension?.governanceGroupIds || [],
      communicationPreferences:
        applicationMetadata?.communicationPreferences || [],
    },
    referralTracking:
      extension?.referralTracking && extension.referralTracking.length > 0
        ? extension.referralTracking.map((referral) => ({
            id: referral.id || createLocalId("referral"),
            referralDate: referral.referralDate || "",
            becameMemberDate: referral.becameMemberDate || "",
            companyReferralName: referral.companyReferralName || "",
            contactName: referral.contactName || "",
            email: referral.email || "",
            contactNumber: referral.contactNumber || "",
            contactNumberExtension: referral.contactNumberExtension || "",
          }))
        : [createEmptyReferralTracking()],
    affiliatedChamberMember: {
      isAffiliatedChamberMember:
        !!extension?.affiliatedChamberMember?.isAffiliatedChamberMember,
      chamberName: extension?.affiliatedChamberMember?.chamberName || "",
    },
    affinityProgramEnrollments:
      extension?.affinityProgramEnrollments?.map((enrollment) => ({
        id: enrollment.id || createLocalId("affinity"),
        affinityProgramPartnerId: enrollment.affinityProgramPartnerId || "",
        affinityProgramPartnerName: enrollment.affinityProgramPartnerName || "",
        idNumber: enrollment.idNumber || "",
        startDate: enrollment.startDate || "",
        billingOption: enrollment.billingOption || "",
        status: enrollment.status || "",
        notes: enrollment.notes || "",
      })) || [],
    directory: {
      companyName: member.name || "",
      companyDescription: member.description || "",
      companyWebsite: member.website || "",
      googleReviewUrl: member.googleReviewLink || "",
      mainCompanyContactName: member.contactName || "",
      mainCompanyContactEmail: member.email || "",
      mainCompanyPhone: member.phoneNumber || "",
      mainCompanyPhoneExt: member.phoneNumberExtension || "",
      tollFreePhone: formatStaffTollFreeInput(member.tollFreePhoneNumber),
      faxNumber: formatStaffFaxInput(member.faxNumber),
      directoryCategoryIds: (member.categoryId || []) as ChamberMemberCategory[],
      showInDirectory: member.showInApp,
      seoTags: member.seoTags || [],
    },
    address: {
      hideStreetAddressInDirectories: !!member.hideAddressFromDirectory,
      ...getCompanyFormAddress(detail),
    },
    socialLinks: {
      facebookUrl: member.facebook || "",
      instagramUrl: member.instagram || "",
      threadsUrl: member.threads || "",
      linkedinUrl: member.linkedin || "",
      xTwitterUrl: member.twitter || "",
      tikTokUrl: member.tiktok || "",
      youtubeUrl: member.youtube || "",
    },
    customContacts: (member.customContacts || []).map((contact) => ({
      id: contact.id, name: contact.name, email: contact.email, jobTitle: contact.jobTitle,
      phoneNumber: contact.phoneNumber, phoneNumberExtension: contact.phoneNumberExtension,
      mobilePhoneNumber: contact.mobilePhoneNumber,
    })),
    contacts: {
      primaryVoting: {
        name: member.primaryContactName || "",
        title: member.primaryContactTitle || "",
        email: member.primaryContactEmail || "",
        companyPhone: member.primaryContactPhoneNumber || "",
        ext: member.primaryContactPhoneNumberExtension || "",
        mobilePhoneNumber: member.primaryContactMobilePhoneNumber || "",
      },
      administrator: {
        name: member.adminConsoleContactName || "",
        title: member.cpaAdminCompanyAdminContactTitle || "",
        email: member.adminConsoleLoginEmail || "",
        companyPhone: member.cpaAdminCompanyAdminContactNumber || "",
        ext: member.cpaAdminCompanyAdminContactNumberExtension || "",
        mobilePhoneNumber: member.cpaAdminCompanyAdminContactMobilePhoneNumber || "",
      },
      accountsPayable: {
        name: extension?.accountsPayableContactName || "",
        title: extension?.accountsPayableContactTitle || "",
        email: extension?.accountsPayableContactEmail || "",
        companyPhone: extension?.accountsPayableContactNumber || "",
        ext: extension?.accountsPayableContactNumberExtension || "",
        mobilePhoneNumber: extension?.accountsPayableContactMobilePhoneNumber || "",
      },
      employeeAdoption: {
        name: member.cpaEmployeeAdoptionContactName || "",
        title: member.cpaEmployeeAdoptionContactTitle || "",
        email: member.cpaEmployeeAdoptionContactEmail || "",
        companyPhone: member.cpaEmployeeAdoptionContactNumber || "",
        ext: member.cpaEmployeeAdoptionContactNumberExtension || "",
        mobilePhoneNumber: member.cpaEmployeeAdoptionContactMobilePhoneNumber || "",
      },
      marketingCommunication: {
        name: extension?.marketingCommunicationContactName || "",
        title: extension?.marketingCommunicationContactTitle || "",
        email: extension?.marketingCommunicationContactEmail || "",
        companyPhone: extension?.marketingCommunicationContactNumber || "",
        ext: extension?.marketingCommunicationContactNumberExtension || "",
        mobilePhoneNumber: extension?.marketingCommunicationContactMobilePhoneNumber || "",
      },
      hrEmployeeEngagement: {
        name: extension?.hrEmployeeEngagementContactName || "",
        title: extension?.hrEmployeeEngagementContactTitle || "",
        email: extension?.hrEmployeeEngagementContactEmail || "",
        companyPhone: extension?.hrEmployeeEngagementContactNumber || "",
        ext: extension?.hrEmployeeEngagementContactNumberExtension || "",
        mobilePhoneNumber: extension?.hrEmployeeEngagementContactMobilePhoneNumber || "",
      },
      sales: {
        name: extension?.salesBusinessDevelopmentContactName || "",
        title: extension?.salesBusinessDevelopmentContactTitle || "",
        email: extension?.salesBusinessDevelopmentContactEmail || "",
        companyPhone: extension?.salesBusinessDevelopmentContactNumber || "",
        ext: extension?.salesBusinessDevelopmentContactNumberExtension || "",
        mobilePhoneNumber: extension?.salesBusinessDevelopmentContactMobilePhoneNumber || "",
      },
    },
    // The "same as" flags persist in applicationMetadata. The marketing/hr
    // aliases are legacy duplicates that CreateMember also writes, so they act
    // as fallbacks for records saved before the dedicated fields existed.
    contactSameAs: {
      primaryVoting:
        !!applicationMetadata?.primaryVotingSameAsMainCompanyContact,
      administrator: !!(
        applicationMetadata?.administratorSameAsCompanyPrimaryContact ??
        applicationMetadata?.marketingSameAsCompanyPrimaryContact
      ),
      accountsPayable:
        !!applicationMetadata?.accountsPayableSameAsCompanyPrimaryContact,
      employeeAdoption: !!(
        applicationMetadata?.employeeAdoptionSameAsCompanyPrimaryContact ??
        applicationMetadata?.hrSameAsCompanyPrimaryContact
      ),
      marketingCommunication:
        !!applicationMetadata?.marketingCommunicationSameAsCompanyPrimaryContact,
      hrEmployeeEngagement:
        !!applicationMetadata?.hrEmployeeEngagementSameAsCompanyPrimaryContact,
      sales: !!applicationMetadata?.salesSameAsCompanyPrimaryContact,
    },
    billingAddress: getMemberBillingFormAddress({
      billing: extension?.accountsPayableBillingAddress,
      company: getCompanyFormAddress(detail),
    }),
    profile: {
      internalNotes: extension?.profile?.internalNotes || "",
      // Map to plain objects so Apollo's __typename never leaks into the form
      // state (and from there into the update mutation input).
      customFields: (extension?.profile?.customFields || []).map((field) => ({
        id: field.id,
        fieldName: field.fieldName,
        value: field.value,
      })),
    },
    logo: {
      fileName: member.logo || "",
      previewUrl: "",
    },
  };
};

export const buildMemberInputFromForm = ({
  currentStatus,
  detail,
  formState,
}: {
  currentStatus: MemberStatus;
  detail: BackendMemberDetail;
  formState: UpdateMemberFormState;
}): ChamberMemberInput => {
  const member = detail.member;
  const extension = member.cmpExtension;
  const primaryVoting = formState.contacts.primaryVoting;
  const administrator = formState.contacts.administrator;
  const accountsPayable = formState.contacts.accountsPayable;
  const employeeAdoption = formState.contacts.employeeAdoption;
  const marketingCommunication = formState.contacts.marketingCommunication;
  const hrEmployeeEngagement = formState.contacts.hrEmployeeEngagement;
  const sales = formState.contacts.sales;
  const storedBillingAddress = extension?.accountsPayableBillingAddress;
  const billingAddress = keepsStoredBlankBillingAddress({
    billing: storedBillingAddress,
    company: getCompanyFormAddress(detail),
    form: formState.billingAddress,
  })
    ? getStoredBillingFormAddress(storedBillingAddress)
    : formState.billingAddress;
  // IMPORTANT: every field below is whitelisted from the form state (or copied
  // one scalar at a time from the loaded record), mirroring CreateMember's
  // buildMemberInput. Never spread Apollo query result objects into this
  // input: they carry __typename plus Return-only fields (source, stripe*,
  // pricingSnapshot, weightedEmployees, ...) that the GraphQL input types
  // reject, which made the update mutation fail.
  return {
    id: member.id,
    chamberId: member.chamberId,
    categoryId: formState.directory.directoryCategoryIds,
    name: formState.directory.companyName.trim(),
    description: toOptionalString(formState.directory.companyDescription),
    seoTags: formState.directory.seoTags,
    customContacts: formState.customContacts,
    website: toOptionalString(formState.directory.companyWebsite),
    facebook: toOptionalString(formState.socialLinks.facebookUrl),
    instagram: toOptionalString(formState.socialLinks.instagramUrl),
    threads: toOptionalString(formState.socialLinks.threadsUrl),
    linkedin: toOptionalString(formState.socialLinks.linkedinUrl),
    twitter: toOptionalString(formState.socialLinks.xTwitterUrl),
    tiktok: toOptionalString(formState.socialLinks.tikTokUrl),
    youtube: toOptionalString(formState.socialLinks.youtubeUrl),
    googleReviewLink: toOptionalString(formState.directory.googleReviewUrl),
    address: {
      streetAddress: formState.address.streetAddress.trim(),
      city: formState.address.city.trim(),
      province: formState.address.provinceOrState.trim(),
      postalCode: formState.address.postalOrZipCode.trim(),
    },
    hideAddressFromDirectory:
      formState.address.hideStreetAddressInDirectories,
    phoneNumber: formState.directory.mainCompanyPhone.trim(),
    phoneNumberExtension: toOptionalString(
      formState.directory.mainCompanyPhoneExt
    ),
    // IF11 MB4: digits only; "" clears.
    tollFreePhoneNumber: getMemberPhoneDigits(formState.directory.tollFreePhone),
    faxNumber: getMemberPhoneDigits(formState.directory.faxNumber),
    contactName: toOptionalString(
      formState.directory.mainCompanyContactName
    ),
    email: formState.directory.mainCompanyContactEmail.trim(),
    // Explicit null clears a deleted logo. A section save keeps the saved
    // logo when the field is omitted, so Delete then Save never removed it.
    logo: getLogoInputValue(formState.logo).trim() || null,
    showInApp: formState.directory.showInDirectory,
    adminConsoleLoginEmail: toOptionalContactString(administrator.email),
    adminConsoleContactName: toOptionalContactString(administrator.name),
    isSuspended: currentStatus === "Suspended" || currentStatus === "Past",
    cpaAdminCompanyAdminContactTitle: toOptionalContactString(administrator.title),
    cpaAdminCompanyAdminContactNumber: toOptionalContactString(
      administrator.companyPhone
    ),
    cpaAdminCompanyAdminContactNumberExtension: toOptionalContactString(
      administrator.ext
    ),
    primaryContactName: toOptionalContactString(primaryVoting.name),
    primaryContactTitle: toOptionalContactString(primaryVoting.title),
    primaryContactEmail: toOptionalContactString(primaryVoting.email),
    primaryContactPhoneNumber: toOptionalContactString(primaryVoting.companyPhone),
    primaryContactPhoneNumberExtension: toOptionalContactString(primaryVoting.ext),
    cpaEmployeeAdoptionContactName: toOptionalContactString(employeeAdoption.name),
    cpaEmployeeAdoptionContactEmail: toOptionalContactString(employeeAdoption.email),
    cpaEmployeeAdoptionContactTitle: toOptionalContactString(employeeAdoption.title),
    cpaEmployeeAdoptionContactNumber: toOptionalContactString(
      employeeAdoption.companyPhone
    ),
    cpaEmployeeAdoptionContactNumberExtension: toOptionalContactString(
      employeeAdoption.ext
    ),
    primaryContactMobilePhoneNumber: toOptionalContactString(primaryVoting.mobilePhoneNumber),
    cpaAdminCompanyAdminContactMobilePhoneNumber: toOptionalContactString(administrator.mobilePhoneNumber),
    cpaEmployeeAdoptionContactMobilePhoneNumber: toOptionalContactString(employeeAdoption.mobilePhoneNumber),
    lifecycleStatus: lifecycleStatusByLabel[currentStatus],
    cmpExtension: {
      accountsPayableContactMobilePhoneNumber: toOptionalContactString(accountsPayable.mobilePhoneNumber),
      marketingCommunicationContactMobilePhoneNumber: toOptionalContactString(marketingCommunication.mobilePhoneNumber),
      hrEmployeeEngagementContactMobilePhoneNumber: toOptionalContactString(hrEmployeeEngagement.mobilePhoneNumber),
      salesBusinessDevelopmentContactMobilePhoneNumber: toOptionalContactString(sales.mobilePhoneNumber),
      // Workflow tracking is copied scalar-by-scalar from the loaded record so
      // an update does not wipe approval / payment-request / archive state.
      boardApprovalRequired: extension?.boardApprovalRequired,
      boardApprovedAtUtcMilli: extension?.boardApprovedAtUtcMilli,
      boardApprovedByUserId: extension?.boardApprovedByUserId,
      paymentRequestState: extension?.paymentRequestState,
      lastPaymentRequestSentAtUtcMilli:
        extension?.lastPaymentRequestSentAtUtcMilli,
      archivedAtUtcMilli: extension?.archivedAtUtcMilli,
      archiveReason: extension?.archiveReason,
      accountsPayableContactName: toOptionalContactString(accountsPayable.name),
      accountsPayableContactTitle: toOptionalContactString(accountsPayable.title),
      accountsPayableContactEmail: toOptionalContactString(accountsPayable.email),
      accountsPayableContactNumber: toOptionalContactString(
        accountsPayable.companyPhone
      ),
      accountsPayableContactNumberExtension: toOptionalContactString(
        accountsPayable.ext
      ),
      accountsPayableBillingAddress: {
        streetAddress: toOptionalString(billingAddress.streetAddress),
        city: toOptionalString(billingAddress.city),
        province: toOptionalString(billingAddress.provinceOrState),
        postalCode: toOptionalString(billingAddress.postalOrZipCode),
        country: toOptionalString(billingAddress.country),
      },
      marketingCommunicationContactName: toOptionalContactString(
        marketingCommunication.name
      ),
      marketingCommunicationContactTitle: toOptionalContactString(
        marketingCommunication.title
      ),
      marketingCommunicationContactEmail: toOptionalContactString(
        marketingCommunication.email
      ),
      marketingCommunicationContactNumber: toOptionalContactString(
        marketingCommunication.companyPhone
      ),
      marketingCommunicationContactNumberExtension: toOptionalContactString(
        marketingCommunication.ext
      ),
      hrEmployeeEngagementContactName: toOptionalContactString(
        hrEmployeeEngagement.name
      ),
      hrEmployeeEngagementContactTitle: toOptionalContactString(
        hrEmployeeEngagement.title
      ),
      hrEmployeeEngagementContactEmail: toOptionalContactString(
        hrEmployeeEngagement.email
      ),
      hrEmployeeEngagementContactNumber: toOptionalContactString(
        hrEmployeeEngagement.companyPhone
      ),
      hrEmployeeEngagementContactNumberExtension: toOptionalContactString(
        hrEmployeeEngagement.ext
      ),
      salesBusinessDevelopmentContactName: toOptionalContactString(sales.name),
      salesBusinessDevelopmentContactTitle: toOptionalContactString(sales.title),
      salesBusinessDevelopmentContactEmail: toOptionalContactString(sales.email),
      salesBusinessDevelopmentContactNumber: toOptionalContactString(
        sales.companyPhone
      ),
      salesBusinessDevelopmentContactNumberExtension: toOptionalContactString(
        sales.ext
      ),
      association: {
        isAssociateCompanyMember:
          formState.association.isAssociateCompanyMember,
        primaryMemberCompanyId:
          toOptionalString(formState.association.primaryMemberCompanyId),
      },
      membership: buildProfileUpdateMembershipInput({
        persistedMembership: extension?.membership,
        thirdPartyBillingSnapshot:
          formState.membership.thirdPartyBillingSnapshot,
        emailCommunicationConsentConfirmed:
          formState.membership.emailCommunicationConsentConfirmed,
      }),
      companyDetails: {
        // Always sent: the form holds the saved number, and "" clears it.
        charityRegistrationNumber: (
          formState.companyDetails.charityRegistrationNumber || ""
        ).trim(),
        // IF11 MB3: always sent; "" clears it.
        numberedCompany: (formState.companyDetails.numberedCompany || "").trim(),
        yearEstablished: toOptionalString(
          formState.companyDetails.yearEstablished
        ),
        // Always sent from this build, so unticking every box saves [].
        memberOwnershipDiversity: getMemberOwnershipDiversityInput(
          formState.companyDetails.memberOwnershipDiversity
        ),
        howDidYouHearAboutUs: formState.companyDetails.howDidYouHearAboutUs,
        howDidYouHearAboutUsOther: toOptionalString(
          formState.companyDetails.howDidYouHearAboutUsOther
        ),
        primaryReasonsForJoining:
          formState.companyDetails.primaryReasonsForJoining,
        primaryReasonForJoiningOther: toOptionalString(
          formState.companyDetails.primaryReasonForJoiningOther
        ),
        activeSocialPlatforms: formState.companyDetails.activeSocialPlatforms,
        boardOrCommitteeInterest: toOptionalString(
          formState.companyDetails.boardOrCommitteeInterest
        ),
      },
      applicationMetadata: {
        country: toOptionalString(formState.address.country),
        primaryVotingSameAsMainCompanyContact:
          formState.contactSameAs.primaryVoting,
        administratorSameAsCompanyPrimaryContact:
          formState.contactSameAs.administrator,
        accountsPayableSameAsCompanyPrimaryContact:
          formState.contactSameAs.accountsPayable,
        employeeAdoptionSameAsCompanyPrimaryContact:
          formState.contactSameAs.employeeAdoption,
        marketingCommunicationSameAsCompanyPrimaryContact:
          formState.contactSameAs.marketingCommunication,
        hrEmployeeEngagementSameAsCompanyPrimaryContact:
          formState.contactSameAs.hrEmployeeEngagement,
        // Legacy aliases kept in sync exactly like CreateMember's builder.
        marketingSameAsCompanyPrimaryContact:
          formState.contactSameAs.administrator,
        hrSameAsCompanyPrimaryContact: formState.contactSameAs.employeeAdoption,
        salesSameAsCompanyPrimaryContact: formState.contactSameAs.sales,
        communicationPreferences:
          formState.insights.communicationPreferences,
      },
      profile: {
        internalNotes: toOptionalString(formState.profile.internalNotes),
        customFields: formState.profile.customFields
          .filter((field) => field.fieldName.trim())
          .map((field) => ({
            id: field.id,
            fieldName: field.fieldName.trim(),
            value: field.value,
          })),
      },
      chamberPerksAppHeardAbout: toOptionalString(
        formState.insights.chamberPerksAppHeardAbout
      ),
      chamberPerksAppDownloaded: toOptionalString(
        formState.insights.chamberPerksAppDownloaded
      ),
      boardDirectorInterest: toOptionalString(
        formState.insights.boardDirectorInterest
      ),
      committeeInterest: toOptionalString(
        formState.insights.committeeInterest
      ),
      governanceGroupIds: formState.insights.governanceGroupIds,
      referralTracking: formState.referralTracking
        .filter(
          (referral) =>
            referral.companyReferralName.trim() ||
            referral.contactName.trim() ||
            referral.email.trim() ||
            referral.contactNumber.trim()
        )
        .map((referral) => ({
          id: referral.id,
          referralDate: toOptionalString(referral.referralDate),
          becameMemberDate: toOptionalString(referral.becameMemberDate),
          companyReferralName: toOptionalString(referral.companyReferralName),
          contactName: toOptionalString(referral.contactName),
          email: toOptionalString(referral.email),
          contactNumber: toOptionalString(referral.contactNumber),
          contactNumberExtension: toOptionalString(
            referral.contactNumberExtension
          ),
        })),
      affiliatedChamberMember: {
        isAffiliatedChamberMember:
          formState.affiliatedChamberMember.isAffiliatedChamberMember,
        chamberName: toOptionalString(
          formState.affiliatedChamberMember.chamberName
        ),
      },
      affinityProgramEnrollments: formState.affinityProgramEnrollments.map(
        (enrollment) => ({
          id: enrollment.id,
          affinityProgramPartnerId: enrollment.affinityProgramPartnerId,
          affinityProgramPartnerName: enrollment.affinityProgramPartnerName,
          idNumber: toOptionalString(enrollment.idNumber),
          startDate: toOptionalString(enrollment.startDate),
          billingOption: toOptionalString(enrollment.billingOption),
          status: toOptionalString(enrollment.status),
          notes: toOptionalString(enrollment.notes),
        })
      ),
    },
  };
};

// CMP Dashboard (21) helperByStatus (IF11 MB8): where the member status model
// is on, every intro ends with the Reports sentence; Cancelled has its own text.
const getHeaderCopy = (status: MemberStatus, statusModel = false, cancelled = false) => {
  const copy = getBaseHeaderCopy(status);
  if (!statusModel) return copy;
  const description = cancelled
    ? "Use this section to see cancelled member status, and to submit new company membership application."
    : copy.description;
  return { ...copy, description: `${description} Members can be filtered by member status in “Reports”.` };
};

const getBaseHeaderCopy = (status: MemberStatus) => {
  if (status === "Pending") {
    return {
      title: "Update Member",
      description:
        "Use this section to see member status, and update any member information.",
    };
  }

  if (status === "Active") {
    return {
      title: "Update Member",
      description:
        "Use this section to see member status, and update any member information. Also, send member login credentials reset password, suspend a member and delete member.",
    };
  }

  if (status === "Suspended") {
    return {
      title: "Update Member",
      description:
        "Use this section to see member status, and to unsuspend member.",
    };
  }

  return {
    title: "Update Member",
    description:
      "Use this section to see deleted member status, and to submit new company membership application.",
  };
};

const memberSaveSuccessDialog: MemberActionSuccessDialogState = {
  title: "", tone: "green",
  body: <>Your changes to this member have been <strong>saved successfully</strong>.</>,
};

// Claude Design (10) Update Member Form onSavePage: a contact email change
// that moved or turned off that contact's own login adds its notes.
const getContactLoginSaveDialog = (notes: string[]): MemberActionSuccessDialogState => ({
  title: "", tone: "green",
  body: <>Your changes to this member have been <strong>saved successfully</strong>. {notes.join(" ")}</>,
  note: "The previous email address can no longer sign in to the Member Dashboard.",
});

const csvEscape = (value: string) => `"${value.replace(/"/g, '""')}"`;

const StepFooter = ({ activeStep, error, loading, isActive, onBack, onNext }: {
  activeStep: number; error?: string; loading: boolean; isActive: boolean;
  onBack: () => void; onNext: () => void;
}) => activeStep === 5 ? null : (
  <div className="mt-6 pb-2">
    {error && <Alert severity="error" className="mb-4">{error}</Alert>}
    <div className="flex flex-col justify-between gap-3 sm:flex-row">
      <button type="button" disabled={loading} onClick={onBack} className="flex items-center justify-center gap-2 rounded-lg border border-[#C7C7C7] bg-white px-[18px] py-3 text-[13px] font-semibold text-[#0A0A0A] hover:bg-[#F7F7F8] disabled:opacity-50">
        <ArrowBackIcon sx={{ fontSize: 14 }} />
        {activeStep === 1 ? "Back to Member Lists" : `Previous:  ${["", "Member Directory Information", "Member Contacts", "Member Insights"][activeStep - 1]}`}
      </button>
      {(activeStep < 4 || isActive) && <button type="button" disabled={loading} onClick={onNext} className="flex items-center justify-center gap-2 rounded-lg border-0 bg-[#E1A731] px-[22px] py-[13px] text-[13px] font-bold text-white hover:bg-[#C6912A] disabled:opacity-50">
        {`Next Step:  ${updateMemberWorkflowSteps[activeStep].title}`}<ArrowForwardIcon sx={{ fontSize: 14 }} />
      </button>}
    </div>
  </div>
);

const StatusPill = ({
  tone,
  children,
}: {
  tone: "red" | "green" | "gray";
  children: React.ReactNode;
}) => (
  <span
    className={`inline-flex w-fit justify-center rounded-[14px] px-[14px] py-[5px] text-[12.5px] font-bold ${
      tone === "green"
        ? "bg-[#12B981] text-white"
        : tone === "red"
          ? "bg-[#F21B1B] text-white"
          : "bg-[#727272] text-white"
    }`}
  >
    {children}
  </span>
);

const statusPanelButtonBaseSx = {
  "&&&": {
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "normal",
    textTransform: "none",
    borderRadius: "8px",
    padding: "12px 20px",
    minWidth: 0,
    width: "fit-content",
    gap: "8px",
  },
  "& .MuiButton-startIcon": { margin: 0 },
};

const statusPanelGoldButtonSx = {
  ...statusPanelButtonBaseSx,
  "&&&": {
    ...statusPanelButtonBaseSx["&&&"],
    padding: "11px 18px",
  },
  backgroundColor: cpaGold,
  "&:hover": { backgroundColor: "#C6912A" },
};

const statusPanelGrayButtonSx = {
  ...statusPanelButtonBaseSx,
  backgroundColor: "#F1F3F5",
  color: "#0A0A0A",
  border: "1px solid #D7DBE0",
  "&:hover": { backgroundColor: "#D1D5DB" },
};

const statusPanelRedButtonSx = {
  ...statusPanelButtonBaseSx,
  backgroundColor: "#F21B1B",
  "&:hover": { backgroundColor: "#D01414" },
};

const statusPanelGreenButtonSx = {
  ...statusPanelButtonBaseSx,
  backgroundColor: "#12B981",
  "&:hover": { backgroundColor: "#009934" },
};

const StatusCheckIcon = ({ checked = true, color = "#0A0A0A" }: {
  checked?: boolean;
  color?: string;
}) => (
  <svg aria-hidden="true" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="mt-[1px] shrink-0">
    <circle cx="12" cy="12" r="10" />
    {checked && <path d="m8.5 12.2 2.4 2.4 4.6-4.8" />}
  </svg>
);

const StatusActionIcon = ({ icon, size = 14, className = "" }: {
  icon: "send" | "trash" | "suspend" | "unsuspend";
  size?: number;
  className?: string;
}) => (
  <svg aria-hidden="true" viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`shrink-0 ${className}`}>
    {icon === "send" && <><path d="m22 2-7 20-4-9-9-4Z" /><path d="M22 2 11 13" /></>}
    {icon === "trash" && <><path d="M3 6h18" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></>}
    {(icon === "suspend" || icon === "unsuspend") && <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />{icon === "suspend" ? <><path d="M17 9v6" /><path d="M21 9v6" /></> : <path d="m16 11 2 2 4-4" />}</>}
  </svg>
);

const StatusChecklistRow = ({
  title,
  description,
  pill,
  action,
  icon,
}: {
  title: string;
  description: string;
  pill?: React.ReactNode;
  action?: React.ReactNode;
  icon?: React.ReactNode;
}) => (
  <div className="flex items-start gap-[14px] border-b border-[#ECECEC] pb-[18px]">
    {icon || <StatusCheckIcon />}
    <div className="flex min-w-0 flex-col gap-[8px]">
      <p className="text-[16px] font-bold text-[#0A0A0A]">{title}</p>
      <p className="text-[14px] text-[#0A0A0A]">{description}</p>
      {pill}
      {action && <div className="mt-[4px]">{action}</div>}
    </div>
  </div>
);

// Radio row control mirroring Create step 3 (Chamber Perks App questions).
const RadioChoiceGroup = ({
  label,
  options,
  value,
  onChange,
  columns = "compact",
  disabled,
  renderOptionLabel,
  baseline,
}: {
  label: string;
  options: string[];
  value: string;
  onChange: (value: string) => void;
  columns?: "compact" | "wide";
  disabled?: boolean;
  renderOptionLabel?: (option: string) => React.ReactNode;
  baseline?: string;
}) => (
  <div>
    <p className="mb-1.5 text-sm font-bold text-gray-950">{label}</p>
    <RadioGroup
      row
      value={value}
      onChange={(event) => onChange(event.target.value)}
      sx={columns === "wide"
        ? { display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0,1fr))" }, columnGap: "32px", rowGap: "8px" }
        : { display: "flex", columnGap: "60px", rowGap: "8px" }}
    >
      {options.map((option) => (
        <FormControlLabel
          key={option}
          value={option}
          disabled={disabled}
          sx={choiceControlLabelSx}
          control={<Radio sx={{ ...radioControlSx, ...changedChoiceControlSx(baseline !== undefined && baseline !== value && (option === value || option === baseline)) }} />}
          label={
            <span className="text-sm text-gray-950">
              {renderOptionLabel ? renderOptionLabel(option) : option}
            </span>
          }
        />
      ))}
    </RadioGroup>
  </div>
);

type InterestOptionMatrixRow = {
  id: string;
  label: string;
  affirmativeValue: string;
  value: string;
  baseline: string;
  onChange: (value: string) => void;
};

// Board-director/committee interest rows: affirmative sentence radio at the
// left plus No / Maybe radios to the right (step 3 screenshot spec).
const InterestOptionMatrix = ({
  label,
  rows,
  disabled,
}: {
  label: string;
  rows: InterestOptionMatrixRow[];
  disabled?: boolean;
}) => (
  <div>
    <p className="mb-1.5 text-sm font-bold text-gray-950">{label}</p>
    <div className="space-y-2">
      {rows.map((row) => (
        <RadioGroup
          key={row.id}
          row
          value={row.value}
          onChange={(event) => row.onChange(event.target.value)}
          sx={{ display: "grid", maxWidth: "760px", gridTemplateColumns: { xs: "1fr", sm: "minmax(0,2fr) 1fr 1fr" }, columnGap: "14px", rowGap: "8px" }}
        >
          <FormControlLabel
            value={row.affirmativeValue}
            disabled={disabled}
            sx={choiceControlLabelSx}
            control={<Radio sx={{ ...radioControlSx, ...changedChoiceControlSx(row.baseline !== row.value && (row.value === row.affirmativeValue || row.baseline === row.affirmativeValue)) }} />}
            label={<span className="text-sm text-gray-950">{row.label}</span>}
          />
          {["No", "Maybe"].map((option) => (
            <FormControlLabel
              key={option}
              value={option}
              disabled={disabled}
              sx={choiceControlLabelSx}
              control={<Radio sx={{ ...radioControlSx, ...changedChoiceControlSx(row.baseline !== row.value && (row.value === option || row.baseline === option)) }} />}
              label={<span className="text-sm text-gray-950">{option}</span>}
            />
          ))}
        </RadioGroup>
      ))}
    </div>
  </div>
);

// Shared "status dot" rendering for affinity enrollments: used by the
// Enrolled Affinity Programs table and the Update modal's Status select so
// both surfaces stay in sync (mirrors Create step 3).
const AffinityEnrollmentStatusIndicator = ({ status }: { status: string }) => {
  const isInactive = status === "Inactive";

  return (
    <span
      className={`inline-flex items-center gap-2 font-semibold ${
        isInactive ? "text-red-600" : "text-green-700"
      }`}
    >
      <span
        className={`h-2 w-2 rounded-full ${
          isInactive ? "bg-red-500" : "bg-green-500"
        }`}
      />
      {status || "Active"}
    </span>
  );
};

const UpdateMember = () => {
  const { memberId: memberIdFromRoute } = useParams();
  const navigate = useNavigate();
  const noun = useCmpOrganizationNoun();
  const location = useLocation();
  const setPageHeaderOverride = usePageHeaderOverride();
  const chamber = useGetChamberFromUserIdInAuth();
  const chamberId = chamber?.id || "";
  const [memberIdForInput] = useState(
    () =>
      (memberIdFromRoute || "").trim() ||
      getUpdateMemberNavigationMemberId(location.state).trim() ||
      consumeUpdateMemberNavigationMemberId()
  );
  const [activeStep, setActiveStep] = useState(1);
  const [formStateWithBaseline, setMemberFormState] = useState(() => {
    const initialFormState = emptyFormState();

    return {
      current: initialFormState,
      savedBaseline: initialFormState,
    };
  });
  const formState = formStateWithBaseline.current;
  const savedFormBaseline = formStateWithBaseline.savedBaseline;
  // Saved values decide the invoice recipient, so show those, not unsaved edits.
  const savedInvoiceRecipient = getMemberInvoiceRecipient({
    administrator: savedFormBaseline.contacts.administrator,
    billing: savedFormBaseline.contacts.accountsPayable,
    mainCompany: {
      email: savedFormBaseline.directory.mainCompanyContactEmail,
      name: savedFormBaseline.directory.mainCompanyContactName,
    },
    primary: savedFormBaseline.contacts.primaryVoting,
  });
  const savedBaselineRef = useRef(savedFormBaseline);
  const currentFormRef = useRef(formState);
  const savedMemberRevisionRef = useRef<number | null>(null);
  const confirmedRenewalDetailRef = useRef<BackendMemberDetail | null>(null);
  const pendingIdentityReadbackRef = useRef<{ memberId: string; email: string; previousRevision: number | null } | null>(null);
  const setFormStateWithBaseline = useCallback((action: React.SetStateAction<typeof formStateWithBaseline>) => {
    const previous = { current: currentFormRef.current, savedBaseline: savedBaselineRef.current };
    const next = typeof action === "function" ? action(previous) : action;
    currentFormRef.current = next.current;
    savedBaselineRef.current = next.savedBaseline;
    setMemberFormState(next);
  }, []);
  const setFormState = (
    action: React.SetStateAction<UpdateMemberFormState>
  ) => {
    setFormStateWithBaseline((currentState) => {
      const nextFormState =
        typeof action === "function"
          ? action(currentState.current)
          : action;

      return nextFormState === currentState.current
        ? currentState
        : { ...currentState, current: nextFormState };
    });
  };
  const [savingSection, setSavingSection] = useState<CmpMemberSectionEnum | null>(null);
  const [sectionErrors, setSectionErrors] = useState<Record<string, string>>({});
  const [pendingStep, setPendingStep] = useState<number | null>(null);
  const [renewalUnavailable, setRenewalUnavailable] = useState(false);
  const [additionalContactDraftDirty, setAdditionalContactDraftDirty] = useState(false);
  // Design nudge under a "same as" checkbox when the source contact is missing.
  const [contactSameAsNudge, setContactSameAsNudge] = useState<ContactRole | null>(null);
  const [additionalContactResetKey, setAdditionalContactResetKey] = useState(0);
  const [addStaffNoteMutation, { loading: addingStaffNote }] = useAddCmpMemberStaffNoteMutation({
    context: memberRequestApolloContext,
  });
  const [staffNoteDraft, setStaffNoteDraft] = useState("");
  const staffNoteDraftRef = useRef(staffNoteDraft);
  staffNoteDraftRef.current = staffNoteDraft;
  const [staffNoteError, setStaffNoteError] = useState("");
  const [localStaffNotes, setLocalStaffNotes] = useState<CmpStaffNoteFieldsFragment[] | null>(null);
  const staffNoteOperation = useRef<{ body: string; operationId: string } | null>(null);
  const membershipAdjustmentOperationRef = useRef<{
    requestFingerprint: string;
    operationId: string;
  } | null>(null);
  const [renewalFormBaseline, setRenewalFormBaseline] = useState<MembershipFormState | null>(null);
  const [membershipAdjustmentDraft, setMembershipAdjustmentDraft] =
    useState<MembershipFormState | null>(null);
  const [membershipAdjustmentError, setMembershipAdjustmentError] =
    useState("");
  const [membershipAdjustmentErrors, setMembershipAdjustmentErrors] =
    useState<MemberBillingErrors>({});
  const [membershipAdjustmentSubmitting, setMembershipAdjustmentSubmitting] =
    useState(false);
  const [currentStatus, setCurrentStatus] = useState<MemberStatus>("Pending");
  const { user: authenticatedUser } = useGetUserFromUserIdInAuthState();
  const memberPagePermissions =
    authenticatedUser?.usagePermissions?.chamberMemberPro_EnabledPages || [];
  // IF11 MB5 / Q31 (Update Member Form :94-99, :2766-2769): staff need
  // "Change Existing Member Company Name"; the owner and root admin always can.
  const canEditCompanyName = authenticatedUser?.userType !== UserType.ChamberStaff ||
    (memberPagePermissions as string[]).includes(CMP_PERMISSION_VALUES.changeMemberCompanyName);
  const profileSaveInFlightRef = useRef(false);
  const boardApprovalInFlightRef = useRef(false);
  const [profileSaveInFlight, setProfileSaveInFlight] = useState(false);
  const [statusMessage, setStatusMessage] = useState<StatusMessage | null>(
    null
  );
  const [manualPaymentDialogOpen, setManualPaymentDialogOpen] = useState(false);
  const [manualPaymentError, setManualPaymentError] = useState("");
  const [manualPaymentDraft, setManualPaymentDraft] =
    useState<MemberManualPaymentDraft>({
      manualPaymentMethodId: "",
      receivedDate: getMemberManualPaymentLatestDate({}),
      reference: "",
    });
  const [footerError, setFooterError] = useState("");
  const [logoError, setLogoError] = useState("");
  const [confirmationModal, setConfirmationModal] =
    useState<ConfirmationModal>(null);
  const [memberActionError, setMemberActionError] = useState("");
  // CMP Dashboard (19) L6892-6898: suspend, unsuspend and delete need a reason
  // that is saved to the Member Communication Log.
  const [statusReason, setStatusReason] = useState("");
  const [statusReasonError, setStatusReasonError] = useState(false);
  useEffect(() => { setMemberActionError(""); setStatusReason(""); setStatusReasonError(false); }, [confirmationModal]);
  // CMP Dashboard (19) MISSING REQUIRED FIELDS pop-up for contact saves.
  const [missingFieldRows, setMissingFieldRows] = useState<MissingRequiredFieldsRow[]>([]);
  // Contacts whose save failed because access is ticked without a name and
  // valid email: their fields show the design red border and nudge.
  const [contactAccessTriedRoles, setContactAccessTriedRoles] = useState<string[]>([]);
  const [billingAddressCopySelected, setBillingAddressCopySelected] = useState<boolean | null>(null);
  const [successDialog, setSuccessDialog] =
    useState<MemberActionSuccessDialogState | null>(null);
  const [creditCardDialogOpen, setCreditCardDialogOpen] = useState(false);
  const contactLoginSaveDialogRef = useRef<MemberActionSuccessDialogState | null>(null);
  const [credentialModalMode, setCredentialModalMode] =
    useState<CredentialModalMode>(null);
  const [credentialActionError, setCredentialActionError] = useState("");
  const [
    confirmedCredentialsSentAtUtcMilli,
    setConfirmedCredentialsSentAtUtcMilli,
  ] = useState<number | null>(null);
  const [confirmedAdminLoginEmail, setConfirmedAdminLoginEmail] =
    useState<string | null>(null);
  const credentialActionInFlightRef = useRef(false);
  const editableAdminLoginEmailRef = useRef("");
  const currentMemberAdminLoginEmailRef = useRef("");
  const currentCredentialMemberIdRef = useRef("");
  const [memberLoginCollision, setMemberLoginCollision] =
    useState<PendingMemberLoginCollision | null>(null);
  const memberLoginCollisionRef =
    useRef<PendingMemberLoginCollision | null>(null);
  const memberLoginCollisionSequenceRef = useRef(0);
  // M5: two focus-trapped dialogs hang the browser. A login collision found
  // while saving from the Unsaved Changes prompt closes that prompt and keeps
  // its target step here, so navigation can continue after the collision.
  const stepAfterMemberLoginCollisionRef = useRef<number | null>(null);
  const memberLoginCollisionSingleFlightRef = useRef(
    createMemberLoginCollisionSingleFlight()
  );
  const [memberLoginCollisionLoading, setMemberLoginCollisionLoading] =
    useState(false);
  const [previewEmailMode, setPreviewEmailMode] =
    useState<CredentialModalMode>(null);
  const [affinityDraft, setAffinityDraft] = useState(createEmptyAffinityDraft);
  const [affinityEnrollmentError, setAffinityEnrollmentError] = useState("");
  // Save on the Affinity card first asks for confirmation (Affinity Program
  // Partner PROCEED/CANCEL modal per the step 3 spec annotations).
  const [affinityConfirmOpen, setAffinityConfirmOpen] = useState(false);
  // Design (13) Step 5 pop-ups for "Invoice Affiliated Primary Company".
  const [affiliatePrompt, setAffiliatePrompt] = useState<"none" | "confirm" | null>(null);
  // Row being edited via the Enrolled Affinity Programs table's Edit button
  // (the "Update {program name}" modal).
  const [editingAffinityEnrollment, setEditingAffinityEnrollment] =
    useState<AffinityProgramEnrollment | null>(null);
  const affinityDialogContentRef = useRef<HTMLDivElement>(null);
  const membershipAdjustmentHasChanges = hasMembershipAdjustmentChanges({
    draft: membershipAdjustmentDraft,
    persisted: activeStep === 5 && renewalFormBaseline ? renewalFormBaseline : formState.membership,
  });
  const hasUnsavedProfileChanges = hasUnsavedMemberFormChanges({
    baseline: savedFormBaseline,
    current: formState,
  });
  const affinityDraftHasChanges =
    affinityDraft.affinityProgramPartnerId.trim().length > 0 ||
    affinityDraft.idNumber.trim().length > 0 ||
    affinityDraft.startDate.trim().length > 0 ||
    affinityDraft.billingOption.trim().length > 0 ||
    affinityDraft.status !== "Active" ||
    affinityDraft.notes.trim().length > 0;
  const editingAffinityEnrollmentBaseline = editingAffinityEnrollment
    ? formState.affinityProgramEnrollments.find(
        (enrollment) => enrollment.id === editingAffinityEnrollment.id
      )
    : undefined;
  const editingAffinityEnrollmentHasChanges = editingAffinityEnrollment
    ? !editingAffinityEnrollmentBaseline ||
      hasUnsavedFormChanges({
        baseline: editingAffinityEnrollmentBaseline,
        current: editingAffinityEnrollment,
      })
    : false;
  // Contact dashboard access drafts (Claude Design (8)): the access checkbox
  // and toggle change here and the contact's Save Changes applies them.
  const [contactAccessDrafts, setContactAccessDrafts] = useState<Record<string, boolean>>({});
  const contactAccessDraftsRef = useRef(contactAccessDrafts);
  contactAccessDraftsRef.current = contactAccessDrafts;
  const hasContactAccessDrafts = Object.keys(contactAccessDrafts).length > 0;
  const isDirty =
    staffNoteDraft.trim().length > 0 ||
    additionalContactDraftDirty ||
    hasUnsavedProfileChanges ||
    affinityDraftHasChanges ||
    editingAffinityEnrollmentHasChanges ||
    hasContactAccessDrafts;
  const openDraftsRef = useRef(false);
  openDraftsRef.current = additionalContactDraftDirty || affinityDraftHasChanges || editingAffinityEnrollmentHasChanges || membershipAdjustmentHasChanges;

  const {
    data: detailData,
    loading: detailLoading,
    error: detailError,
    refetch: refetchDetail,
  } = useGetCmpMemberDetailQuery({
    variables: {
      input: {
        chamberId,
        memberId: memberIdForInput,
      },
    },
    skip: !chamberId || !memberIdForInput,
    fetchPolicy: "cache-and-network",
    context: PRESERVE_ERROR_FOR_CALLER_CONTEXT,
  });
  const detail = detailData?.getCmpMemberDetail;
  const renewalDetail = detail ? getLatestMemberRenewalDetail(detail, confirmedRenewalDetailRef.current) : undefined;
  const member = detail?.member;
  const credentialMemberId = memberIdForInput || member?.id || "";
  const { data: categoriesData } = useGetAllCategoriesQuery();
  const {
    data: globalSettingsData,
    loading: globalSettingsLoading,
  } = useCmpGlobalSettings(chamberId);
  const {
    data: membershipSettingsData,
    loading: membershipSettingsLoading,
  } = useCmpMembershipSettings(chamberId);
  const {
    data: governanceSettingsData,
    loading: governanceSettingsLoading,
    error: governanceSettingsError,
  } = useCmpGovernanceSettings(chamberId, "cache-and-network", memberRequestApolloContext);
  const chamberSettingsLoading =
    globalSettingsLoading || membershipSettingsLoading;
  const {
    data: affinityProgramsData,
    loading: affinityProgramsLoading,
    error: affinityProgramsError,
  } = useGetAllAffinityProgramPartnersForChamberQuery({
    variables: { chamberId },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
    context: memberRequestApolloContext,
  });
  // Primary Member companies for the Associate Member Company dropdown come
  // from the chamber's real (Active) member list, per the step 3 spec's
  // "Select the Primary Member Company" drop-down.
  // Both lookups show their error under the Primary Member company field
  // instead of opening the error page.
  const {
    data: primaryCompaniesData,
    loading: primaryCompaniesLoading,
    error: primaryCompaniesError,
  } =
    useGetCmpManageMembersQuery({
      variables: {
        input: {
          chamberId,
          // The primary company picker needs member rows only, which skips
          // the chamber invoice read (large chambers: seconds faster).
          includeInvoiceSummary: false,
          lifecycleStatuses: [CmpMemberLifecycleStatusEnum.Active],
        },
      },
      skip: !chamberId,
      fetchPolicy: "cache-and-network",
      context: memberRequestApolloContext,
    });
  // The affiliated Primary Member's own record gives the invoice payer
  // (Accounts Payable contact and billing address).
  const affiliatedPrimaryMemberId =
    formState.association.isAssociateCompanyMember &&
    (primaryCompaniesData?.getCmpManageMembers.rows || []).some(
      (row) => row.id === formState.association.primaryMemberCompanyId
    )
      ? formState.association.primaryMemberCompanyId
      : "";
  const {
    data: affiliatedPrimaryMemberData,
    error: affiliatedPrimaryMemberError,
  } = useGetCmpMemberDetailQuery({
    variables: { input: { chamberId, memberId: affiliatedPrimaryMemberId } },
    skip: !chamberId || !affiliatedPrimaryMemberId,
    fetchPolicy: "cache-first",
    context: memberRequestApolloContext,
  });
  const {
    data: previewEmailData,
    loading: previewEmailLoading,
    error: previewEmailError,
  } = useGetSampleLoginInfoEmailQuery({
    variables: {
      chamberMemberId: credentialMemberId,
      type:
        previewEmailMode === "resetPassword"
          ? LoginInfoEmailTypes.ResetPassword
          : LoginInfoEmailTypes.BrandNew,
    },
    skip: !previewEmailMode || !credentialMemberId,
    fetchPolicy: "network-only",
    context: PRESERVE_ERROR_FOR_CALLER_CONTEXT,
  });
  const [updateCmpMember, { loading: updatingMember }] =
    useUpdateCmpMemberMutation({
      context: PRESERVE_ERROR_FOR_CALLER_CONTEXT,
    });
  const [
    getMemberDashboardLoginAvailability,
    { loading: checkingMemberDashboardLoginAvailability },
  ] = useGetMemberDashboardLoginAvailabilityLazyQuery({
    fetchPolicy: "network-only",
    context: PRESERVE_ERROR_FOR_CALLER_CONTEXT,
  });
  const [
    chamberMemberChangeAdminConsoleLoginEmail,
    { loading: changingAdminLoginEmail },
  ] = useChamberMemberChangeAdminConsoleLoginEmailMutation({
    context: PRESERVE_ERROR_FOR_CALLER_CONTEXT,
  });
  const [approveCmpMemberBoard, { loading: approvingBoard }] =
    useApproveCmpMemberBoardMutation({ context: memberRequestApolloContext });
  const [sendCmpInvoicePaymentRequest, { loading: sendingFirstInvoice }] =
    useSendCmpInvoicePaymentRequestMutation({ context: memberRequestApolloContext });
  const [resendCmpInvoicePaymentRequest, { loading: resendingInvoice }] =
    useResendCmpInvoicePaymentRequestMutation({ context: memberRequestApolloContext });
  const [recordCmpMemberManualPayment, { loading: recordingMemberPayment }] =
    useRecordCmpMemberManualPaymentMutation({
      context: PRESERVE_ERROR_FOR_CALLER_CONTEXT,
    });
  const [suspendCmpMember, { loading: suspendingMember }] =
    useSuspendCmpMemberMutation({ context: memberRequestApolloContext });
  const [cancelCmpMemberRenewal, { loading: cancellingRenewal }] =
    useCancelCmpMemberRenewalMutation({ context: memberRequestApolloContext });
  const [uncancelCmpMemberRenewal, { loading: uncancellingRenewal }] =
    useUncancelCmpMemberRenewalMutation({ context: memberRequestApolloContext });
  const [activateCmpMember, { loading: activatingMember }] =
    useActivateCmpMemberMutation({ context: memberRequestApolloContext });
  const [archiveCmpMember, { loading: archivingMember }] =
    useArchiveCmpMemberMutation({
      context: memberPastTransitionApolloContext,
    });
  const [startApplicationFromExisting, { loading: startingApplication }] =
    useStartCmpMemberApplicationFromExistingMutation({ context: memberRequestApolloContext });
  const [
    setNewTemporaryChamberMemberPassword,
    { loading: generatingCredentials },
  ] = useSetNewTemporaryChamberMemberPasswordMutation({
    context: PRESERVE_ERROR_FOR_CALLER_CONTEXT,
  });
  const [sendLoginInfoEmailToChamberMember, { loading: sendingCredentials }] =
    useSendLoginInfoEmailToChamberMemberMutation({
      context: PRESERVE_ERROR_FOR_CALLER_CONTEXT,
    });
  const statusActions = detail?.statusActions;
  // Monday 13389365001 (S9): the reason shows under every grey Delete Member button.
  const deleteBlockedReason = !statusActions?.canArchive ? statusActions?.archiveBlockedReason : null;
  const paymentRequestWasSent =
    statusActions?.paymentRequestState === CmpPaymentRequestStateEnum.Sent;
  const sendingInvoice = sendingFirstInvoice || resendingInvoice;
  const isReadOnly = !!detail?.isReadOnly || currentStatus === "Past";
  const credentialOperationLoading =
    generatingCredentials ||
    sendingCredentials ||
    checkingMemberDashboardLoginAvailability ||
    memberLoginCollisionLoading;
  const actionLoading =
    profileSaveInFlight ||
    approvingBoard ||
    sendingInvoice ||
    recordingMemberPayment ||
    suspendingMember ||
    cancellingRenewal ||
    uncancellingRenewal ||
    activatingMember ||
    archivingMember ||
    startingApplication ||
    generatingCredentials ||
    sendingCredentials ||
    changingAdminLoginEmail ||
    checkingMemberDashboardLoginAvailability ||
    memberLoginCollisionLoading;
  const categories = useMemo(
    () => sortMemberCategoryOptions(categoriesData?.getAllCategories || []),
    [categoriesData?.getAllCategories]
  );
  const globalSettings =
    globalSettingsData?.getChamberMemberProSettings;
  const membershipSettings =
    membershipSettingsData?.getChamberMemberProSettings;
  const chamberSettings = useMemo(
    () =>
      globalSettings && membershipSettings
        ? {
            ...globalSettings,
            membership: membershipSettings.membership,
          }
        : undefined,
    [globalSettings, membershipSettings]
  );
  const enabledManualPaymentMethods =
    chamberSettings?.paymentsAndTaxes.manualPaymentMethods.filter(
      (method) => method.enabled
    ) || [];
  const chamberGroupInsuranceCompanyName =
    chamberSettings?.paymentsAndTaxes.chamberGroupInsurancePayment.billingDetails
      .companyName;
  const chamberGroupInsuranceBillingDetails =
    chamberSettings?.paymentsAndTaxes.chamberGroupInsurancePayment.billingDetails;
  const chamberGroupInsuranceFullBillingDetails =
    chamberGroupInsuranceBillingDetails as
      | (typeof chamberGroupInsuranceBillingDetails & {
          addressLine2?: string | null;
          province?: string | null;
          country?: string | null;
        })
      | undefined;
  const chamberGroupInsuranceBillingSnapshot = {
    customerName: chamberGroupInsuranceBillingDetails?.companyName || "",
    contactName: chamberGroupInsuranceBillingDetails?.contactName || "",
    contactEmail: chamberGroupInsuranceBillingDetails?.contactEmail || "",
    contactPhone: chamberGroupInsuranceBillingDetails?.phoneNumber || "",
    addressLine1: chamberGroupInsuranceBillingDetails?.streetAddress || "",
    addressLine2:
      chamberGroupInsuranceFullBillingDetails?.addressLine2 || "",
    city: chamberGroupInsuranceBillingDetails?.city || "",
    province: chamberGroupInsuranceFullBillingDetails?.province || "",
    postalCode: chamberGroupInsuranceBillingDetails?.postalCode || "",
    country: chamberGroupInsuranceFullBillingDetails?.country || "Canada",
  };
  const chamberGroupInsuranceOptionAvailable = Boolean(
    chamberSettings?.paymentsAndTaxes.chamberGroupInsurancePayment.enabled &&
      Object.keys(
        getMemberThirdPartyBillingSnapshotErrors(
          chamberGroupInsuranceBillingSnapshot
        )
      ).length === 0
  );
  const membershipModels = useMemo(
    () =>
      chamberSettings?.membership.membershipModels.filter((model) => model.enabled) ||
      [],
    [chamberSettings?.membership.membershipModels]
  );
  const creditCardPaymentsReady = getCmpCreditCardPaymentsReady(
    chamberSettings?.paymentsAndTaxes.stripeConnect
  );
  const membershipBillingDraftValue = (activeStep === 5)
    ? {
        ...(membershipAdjustmentDraft ||
          cloneMembershipFormState(formState.membership)),
        // Consent remains an ordinary member field and add-ons are immutable in
        // the adjustment flow, so both always come from persisted form state.
        emailCommunicationConsentConfirmed:
          formState.membership.emailCommunicationConsentConfirmed,
        selectedAdditionalCostIds: [
          ...formState.membership.selectedAdditionalCostIds,
        ],
      }
    : formState.membership;
  const isPendingJohnsonGroupSelection =
    currentStatus === "Pending" &&
    membershipBillingDraftValue.billingResponsibility ===
      "chamberGroupInsurance";
  const isThirdPartyMembershipBilling =
    membershipBillingDraftValue.billingResponsibility ===
      "affiliatedPrimaryCompany" ||
    membershipBillingDraftValue.billingResponsibility === "otherThirdParty" ||
    membershipBillingDraftValue.billingResponsibility ===
      "chamberGroupInsurance";
  const monthlyScheduleRequiresUnavailableCard =
    !creditCardPaymentsReady && (activeStep === 5 || !isThirdPartyMembershipBilling);
  const chamberEnabledPaymentSchedules =
    chamberSettings?.membership.enabledPaymentSchedules || [
      ChamberMemberProPaymentScheduleEnum.Annual,
    ];
  const enabledPaymentSchedules = getMemberSelectablePaymentSchedules({
    enabledPaymentSchedules: chamberEnabledPaymentSchedules,
    savedPaymentSchedule: detail?.member.cmpExtension?.membership?.paymentSchedule,
  }).filter(
    (schedule) =>
      schedule !== ChamberMemberProPaymentScheduleEnum.Monthly ||
      !monthlyScheduleRequiresUnavailableCard
  );
  const hasUnavailableMonthlySchedule =
    monthlyScheduleRequiresUnavailableCard &&
    membershipBillingDraftValue.paymentSchedule ===
      ChamberMemberProPaymentScheduleEnum.Monthly;
  const paymentScheduleResolution = normalizeMemberPaymentSchedule({
    enabledPaymentSchedules,
    storedPaymentSchedule: membershipBillingDraftValue.paymentSchedule,
  });
  const membershipBillingValue: MembershipFormState = {
    ...membershipBillingDraftValue,
    paymentSchedule: (activeStep === 5)
      ? paymentScheduleResolution.paymentSchedule
      : membershipBillingDraftValue.paymentSchedule,
  };
  const visibleMembershipBillingOptions = membershipBillingOptions.filter(
    (option) =>
      option.value !== "otherThirdParty" ||
      membershipBillingValue.billingResponsibility === "otherThirdParty"
  );
  const paymentScheduleOptions: MemberBillingOption[] =
    enabledPaymentSchedules.map((schedule) => {
      const label =
        schedule === ChamberMemberProPaymentScheduleEnum.Monthly
          ? "Monthly"
          : "Yearly";
      return {
        value: schedule,
        // The member's saved schedule after the chamber disabled it.
        label: chamberEnabledPaymentSchedules.includes(schedule)
          ? label
          : `${label} (Current)`,
      };
    });

  if (membershipBillingValue.paymentSchedule && !paymentScheduleOptions.some((option) => option.value === membershipBillingValue.paymentSchedule)) {
    paymentScheduleOptions.unshift({
      value: membershipBillingValue.paymentSchedule,
      label: `${membershipBillingValue.paymentSchedule === ChamberMemberProPaymentScheduleEnum.Monthly ? "Monthly" : "Yearly"} (Current - unavailable)`,
      disabled: true,
    });
  }

  const shouldShowPaymentSchedule =
    shouldShowMemberPaymentScheduleSelection(paymentScheduleResolution) ||
    hasUnavailableMonthlySchedule;
  const membershipDiscountEnabled =
    !!chamberSettings?.paymentsAndTaxes.membershipDiscount.enabled ||
    formState.membership.membershipDiscount.enabled;
  const allMembershipModels =
    chamberSettings?.membership.membershipModels || [];
  const selectedMembershipModel = allMembershipModels.find(
    (model) => model.id === membershipBillingValue.membershipTypeId
  );
  const previousMembershipModel = allMembershipModels.find(
    (model) => model.id === formState.membership.membershipTypeId
  );
  // Member Insights: the charity number shows for the saved or the selected
  // membership type when CAP flags it, or when a number is already saved.
  const showCharityRegistrationNumber =
    shouldShowMemberCharityRegistrationNumber({
      membershipModels: [previousMembershipModel, selectedMembershipModel],
      savedValue: savedFormBaseline.companyDetails.charityRegistrationNumber,
    });
  // Design: a generated current-cycle renewal invoice (any status) locks Step 5.
  const isLockedRenewal = activeStep === 5 && renewalDetail?.renewal?.stepState === CmpMemberRenewalStepStateEnum.InvoiceGenerated;
  const renewalStatusCard = getMemberRenewalStatusCard({
    renewal: detail?.renewal,
    autoRenewOn: !!(member?.cmpExtension?.membership?.paymentChoice?.autoRenew && chamberSettings?.membership.allowAutoRenewal),
    timeZone: normalizeCmpTimeZone(chamberSettings?.setup.timeZone).timeZone,
  });
  // IF11 MB8: the status model panel where the backend returns a member status (Sand Bay first).
  const statusModel = !!detail?.memberStatus;
  const statusRenewalDateLabel = formatUtcCalendarDate(member?.cmpExtension?.membership?.pricingSnapshot.renewalDateUtcMilli);
  // Same rule as the backend isCmpNoChargeMembership: no renewal invoice goes to this member.
  const memberNoCharge = member?.cmpExtension?.membership?.paymentResponsibility === ChamberMemberProMembershipModelPaymentResponsibilityEnum.NoCharge ||
    member?.cmpExtension?.membership?.paymentChoice?.type === CmpPaymentChoiceTypeEnum.Free;
  const statusModelPanelStatus = detail?.memberStatus && detail.memberStatus !== CmpMemberStatusEnum.Pending ? detail.memberStatus : null;
  // Design "Suspended effective" date: the latest Suspend in the Member Communication Log.
  const suspendedOnUtcMilli = (detail?.staffNotes || [])
    .filter((note) => /^Member suspended\./.test(note.body || ""))
    .reduce<number | null>((latest, note) => Math.max(latest || 0, note.createdAtUtcMilli || 0) || latest, null);
  const persistedMembership = (activeStep === 5 ? renewalDetail : detail)?.member.cmpExtension?.membership;
  const memberBillingSnapshot = getMemberBillingSnapshot({
    memberId: member?.id || "",
    membership: persistedMembership,
    invoices: detail?.invoices || [],
  });
  const savedBillingSnapshot = isLockedRenewal && memberBillingSnapshot ? {
    ...memberBillingSnapshot,
    membershipInvoiceTotalInCents: renewalDetail?.renewal?.cycleInvoice?.totalAmountInCents,
  } : memberBillingSnapshot;
  const showSavedBillingSnapshot = isLockedRenewal || activeStep !== 5;
  const formatSavedBillingAmount = (amount: number | null | undefined) =>
    amount === null || amount === undefined || !savedBillingSnapshot
      ? "Amount unavailable"
      : new Intl.NumberFormat("en-CA", { style: "currency", currency: savedBillingSnapshot.currency }).format(amount / 100);
  const pendingMembershipChange =
    (activeStep === 5 ? renewalDetail?.renewal?.pendingMembershipChange : detail?.member.cmpExtension?.pendingMembershipChange);
  const membershipPresentation = getMemberMembershipPresentation({
    configuredOptions: membershipModels.map((model) => ({
      value: model.id,
      label: model.name,
    })),
    isSettingsLoading: chamberSettingsLoading,
    membershipTypeId: activeStep === 5 ? membershipBillingValue.membershipTypeId : formState.membership.membershipTypeId,
    membershipTypeName: activeStep === 5 && pendingMembershipChange?.newMembership.membershipTypeId === membershipBillingValue.membershipTypeId
      ? pendingMembershipChange.newMembership.membershipTypeName
      : persistedMembership?.membershipTypeName || previousMembershipModel?.name,
  });
  const membershipOptions: MemberBillingOption[] =
    membershipPresentation.options;

  const membershipPaymentMethodOptions: MemberBillingOption[] = [];

  if (creditCardPaymentsReady) {
    membershipPaymentMethodOptions.push({
      value: "creditCard",
      label: "Credit Card",
    });
  }

  (chamberSettings?.paymentsAndTaxes.manualPaymentMethods || [])
    .filter((method) => method.enabled)
    .forEach((method) => {
      const value = getPaymentMethodFromChoice({
        type: CmpPaymentChoiceTypeEnum.Manual,
        manualPaymentMethodId: method.id,
      });

      if (
        value &&
        !membershipPaymentMethodOptions.some(
          (option) => option.value === value
        )
      ) {
        membershipPaymentMethodOptions.push({
          value,
          label: method.label,
        });
      }
    });

  const allowedMembershipPaymentMethodOptions =
    membershipBillingValue.paymentSchedule ===
    ChamberMemberProPaymentScheduleEnum.Monthly
      ? membershipPaymentMethodOptions.filter(
          (option) => option.value === "creditCard"
        )
      : membershipPaymentMethodOptions;

  if (
    membershipBillingValue.paymentMethod &&
    !allowedMembershipPaymentMethodOptions.some(
      (option) => option.value === membershipBillingValue.paymentMethod
    )
  ) {
    allowedMembershipPaymentMethodOptions.unshift({
      value: membershipBillingValue.paymentMethod,
      label: `${
        persistedMembership?.paymentChoice.manualPaymentMethodLabel ||
        "Current payment method"
      } (Current - unavailable)`,
      disabled: true,
    });
  }

  const displayedMembershipAddOns = useMemo(
    () =>
      getDisplayedMembershipAddOns({
        legacyDescription: `This saved benefit is no longer available in ${noun.Singular} Set Up. Its current amount is unavailable, it remains attached for historical reference, and it will not be charged by this membership update.`,
        membershipModels: chamberSettings?.membership.membershipModels || [],
        membershipTypeId: formState.membership.membershipTypeId,
        selectedAdditionalCostIds: formState.membership.selectedAdditionalCostIds,
      }),
    [
      chamberSettings?.membership.membershipModels,
      formState.membership.membershipTypeId,
      formState.membership.selectedAdditionalCostIds,
      noun,
    ]
  );
  const selectedAdditionalCostIds = new Set(
    formState.membership.selectedAdditionalCostIds
  );
  const selectedAddOnTotalInCents = displayedMembershipAddOns.reduce(
    (total, addOn) =>
      total +
      (selectedAdditionalCostIds.has(addOn.id)
        ? Math.round((addOn.annualAmountInDollars || 0) * 100)
        : 0),
    0
  );
  // Tax-exempt add-ons are billed without tax (Chamber Set Up add-on option).
  const selectedTaxExemptAddOnTotalInCents = displayedMembershipAddOns.reduce(
    (total, addOn) =>
      total +
      (selectedAdditionalCostIds.has(addOn.id) && addOn.taxExempt
        ? Math.round((addOn.annualAmountInDollars || 0) * 100)
        : 0),
    0
  );
  const selectedTaxableAddOnTotalInCents =
    selectedAddOnTotalInCents - selectedTaxExemptAddOnTotalInCents;
  const selectedAddOnAmountUnavailable = displayedMembershipAddOns.some(
    (addOn) =>
      selectedAdditionalCostIds.has(addOn.id) &&
      addOn.annualAmountInDollars === null
  );
  const baseMembershipPrice = getMembershipBasePrice({
    fullTimeEmployees: membershipBillingValue.fullTimeEmployees,
    membershipModel: selectedMembershipModel,
    partTimeEmployees: membershipBillingValue.partTimeEmployees,
    partTimeEmployeesPerFullTime:
      chamberSettings?.membership.partTimeEmployeesPerFullTime,
    paymentSchedule: membershipBillingValue.paymentSchedule,
  });
  const persistedPricingSnapshot = persistedMembership?.pricingSnapshot;
  const isShowingPersistedUnavailableMembership =
    !!membershipBillingValue.membershipTypeId &&
    !selectedMembershipModel &&
    membershipBillingValue.membershipTypeId ===
      formState.membership.membershipTypeId;
  const baseMembershipAmountInCents = isShowingPersistedUnavailableMembership
    ? persistedPricingSnapshot?.baseAmountInCents || 0
    : baseMembershipPrice.amountInCents;
  // Updating an existing member uses regular renewal pricing. Signup
  // proration and its "month you are joining" language are Create-only.
  const effectiveMembershipAmountInCents = baseMembershipAmountInCents;
  const membershipDiscountInCents =
    membershipDiscountEnabled && membershipBillingValue.membershipDiscount.enabled
    ? Math.round(
        getNumber(membershipBillingValue.membershipDiscount.amount) * 100
      )
    : 0;
  const isMonthlySchedule =
    membershipBillingValue.paymentSchedule ===
      ChamberMemberProPaymentScheduleEnum.Monthly;
  const membershipInvoiceTaxRatePercentage =
    getMembershipInvoiceTaxRatePercentage({
      invoiceTaxSettings:
        chamberSettings?.paymentsAndTaxes.invoiceTaxSettings.membershipInvoices,
    });
  const getMembershipInvoiceTaxInCents = (taxableAmountInCents: number) =>
    Math.round((taxableAmountInCents * membershipInvoiceTaxRatePercentage) / 100);
  // BILL-002/003: a monthly membership is invoiced ONCE for the full
  // membership total (monthly price annualized over the 12-installment plan)
  // while add-on benefits go on a SECOND invoice at full (annual) amount.
  // Spec §8: step-4 totals are tax-inclusive to match Create Member's
  // presentation (display-only — saving still generates no invoices).
  const membershipInvoiceTaxableInCents = Math.max(
    0,
    (activeStep === 5 && getPaymentResponsibility(membershipBillingValue.billingResponsibility) === ChamberMemberProMembershipModelPaymentResponsibilityEnum.NoCharge
      ? 0
      : isMonthlySchedule && activeStep !== 5
        ? baseMembershipAmountInCents * 12
        : effectiveMembershipAmountInCents) - membershipDiscountInCents
  );
  const membershipInvoiceTotalInCents =
    membershipInvoiceTaxableInCents +
    getMembershipInvoiceTaxInCents(membershipInvoiceTaxableInCents);
  const addOnInvoiceTotalInCents =
    selectedAddOnTotalInCents +
    getMembershipInvoiceTaxInCents(selectedTaxableAddOnTotalInCents);
  const invoiceTaxableInCents = Math.max(
    0,
    effectiveMembershipAmountInCents +
      selectedTaxableAddOnTotalInCents -
      membershipDiscountInCents
  );
  const invoiceTotalInCents =
    invoiceTaxableInCents +
    selectedTaxExemptAddOnTotalInCents +
    getMembershipInvoiceTaxInCents(invoiceTaxableInCents);
  const affinityProgramOptions =
    affinityProgramsData?.getAllAffinityProgramPartnersForChamber
      ?.filter(
        (program) =>
          program.activationStatus !==
          AffinityProgramPartnerActivationStatusEnum.Deactivated
      )
      .sort((firstProgram, secondProgram) =>
        firstProgram.companyName.localeCompare(secondProgram.companyName)
      ) || [];
  const primaryMemberCompanySelectOptions = (() => {
    const options = (primaryCompaniesData?.getCmpManageMembers.rows || [])
      .filter((row) => row.id !== (memberIdForInput || member?.id))
      .map((row) => ({ value: row.id, label: row.companyName }))
      .sort((firstOption, secondOption) =>
        firstOption.label.localeCompare(secondOption.label)
      );
    const storedPrimaryCompanyValue =
      formState.association.primaryMemberCompanyId;

    if (
      storedPrimaryCompanyValue &&
      !options.some((option) => option.value === storedPrimaryCompanyValue)
    ) {
      // Older records stored a typed-in company name instead of a member id;
      // keep the saved value selectable so it still renders.
      options.unshift({
        value: storedPrimaryCompanyValue,
        label: storedPrimaryCompanyValue,
      });
    }

    return options;
  })();
  const affiliatedPrimaryCompanyName =
    primaryMemberCompanySelectOptions.find(
      (option) =>
        option.value === formState.association.primaryMemberCompanyId
    )?.label || "";
  const persistedMemberAdminLoginEmail =
    getPersistedMemberLoginEmail(member);
  const memberAdminEmail =
    confirmedAdminLoginEmail || persistedMemberAdminLoginEmail;
  editableAdminLoginEmailRef.current = normalizeMemberLoginEmail(
    formState.contacts.administrator.email
  );
  currentMemberAdminLoginEmailRef.current = memberAdminEmail;
  currentCredentialMemberIdRef.current = credentialMemberId;
  const memberDisplayName = member?.name || formState.directory.companyName || "this member";
  const memberAdminName = member?.adminConsoleContactName || savedFormBaseline.contacts.administrator.name || member?.name || "";
  const hasUnsavedAdminLoginEmailChange =
    hasUnsavedMemberLoginEmailChange({
      editableLoginEmail: formState.contacts.administrator.email,
      persistedLoginEmail: memberAdminEmail,
    });
  const latestCredentialsSentAtUtcMilli =
    getLatestCredentialSentAtUtcMilli({
      confirmedCredentialsSentAtUtcMilli,
      persistedCredentialsSentAtUtcMilli: member?.credentialsSentAtUtcMilli,
    });
  const isWebsiteOriginMember =
    member?.cmpExtension?.source === CmpMemberApplicationSourceEnum.Website;
  const setPendingMemberLoginCollision = (
    collision: Omit<PendingMemberLoginCollision, "id">
  ) => {
    const nextCollision = {
      ...collision,
      id: memberLoginCollisionSequenceRef.current + 1,
    };

    memberLoginCollisionSequenceRef.current = nextCollision.id;
    memberLoginCollisionRef.current = nextCollision;
    setPendingStep(step => {
      if (step !== null) stepAfterMemberLoginCollisionRef.current = step;
      return null;
    });
    setMemberLoginCollision(nextCollision);
  };
  const clearPendingMemberLoginCollision = () => {
    if (memberLoginCollisionSingleFlightRef.current.isRunning()) {
      return;
    }

    // Cancel keeps staff on this step with their edits.
    stepAfterMemberLoginCollisionRef.current = null;
    memberLoginCollisionSequenceRef.current += 1;
    memberLoginCollisionRef.current = null;
    setMemberLoginCollision(null);
  };
  const isPendingMemberLoginCollisionCurrent = (
    collision: PendingMemberLoginCollision
  ): boolean => {
    if (
      memberLoginCollisionRef.current?.id !== collision.id ||
      collision.memberId !== credentialMemberId
    ) {
      return false;
    }

    const currentLoginEmail =
      collision.action === "save"
        ? editableAdminLoginEmailRef.current
        : currentMemberAdminLoginEmailRef.current;

    return (
      currentCredentialMemberIdRef.current === collision.memberId &&
      normalizeMemberLoginEmail(currentLoginEmail) ===
      collision.actionLoginEmail
    );
  };
  const setMemberLoginActionError = (
    action: MemberLoginCollisionAction,
    message: string
  ) => {
    if (action === "sendCredentials" || action === "downloadCredentials") {
      setCredentialActionError(message);
      return;
    }

    setStatusMessage({ severity: "error", message });
    scrollToTop();
  };
  const checkMemberDashboardLoginAvailability = async ({
    loginEmail,
    memberId,
  }: {
    loginEmail: string;
    memberId: string;
  }) => {
    const result = await getMemberDashboardLoginAvailability({
      variables: {
        input: {
          chamberId,
          loginEmail,
          memberId,
        },
      },
    });

    return getMemberLoginAvailabilityDecision({
      availability:
        result.data?.getMemberDashboardLoginAvailability,
      expectedLoginEmail: loginEmail,
      queryError: result.error,
    });
  };
  const credentialLifecycleRef = useRef(currentStatus);
  credentialLifecycleRef.current = currentStatus;
  const getCredentialActionBlock = () => {
    const lifecycleBlock = getMemberCredentialLifecycleBlock(lifecycleStatusByLabel[credentialLifecycleRef.current]);
    if (lifecycleBlock) return lifecycleBlock;
    if (editableAdminLoginEmailRef.current !== currentMemberAdminLoginEmailRef.current) return "Select Save Changes in Member Administrator Contact first. Then you can send or reset credentials.";
    if (pendingIdentityReadbackRef.current) return "Refresh the page before you send or reset credentials. This checks that the new sign-in email was saved.";
    return "";
  };
  const handleOpenCredentialModal = (
    mode: Exclude<CredentialModalMode, null>
  ) => {
    const blockedReason = getCredentialActionBlock();
    if (blockedReason) {
      setStatusMessage({ severity: "warning", message: blockedReason });
      return;
    }

    setCredentialActionError("");
    setCredentialModalMode(mode);
  };
  const handleCloseCredentialModal = () => {
    if (credentialOperationLoading) {
      return;
    }

    setCredentialActionError("");
    setCredentialModalMode(null);
  };
  const refetchCredentialMemberDetail = async (): Promise<void> => {
    const refreshedDetailResult = await refetchDetail();

    if (!refreshedDetailResult.data?.getCmpMemberDetail) {
      throw new Error("No refreshed member detail was returned.");
    }
  };
  const recordConfirmedIdentity = (email: string, expectedEditableEmail: string, previousRevision: number | null) => {
    setConfirmedAdminLoginEmail(email);
    currentMemberAdminLoginEmailRef.current = email;
    pendingIdentityReadbackRef.current = { memberId: credentialMemberId, email, previousRevision };
    setFormStateWithBaseline((previous) => {
      const current = normalizeMemberLoginEmail(previous.current.contacts.administrator.email) === expectedEditableEmail
        ? { ...previous.current, contacts: { ...previous.current.contacts, administrator: { ...previous.current.contacts.administrator, email } } }
        : previous.current;
      editableAdminLoginEmailRef.current = normalizeMemberLoginEmail(current.contacts.administrator.email);
      return {
        current,
        savedBaseline: { ...previous.savedBaseline, contacts: { ...previous.savedBaseline.contacts, administrator: { ...previous.savedBaseline.contacts.administrator, email } } },
      };
    });
  };
  const confirmPendingIdentityReadback = async (): Promise<BackendMemberDetail | null> => {
    const pending = pendingIdentityReadbackRef.current;
    if (!pending) return null;
    const result = await refetchDetail();
    const readback = result.data?.getCmpMemberDetail;
    const revision = confirmMemberIdentityRevision({ ...pending, detail: readback });
    if (currentCredentialMemberIdRef.current !== pending.memberId) throw new UserFacingError(memberLoginAvailabilityStaleError);
    savedMemberRevisionRef.current = revision;
    pendingIdentityReadbackRef.current = null;
    return readback || null;
  };
  const redirectToManageMembers = useCallback((message: string) => {
    navigate(RoutePaths.chamberAdmin_members_manage, {
      replace: true,
      state: {
        statusMessage: {
          severity: "info",
          message,
        },
      },
    });
  }, [navigate]);

  const memberUpdatedAtUtcMilli = Math.max(member?.updatedAtUtcMilli || 0, savedMemberRevisionRef.current || 0);
  const memberContactAccounts = useMemberContactAccounts({
    chamberId, memberId: member?.id || "", revision: memberUpdatedAtUtcMilli || member?.createdAtUtcMilli || 0,
    lifecycleStatus: detail?.lifecycleStatus || CmpMemberLifecycleStatusEnum.Pending,
    timeZone: chamberSettings?.setup.timeZone, active: activeStep === 2, readOnly: isReadOnly,
    legacyCredentials: {
      disabled: credentialOperationLoading, hasUnsavedLoginEmail: hasUnsavedAdminLoginEmailChange,
      onSend: () => handleOpenCredentialModal("sendCredentials"), onReset: () => setConfirmationModal("resetPassword"),
    },
    onCompleted: async (revision) => { savedMemberRevisionRef.current = revision; await refetchDetail(); },
    accessDrafts: contactAccessDrafts,
    onAccessDraftChange: (contactId, enabled) => setContactAccessDrafts((current) => {
      const next = { ...current };
      if (enabled === null) delete next[contactId];
      else next[contactId] = enabled;
      return next;
    }),
  });
  const currentMemberRevision = () =>
    Math.max(savedMemberRevisionRef.current || 0, member?.updatedAtUtcMilli || 0) || member?.createdAtUtcMilli || 0;
  // Applies every pending access draft in order. Each change advances the
  // member revision, so the next one reads the latest saved revision.
  const applyContactAccessDrafts = async (contactIds = Object.keys(contactAccessDraftsRef.current)): Promise<boolean> => {
    for (const contactId of contactIds) {
      if (!await memberContactAccounts.applyAccessDraft(contactId, currentMemberRevision())) return false;
    }
    return true;
  };
  useEffect(() => { setContactAccessDrafts({}); }, [member?.id]);
  const memberTimeZone = normalizeCmpTimeZone(chamberSettings?.setup.timeZone).timeZone;
  const memberStamp = useCallback((value: number) => DateTime.fromMillis(value, { zone: memberTimeZone }).toFormat("MMM d, yyyy 'at' h:mm a", { locale: "en-US" }), [memberTimeZone]);

  useEffect(() => {
    const nextHeader = getHeaderCopy(currentStatus, !!detail?.memberStatus, detail?.memberStatus === CmpMemberStatusEnum.Cancelled);

    setPageHeaderOverride({
      ...nextHeader,
      title: `Update ${member?.name || "Member"}`,
      titleStyle: { fontSize: 28, fontWeight: 700, lineHeight: 1.25, color: "#0A0A0A" },
      descriptionClassName: "mt-[6px] whitespace-pre-line text-[#0A0A0A]",
      descriptionStyle: { fontSize: 14, lineHeight: "normal" },
      containerClassName: "member-update-page font-['DM_Sans',sans-serif]",
      horizontalPaddingClassName: "px-[16px] md:px-[40px]",
      headerSpacingClassName: "pt-[32px] pb-0 md:pt-[calc(32px-1rem)]",
      actions: (
        <div className="flex flex-wrap items-start gap-[22px] leading-[normal] text-[#0A0A0A]">
          {!!memberUpdatedAtUtcMilli && <div className="flex items-start gap-[8px]">
            <svg aria-hidden="true" viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#0A0A0A" strokeWidth="2" strokeLinecap="round" className="mt-[2px] shrink-0"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
            <div><p className="text-[14px] font-bold">Last Updated</p>
              <p className="mt-[2px] whitespace-nowrap text-[13px]">{memberStamp(memberUpdatedAtUtcMilli)}</p></div>
          </div>}
          {!!latestCredentialsSentAtUtcMilli && <div className="flex items-start gap-[8px]">
            <StatusActionIcon icon="send" size={17} className="mt-[2px] text-[#10B981]" />
            <div><p className="text-[14px] font-bold">Credentials Sent</p>
              <p className="mt-[2px] whitespace-nowrap text-[13px]">{memberStamp(latestCredentialsSentAtUtcMilli)}</p></div>
          </div>}
        </div>
      ),
    });

    return () => setPageHeaderOverride(null);
  }, [currentStatus, detail?.memberStatus, member?.name, memberUpdatedAtUtcMilli, latestCredentialsSentAtUtcMilli, memberStamp, setPageHeaderOverride]);

  useEffect(() => {
    if (!memberIdForInput) {
      redirectToManageMembers("Select a member from Manage Members to update it.");
    }
  }, [memberIdForInput, redirectToManageMembers]);

  useEffect(() => {
    if (!detail || isDirty || profileSaveInFlightRef.current ||
      (detail.member.updatedAtUtcMilli ?? 0) < (savedMemberRevisionRef.current ?? 0)) {
      return;
    }

    const nextFormState = mapDetailToFormState(detail, {
      configuredInsurancePayerName: chamberGroupInsuranceCompanyName,
      insuranceOptionAvailable: chamberGroupInsuranceOptionAvailable,
    });

    if (
      confirmedAdminLoginEmail &&
      getPersistedMemberLoginEmail(detail.member) !==
        confirmedAdminLoginEmail
    ) {
      nextFormState.contacts.administrator.email =
        confirmedAdminLoginEmail;
    }

    savedBaselineRef.current = nextFormState;
    savedMemberRevisionRef.current = detail.member.updatedAtUtcMilli ?? null;
    setFormStateWithBaseline({
      current: nextFormState,
      savedBaseline: nextFormState,
    });
    setMembershipAdjustmentDraft(null);
    setMembershipAdjustmentError("");
    setMembershipAdjustmentErrors({});
    setCurrentStatus(statusLabels[detail.lifecycleStatus]);
  }, [
    chamberGroupInsuranceCompanyName,
    chamberGroupInsuranceOptionAvailable,
    confirmedAdminLoginEmail,
    detail,
    isDirty,
    setFormStateWithBaseline,
  ]);

  useEffect(() => {
    setConfirmedAdminLoginEmail(null);
    setConfirmedCredentialsSentAtUtcMilli(null);
  }, [credentialMemberId]);

  useEffect(() => {
    if (
      confirmedAdminLoginEmail &&
      persistedMemberAdminLoginEmail === confirmedAdminLoginEmail
    ) {
      setConfirmedAdminLoginEmail(null);
    }
  }, [confirmedAdminLoginEmail, persistedMemberAdminLoginEmail]);

  const markDirty = () => {
    setStatusMessage(null);
    setFooterError("");
  };

  const scrollToTop = () => {
    const scrollContainer = document.querySelector(".flex-1.overflow-auto");

    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getRenewalFormState = (nextDetail: CmpMemberDetailFieldsFragment) =>
    mapDetailToFormState(getMemberRenewalFormDetail(nextDetail, confirmedRenewalDetailRef.current), {
      configuredInsurancePayerName: chamberGroupInsuranceCompanyName,
      insuranceOptionAvailable: chamberGroupInsuranceOptionAvailable,
    }).membership;

  const navigateToStep = (step: number) => {
    if (step === 5 && renewalDetail) {
      const initialRenewal = getRenewalFormState(renewalDetail);
      setRenewalFormBaseline(initialRenewal);
      setMembershipAdjustmentDraft(cloneMembershipFormState(initialRenewal));
    } else if (activeStep === 5) {
      setRenewalFormBaseline(null);
      setMembershipAdjustmentDraft(null);
    }
    setActiveStep(step);
    setFooterError("");
    window.setTimeout(scrollToTop, 0);
  };
  const goToStep = (step: number) => {
    if (membershipAdjustmentSubmitting || profileSaveInFlight) return;
    if (step === 5 && currentStatus !== "Active") {
      setRenewalUnavailable(true);
      return;
    }
    if (step < 1 || step > 5 || step === activeStep) return;
    if (isDirty || membershipAdjustmentHasChanges) {
      setPendingStep(step);
      return;
    }
    navigateToStep(step);
  };

  const updateSection = <SectionName extends keyof UpdateMemberFormState>(
    sectionName: SectionName,
    value: Partial<UpdateMemberFormState[SectionName]>
  ) => {
    markDirty();
    setFormState((current) => ({
      ...current,
      [sectionName]: {
        ...current[sectionName],
        ...value,
      },
    }));
  };

  const updateMembershipAdjustmentDraft = (
    value: Partial<MembershipFormState>
  ) => {
    setMembershipAdjustmentError("");
    setMembershipAdjustmentErrors({});
    setStatusMessage(null);
    setMembershipAdjustmentDraft((currentDraft) => ({
      ...(currentDraft || cloneMembershipFormState(formState.membership)),
      ...value,
      // Add-ons are immutable during a membership adjustment.
      selectedAdditionalCostIds: [
        ...formState.membership.selectedAdditionalCostIds,
      ],
    }));
  };

  const updateContact = (
    role: ContactRole,
    field: keyof UpdateMemberFormState["contacts"][ContactRole],
    value: string
  ) => {
    markDirty();
    setFormState((current) => ({
      ...current,
      contacts: {
        ...current.contacts,
        [role]: {
          ...current.contacts[role],
          [field]: value,
        },
      },
      contactSameAs: { ...current.contactSameAs, [role]: false },
    }));
  };

  const handleContactSameAsToggle = (role: ContactRole, checked: boolean) => {
    const sourceContact = getMemberContactCopySource(currentFormRef.current, role);
    if (checked && (!sourceContact.name.trim() || !hasEmailShape(sourceContact.email.trim()))) {
      setContactSameAsNudge(role);
      return;
    }
    markDirty();
    setContactSameAsNudge(null);
    setFormState((current) => ({
      ...current,
      contactSameAs: { ...current.contactSameAs, [role]: checked },
      // Removing the copy selection must not erase entered contact details.
      contacts: checked ? { ...current.contacts, [role]: { ...current.contacts[role], ...getMemberContactCopySource(current, role) } } : current.contacts,
    }));
  };

  const updateReferral = (
    referralId: string,
    field: keyof ReferralTrackingInfo,
    value: string
  ) => {
    markDirty();
    setFormState((current) => ({
      ...current,
      referralTracking: current.referralTracking.map((referral) =>
        referral.id === referralId ? { ...referral, [field]: value } : referral
      ),
    }));
  };

  const addReferralTracking = () => {
    markDirty();
    setFormState((current) => ({
      ...current,
      referralTracking: [
        ...current.referralTracking,
        createEmptyReferralTracking(),
      ],
    }));
  };

  // Save on the Affinity Program Partner card first opens the confirmation
  // modal (PROCEED/CANCEL) per the step 3 spec annotation.
  const handleStartAffinityEnrollmentSave = () => {
    if (isReadOnly) return;
    const program = affinityProgramOptions.find(
      (option) => option.id === affinityDraft.affinityProgramPartnerId
    );

    if (!program) {
      setAffinityEnrollmentError("Select an Affinity Program Partner.");
      return;
    }

    setAffinityEnrollmentError("");
    setAffinityConfirmOpen(true);
  };

  const handleConfirmAffinityEnrollment = () => {
    if (isReadOnly) return;
    const program = affinityProgramOptions.find(
      (option) => option.id === affinityDraft.affinityProgramPartnerId
    );

    setAffinityConfirmOpen(false);

    if (!program) {
      return;
    }

    markDirty();
    setFormState((current) => ({
      ...current,
      affinityProgramEnrollments: [
        ...current.affinityProgramEnrollments,
        {
          id: createLocalId("affinity"),
          affinityProgramPartnerName: program.companyName,
          ...affinityDraft,
          status: affinityDraft.status || "Active",
        },
      ],
    }));
    setAffinityDraft(createEmptyAffinityDraft());
  };

  const handleUpdateEditingAffinityEnrollment = (
    field: keyof AffinityProgramEnrollment,
    value: string
  ) => {
    if (isReadOnly) return;
    setEditingAffinityEnrollment((current) =>
      current ? { ...current, [field]: value } : current
    );
  };

  const handleSaveEditingAffinityEnrollment = () => {
    if (!editingAffinityEnrollment || isReadOnly) {
      return;
    }

    markDirty();
    setFormState((current) => ({
      ...current,
      affinityProgramEnrollments: current.affinityProgramEnrollments.map(
        (enrollment) =>
          enrollment.id === editingAffinityEnrollment.id
            ? editingAffinityEnrollment
            : enrollment
      ),
    }));
    setEditingAffinityEnrollment(null);
  };

  const handleLogoUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (!["image/png", "image/jpeg"].includes(file.type)) {
      setLogoError("Logo must be a PNG or JPG file.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setLogoError("Logo must be 5MB or smaller.");
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      setLogoError("");
      updateSection("logo", {
        fileName: file.name,
        previewUrl: reader.result as string,
      });
    };

    reader.readAsDataURL(file);
  };

  const handleLogoDelete = () => {
    setLogoError("");
    updateSection("logo", { fileName: "", previewUrl: "" });
  };

  const discardOpenDrafts = () => {
    setFormState(savedBaselineRef.current);
    setContactAccessDrafts({});
    staffNoteDraftRef.current = "";
    setStaffNoteDraft("");
    setStaffNoteError("");
    setAffinityDraft(createEmptyAffinityDraft());
    setEditingAffinityEnrollment(null);
    setAffinityConfirmOpen(false);
    setAffinityEnrollmentError("");
    setAdditionalContactDraftDirty(false);
    setContactSameAsNudge(null);
    setAdditionalContactResetKey((value) => value + 1);
    membershipAdjustmentOperationRef.current = null;
    setMembershipAdjustmentDraft(null);
    setMembershipAdjustmentError("");
    setMembershipAdjustmentErrors({});
    setSectionErrors({});
    openDraftsRef.current = false;
  };

  const handleNextStep = () => goToStep(activeStep + 1);

  const applyActionResult = async (
    nextDetail: CmpMemberDetailFieldsFragment | null | undefined,
    successTitle: string,
    successBody?: React.ReactNode,
    successIconColor?: string,
  ) => {
    // The action may have saved, so staff check the status before a second try.
    if (!nextDetail) throw new UserFacingError(buildStaffCheckFirstFallback("confirm the change", "Refresh the page and check this member's status"));
    if (nextDetail) {
      savedMemberRevisionRef.current = nextDetail.member.updatedAtUtcMilli ?? savedMemberRevisionRef.current;
      const nextFormState = mapDetailToFormState(nextDetail, {
        configuredInsurancePayerName: chamberGroupInsuranceCompanyName,
        insuranceOptionAvailable: chamberGroupInsuranceOptionAvailable,
      });

      setFormStateWithBaseline({
        current: nextFormState,
        savedBaseline: nextFormState,
      });
      setCurrentStatus(statusLabels[nextDetail.lifecycleStatus]);
      membershipAdjustmentOperationRef.current = null;
      setMembershipAdjustmentDraft(null);
      setMembershipAdjustmentError("");
      setMembershipAdjustmentErrors({});
    }

    // The mutation response is the commit boundary. Close the confirmation and
    // latch success before the independent detail refresh so a network failure
    // cannot expose Proceed again or reclassify a completed action as failed.
    setConfirmationModal(null);

    setSuccessDialog({
      title: successTitle,
      tone: "green",
      iconColor: successIconColor,
      body: successBody,
    });
    const refreshWarning = await refreshAfterCommittedMemberAction(async () => {
      const result = await refetchDetail();
      if (!isCurrentMemberReadback(nextDetail, result.data?.getCmpMemberDetail)) throw new Error("The latest saved member could not be confirmed.");
      return result;
    });

    if (refreshWarning) {
      setSuccessDialog((current) =>
        current
          ? {
              ...current,
              refreshWarning,
              secondaryActionLabel: "Refresh Member",
              onSecondaryAction: () => {
                void handleRetryConfirmedDeliveryDetailRefresh(
                  committedMemberActionRefreshWarning
                );
              },
            }
          : current
      );
    }
  };

  const handleBoardApproval = async (confirmedLoginEmail?: string) => {
    const memberIdForApproval = memberIdForInput || member?.id || "";

    if (
      !chamberId ||
      !memberIdForApproval ||
      boardApprovalInFlightRef.current
    ) {
      return;
    }

    boardApprovalInFlightRef.current = true;

    try {
      if (isWebsiteOriginMember) {
        const savedLoginEmail = normalizeMemberLoginEmail(
          confirmedLoginEmail || memberAdminEmail
        );
        const approvalLoginEmail = getWebsiteApprovalLoginEmail({
          savedLoginEmail,
          primaryContactEmail: member?.primaryContactEmail,
        });

        if (!approvalLoginEmail) {
          throw new UserFacingError(
            "This application has no Primary Contact email. Go to Step 2 Member Contacts, enter the Primary Contact email, click Save Changes, then approve again."
          );
        }

        const availabilityDecision =
          await checkMemberDashboardLoginAvailability({
            loginEmail: approvalLoginEmail,
            memberId: memberIdForApproval,
          });

        if (
          currentCredentialMemberIdRef.current !== memberIdForApproval ||
          currentMemberAdminLoginEmailRef.current !== savedLoginEmail
        ) {
          throw new UserFacingError(memberLoginAvailabilityStaleError);
        }

        if (availabilityDecision.status === "collision") {
          setPendingMemberLoginCollision({
            action: "approveWebsiteApplication",
            memberId: memberIdForApproval,
            currentLoginEmail: savedLoginEmail,
            actionLoginEmail: savedLoginEmail,
            checkedLoginEmail: availabilityDecision.checkedLoginEmail,
            suggestedLoginEmail: availabilityDecision.suggestedLoginEmail,
          });
          return;
        }

        if (availabilityDecision.status === "stale") {
          throw new UserFacingError(memberLoginAvailabilityStaleError);
        }

        if (availabilityDecision.status === "invalid") {
          throw new UserFacingError(availabilityDecision.message);
        }
      }

      const result = await approveCmpMemberBoard({
        variables: {
          input: {
            chamberId,
            memberId: memberIdForApproval,
          },
        },
      });

      await applyActionResult(
        result.data?.approveCmpMemberBoard.detail,
        "", <>{member?.name} has been approved by the {getCmpBoardWording(noun).approvalByAndOr}!</>
      );
    } catch (error) {
      setMemberActionError(getUserFacingErrorText(
        error,
        buildStaffErrorFallback(`approve ${member?.name || "this member"}`)
      ));
    } finally {
      boardApprovalInFlightRef.current = false;
    }
  };

  const handleOpenMemberManualPayment = () => {
    const currentInvoice = detail?.currentInvoice;

    if (!currentInvoice || !statusActions?.canRecordManualPayment) {
      setStatusMessage({
        severity: "error",
        message: "You cannot record a payment for this invoice right now. Refresh the page, then check the invoice in Invoices & Payments > Manage all Invoices & Payments.",
      });
      return;
    }

    const isThirdPartyPayment =
      currentInvoice.paymentChoice.type === CmpPaymentChoiceTypeEnum.ThirdParty;
    const firstManualPaymentMethod = enabledManualPaymentMethods[0];

    if (!isThirdPartyPayment && !firstManualPaymentMethod) {
      setStatusMessage({
        severity: "error",
        message:
          `To record this payment, first turn on a payment method such as Company Cheque or E-Transfer in ${noun.Singular} Set Up > Global Settings > Payment Options.`,
      });
      return;
    }

    setManualPaymentError("");
    setManualPaymentDraft({
      manualPaymentMethodId: isThirdPartyPayment
        ? ""
        : firstManualPaymentMethod.id,
      receivedDate: getMemberManualPaymentLatestDate({
        timeZone: chamberSettings?.setup.timeZone,
      }),
      reference: "",
    });
    setManualPaymentDialogOpen(true);
  };

  const handleRecordMemberManualPayment = async () => {
    const currentInvoice = detail?.currentInvoice;

    if (
      !currentInvoice ||
      !memberIdForInput ||
      !statusActions?.canRecordManualPayment
    ) {
      setManualPaymentError(
        "You cannot record this payment now. Close this window and refresh the page."
      );
      return;
    }

    const isThirdPartyPayment =
      currentInvoice.paymentChoice.type === CmpPaymentChoiceTypeEnum.ThirdParty;
    if (!isThirdPartyPayment && !manualPaymentDraft.manualPaymentMethodId) {
      setManualPaymentError("Select a payment method.");
      return;
    }

    const receivedAtUtcMilli = getUtcCalendarDateStartUtcMilli(
      manualPaymentDraft.receivedDate
    );
    const receivedDateError = getMemberManualPaymentDateError(
      manualPaymentDraft.receivedDate,
      getMemberManualPaymentLatestDate({
        timeZone: chamberSettings?.setup.timeZone,
      }),
    );
    if (receivedDateError || !Number.isFinite(receivedAtUtcMilli)) {
      setManualPaymentError(
        receivedDateError || memberManualPaymentDateErrorMessage,
      );
      return;
    }

    const amountInCents = getPendingMemberPaymentAmountInCents({
      amountDueInCents: currentInvoice.amountDueInCents,
      nextInstallmentAmountInCents:
        currentInvoice.paymentPlan?.nextInstallmentAmountInCents,
    });
    if (!Number.isSafeInteger(amountInCents) || amountInCents <= 0) {
      setManualPaymentError(
        "We could not work out the amount owed on this invoice. Check the invoice in Invoices & Payments > Manage all Invoices & Payments, then try again."
      );
      return;
    }

    setManualPaymentError("");
    try {
      const result = await runCmpMemberManualPaymentOperation({
        chamberId,
        execute: (input) =>
          recordCmpMemberManualPayment({
            variables: { input },
          }),
        input: {
          chamberId,
          memberId: memberIdForInput,
          invoiceId: currentInvoice.id,
          amountInCents,
          manualPaymentMethodId: isThirdPartyPayment
            ? undefined
            : manualPaymentDraft.manualPaymentMethodId,
          receivedAtUtcMilli,
          reference: manualPaymentDraft.reference.trim() || undefined,
          isCmpMemberModuleTestData: shouldMarkCmpModuleTestData,
        },
        invoiceId: currentInvoice.id,
        isAuthoritativelyCompleted: (mutationResult) =>
          mutationResult.data?.recordCmpMemberManualPayment.actionSuccessful ===
          true,
      });
      const nextDetail = result.data?.recordCmpMemberManualPayment.detail;

      if (!nextDetail) {
        throw new UserFacingError(
          "We could not confirm that the payment was recorded. Refresh the page and check the invoice before you try again."
        );
      }

      setManualPaymentDialogOpen(false);
      await applyActionResult(
        nextDetail,
        "",
        "The payment was recorded, and the member status is updated."
      );
    } catch (error) {
      setManualPaymentError(
        getUserFacingErrorText(
          error,
          buildStaffErrorFallback(
            "record the payment",
            "Refresh the page and check the invoice first."
          )
        )
      );
    }
  };

  // MEM-UPDATE-006: (re)send the membership invoice to the member while their
  // payment is outstanding. Pass an explicit invoiceId to (re)send a specific
  // invoice instead — e.g. the separate add-on benefits invoice (BILL-003).
  const handleRetryConfirmedDeliveryDetailRefresh = async (
    refreshWarning = memberDeliveryRefreshWarning
  ) => {
    setSuccessDialog((current) =>
      current ? { ...current, actionLoading: true } : current
    );

    try {
      await refetchDetail();
      setSuccessDialog((current) =>
        current
          ? {
              ...current,
              actionLoading: false,
              refreshWarning: undefined,
              secondaryActionLabel: undefined,
              onSecondaryAction: undefined,
            }
          : current
      );
    } catch {
      setSuccessDialog((current) =>
        current
          ? {
              ...current,
              actionLoading: false,
              refreshWarning,
            }
          : current
      );
    }
  };

  const handleSendInvoice = async (
    invoiceId?: string,
    isResend = !invoiceId && paymentRequestWasSent
  ) => {
    if (!detail || !chamberId) {
      return;
    }

    const targetInvoiceId = invoiceId || detail.currentInvoice?.id;

    if (!targetInvoiceId) {
      setStatusMessage({
        severity: "error",
        message: "No unpaid invoice is available to send to this member.",
      });
      return;
    }

    const paymentRequestLabel = invoiceId
      ? "Add-on invoice payment request"
      : "Membership invoice payment request";

    try {
      const actionResult = await runConfirmedMemberDelivery({
        deliver: async () => {
          if (isResend) {
            const result = await resendCmpInvoicePaymentRequest({
              variables: {
                input: {
                  chamberId,
                  invoiceId: targetInvoiceId,
                },
              },
            });

            return result.data?.resendCmpInvoicePaymentRequest;
          }

          const result = await sendCmpInvoicePaymentRequest({
            variables: {
              input: {
                chamberId,
                invoiceId: targetInvoiceId,
              },
            },
          });

          return result.data?.sendCmpInvoicePaymentRequest;
        },
        wasDeliveryConfirmed: (paymentRequestResult) =>
          wasOutboundEmailDeliveryConfirmed(
            paymentRequestResult?.actionCompletedSuccessfully,
            paymentRequestResult?.outboundEmail
          ),
        getDeliveryFailure: (paymentRequestResult) =>
          new UserFacingError(
            getOutboundEmailFailureMessage({
              email: paymentRequestResult?.outboundEmail,
              label: paymentRequestLabel,
            })
          ),
        onDeliveryConfirmed: (paymentRequestResult) => {
          const outboundEmail = paymentRequestResult?.outboundEmail;

          if (!outboundEmail) {
            return;
          }

          const successMessage = getOutboundEmailSuccessMessage({
            email: outboundEmail,
            label: paymentRequestLabel,
            verb: isResend ? "resent" : "sent",
          });

          const companyName = member?.name || formState.directory.companyName || "this member";
          // Design copy for a membership invoice send. A test-inbox override
          // keeps the delivery helper's message so staff see the real recipient.
          setSuccessDialog({
            title: "",
            tone: "green",
            body: !invoiceId && isResend ? `The membership invoice for ${companyName} has been resent to the member’s billing contact.`
              : !invoiceId && !outboundEmail.recipientOverrideUsed ? <>Invoice emailed successfully to <strong>{companyName}</strong>’s billing contact!</>
              : successMessage,
          });
        },
        refresh: refetchDetail,
      });

      if (actionResult.refreshWarning) {
        setSuccessDialog((current) =>
          current
            ? {
                ...current,
                refreshWarning: actionResult.refreshWarning,
                secondaryActionLabel: "Refresh Member",
                onSecondaryAction: () => {
                  void handleRetryConfirmedDeliveryDetailRefresh();
                },
              }
            : current
        );
      }
    } catch (error) {
      setStatusMessage({
        severity: "error",
        message: getUserFacingErrorText(
          error,
          buildStaffErrorFallback(
            `email the invoice to ${member?.name || "this member"}`
          )
        ),
      });
    }
  };

  const confirmAction = async () => {
    if (!confirmationModal || !chamberId) {
      return;
    }
    setMemberActionError("");

    if (confirmationModal === "approveBoard") {
      await handleBoardApproval();
      return;
    }
    if (confirmationModal === "resetPassword") {
      if (currentStatus !== "Active" && currentStatus !== "Pending") return;
      if (hasUnsavedAdminLoginEmailChange) return;
      await executeSendCredentialsEmail({ loginEmail: memberAdminEmail, sendingMode: "resetPassword" });
      return;
    }

    if (!memberIdForInput) {
      return;
    }
    // Activate Member for a Pending member needs no reason; suspend,
    // unsuspend and delete do (CMP Dashboard (19) L15360).
    const statusReasonKind = getMemberStatusReasonKind(confirmationModal);
    const reason = statusReason.trim();
    if (statusReasonKind && !reason) {
      setStatusReasonError(true);
      return;
    }

    try {
      if (confirmationModal === "suspend") {
        const result = await suspendCmpMember({
          variables: {
            input: {
              chamberId,
              memberId: memberIdForInput,
              note: reason,
            },
          },
        });

        await applyActionResult(
          result.data?.suspendCmpMember.detail,
          "", <>{member?.name} has been suspended.</>
        );
      }

      if (confirmationModal === "cancelRenewal" || confirmationModal === "uncancelRenewal") {
        const company = member?.name || "this member";
        const variables = { input: { chamberId, memberId: memberIdForInput, note: reason } };
        const nextDetail = confirmationModal === "cancelRenewal"
          ? (await cancelCmpMemberRenewal({ variables })).data?.cancelCmpMemberRenewal.detail
          : (await uncancelCmpMemberRenewal({ variables })).data?.uncancelCmpMemberRenewal.detail;
        // CD:15810 success texts.
        await applyActionResult(
          nextDetail,
          "",
          confirmationModal === "cancelRenewal"
            ? <>The membership for {company} will be cancelled automatically on the renewal date. All member privileges, directory listing and Member Dashboard access will be disabled. No Renewal invoice will be Sent.</>
            : <>The membership renewal for {company} has been restored. {company} is back to Active Member status{memberNoCharge ? "." : " and the renewal invoice will be sent automatically."}</>,
          // DCL "Member status pill": both cancel pop-ups use the brand yellow icon.
          "#E1A731",
        );
      }

      if (confirmationModal === "activate") {
        const result = await activateCmpMember({
          variables: {
            input: {
              chamberId,
              memberId: memberIdForInput,
            },
          },
        });

        await applyActionResult(
          result.data?.activateCmpMember.detail,
          "", "Member Activated Successfully!"
        );
      }

      if (confirmationModal === "unsuspend") {
        const result = await activateCmpMember({
          variables: {
            input: {
              chamberId,
              memberId: memberIdForInput,
              note: reason,
            },
          },
        });

        await applyActionResult(
          result.data?.activateCmpMember.detail,
          "", <>{member?.name} has been unsuspended and is now an Active Member!</>
        );
      }

      if (confirmationModal === "delete") {
        const result = await archiveCmpMember({
          variables: {
            input: {
              chamberId,
              memberId: memberIdForInput,
              archivedAtUtcMilli: Date.now(),
              archiveReason: reason,
            },
          },
        });
        const nextDetail = result.data?.archiveCmpMember.detail;
        const paymentSurfaceFeedback =
          getMemberPastTransitionPaymentSurfaceFeedback(nextDetail?.invoices);

        await applyActionResult(
          nextDetail,
          "",
          <><p>{statusModel
            ? `${member?.name} has been deleted. The member shows as Deleted, and member data can be recalled for 365 days when creating a new member. After 365 days the member moves to Past Member.`
            : `${member?.name} has been deleted. All information is stored as a Past Member.`}</p>{paymentSurfaceFeedback && <MemberPastTransitionPaymentSurfaceFeedback feedback={paymentSurfaceFeedback} className="mt-3 text-left" />}</>
        );
      }

      if (confirmationModal === "restartApplication") {
        const result = await startApplicationFromExisting({
          variables: {
            input: {
              chamberId,
              memberId: memberIdForInput,
              isCmpMemberModuleTestData: shouldMarkCmpModuleTestData,
            },
          },
        });
        const restartApplication =
          result.data?.startCmpMemberApplicationFromExisting;
        const prefilledMember = restartApplication?.member;

        if (!prefilledMember) {
          throw new Error("No prefilled member response was returned.");
        }

        navigate(RoutePaths.chamberAdmin_members_create, {
          state: {
            cmpPrefillMember: prefilledMember,
            cmpPrefillSourceMemberId: memberIdForInput,
            cmpPrefillThirdPartyBillingSnapshot:
              restartApplication?.thirdPartyBillingSnapshot ?? null,
          },
        });
      }

      setConfirmationModal(null);
    } catch (error) {
      const memberLabel = member?.name || "this member";
      setMemberActionError(getUserFacingErrorText(
        error,
        buildStaffErrorFallback(
          confirmationModal === "suspend" ? `suspend ${memberLabel}`
            : confirmationModal === "activate" ? `activate ${memberLabel}`
              : confirmationModal === "unsuspend" ? `unsuspend ${memberLabel}`
                : confirmationModal === "delete" ? `delete ${memberLabel}`
                  : confirmationModal === "restartApplication" ? `start a new application for ${memberLabel}`
                  : confirmationModal === "cancelRenewal" ? `cancel the renewal for ${memberLabel}`
                  : confirmationModal === "uncancelRenewal" ? `uncancel the renewal for ${memberLabel}`
                    : `finish this action for ${memberLabel}`,
          "Refresh the page to check the member status first."
        )
      ));
    }
  };

  const applyMembershipAdjustmentResult = (
    nextDetail: CmpMemberDetailFieldsFragment
  ) => {
    const nextMembership = mapDetailToFormState(nextDetail, {
      configuredInsurancePayerName: chamberGroupInsuranceCompanyName,
      insuranceOptionAvailable: chamberGroupInsuranceOptionAvailable,
    }).membership;

    setFormStateWithBaseline((currentState) => ({
      current: {
        ...currentState.current,
        membership: {
          ...nextMembership,
          // These fields are outside the dedicated membership-adjustment write.
          // Keep any newer unsaved profile edit in the editable form.
          emailCommunicationConsentConfirmed:
            currentState.current.membership
              .emailCommunicationConsentConfirmed,
          selectedAdditionalCostIds: [
            ...currentState.current.membership.selectedAdditionalCostIds,
          ],
        },
      },
      savedBaseline: {
        ...currentState.savedBaseline,
        membership: {
          ...nextMembership,
          // The dedicated adjustment did not commit these two fields. Their
          // saved baseline must therefore remain at the previous saved values.
          emailCommunicationConsentConfirmed:
            currentState.savedBaseline.membership
              .emailCommunicationConsentConfirmed,
          selectedAdditionalCostIds: [
            ...currentState.savedBaseline.membership.selectedAdditionalCostIds,
          ],
        },
      },
    }));
    setCurrentStatus(statusLabels[nextDetail.lifecycleStatus]);
    membershipAdjustmentOperationRef.current = null;
    setMembershipAdjustmentDraft(null);
    setMembershipAdjustmentError("");
    setMembershipAdjustmentErrors({});
  };

  const prepareMembershipAdjustment = (requireChanges = true) => {
    const currentDraft = membershipAdjustmentDraft || (activeStep === 5 ? membershipBillingValue : null);
    if (!detail || !chamberId || !currentDraft) {
      return;
    }

    const nextErrors: MemberBillingErrors = {};
    if (activeStep === 5 && (!currentDraft.fullTimeEmployees.trim() || !currentDraft.partTimeEmployees.trim())) {
      nextErrors.employeeCount = "Enter the full-time and part-time employee counts.";
    }
    if (activeStep === 5 && !formState.membership.emailCommunicationConsentConfirmed) {
      nextErrors.emailCommunicationConsentConfirmed = "Confirm the member's email communication consent.";
    }

    if (!currentDraft.membershipTypeId) {
      nextErrors.membershipTypeId = "Select a membership type.";
    } else if (
      !membershipModels.some(
        (model) => model.id === currentDraft.membershipTypeId
      )
    ) {
      nextErrors.membershipTypeId =
        "This membership type is no longer offered. Select another membership type.";
    }

    const updatePaymentScheduleMutationGate =
      gateUpdateMemberPaymentScheduleMutation({
        enabledPaymentSchedules,
        storedPaymentSchedule: currentDraft.paymentSchedule,
      });

    if (updatePaymentScheduleMutationGate.blocked) {
      nextErrors.paymentSchedule = updatePaymentScheduleMutationGate.error;
    }

    if (
      selectedMembershipModel?.pricingModel ===
        ChamberMemberProMembershipPricingModelEnum.EmployeeRange &&
      !getNumber(currentDraft.fullTimeEmployees) &&
      !getNumber(currentDraft.partTimeEmployees)
    ) {
      nextErrors.employeeCount =
        "This membership type is priced by the number of employees. Enter the number of full-time or part-time employees.";
    }

    if (
      membershipDiscountEnabled &&
      currentDraft.membershipDiscount.enabled
    ) {
      if (!currentDraft.membershipDiscount.code.trim()) {
        nextErrors.membershipDiscountCode = "Enter a discount code.";
      }

      if (!currentDraft.membershipDiscount.description.trim()) {
        nextErrors.membershipDiscountDescription =
          "Enter a discount description.";
      }

      if (!getNumber(currentDraft.membershipDiscount.amount)) {
        nextErrors.membershipDiscountAmount = "Enter a discount amount.";
      }
    }

    if (
      !chamberSettings?.paymentsAndTaxes.membershipDiscount.enabled &&
      currentDraft.membershipDiscount.enabled
    ) {
      nextErrors.membershipDiscountAmount =
        `Clear the saved discount because membership discounts are disabled in ${noun.Singular} Set Up.`;
    }

    if (
      !chamberSettings?.membership.allowAutoRenewal &&
      currentDraft.autoRenew
    ) {
      nextErrors.autoRenew =
        `Clear auto-renew because automatic renewal is disabled in ${noun.Singular} Set Up.`;
    }

    if (
      currentDraft.billingResponsibility ===
        "affiliatedPrimaryCompany" &&
      !formState.association.primaryMemberCompanyId
    ) {
      nextErrors.billingResponsibility =
        memberBillingPrimaryCompanyNotSelectedMessage;
    }

    if (
      currentDraft.billingResponsibility ===
        "chamberGroupInsurance" &&
      (!chamberSettings?.paymentsAndTaxes.chamberGroupInsurancePayment.enabled ||
        !chamberGroupInsuranceCompanyName)
    ) {
      nextErrors.billingResponsibility =
        getMemberBillingGroupInsuranceNotSetUpMessage(`${noun.Singular} Set Up`);
    }

    if (
      currentDraft.billingResponsibility === "otherThirdParty" &&
      !currentDraft.thirdPartyPayerName.trim()
    ) {
      nextErrors.billingResponsibility =
        memberBillingThirdPartyPayerMissingMessage;
    }

    if (
      currentDraft.billingResponsibility === "affiliatedPrimaryCompany" &&
      formState.association.primaryMemberCompanyId &&
      Object.keys(
        getMemberThirdPartyBillingSnapshotErrors(
          currentDraft.thirdPartyBillingSnapshot
        )
      ).length > 0
    ) {
      // Update Member has no payer form for this option (design (13)), so
      // name the record that needs the missing billing details.
      nextErrors.billingResponsibility =
        `${affiliatedPrimaryCompanyName || "The Primary Member company"} needs a billing email and billing address. Add them to its Accounts Payable contact, then choose this option again.`;
    }

    if (currentDraft.billingResponsibility === "otherThirdParty") {
      Object.entries(
        getMemberThirdPartyBillingSnapshotErrors(
          currentDraft.thirdPartyBillingSnapshot
        )
      ).forEach(([field, message]) => {
        if (message) {
          const errorKey =
            `thirdPartyBillingSnapshot.${field}` as keyof MemberBillingErrors;
          nextErrors[errorKey] = message;
        }
      });
    }

    if (
      currentDraft.billingResponsibility === "memberPays" &&
      !allowedMembershipPaymentMethodOptions.some(
        (option) =>
          !option.disabled &&
          option.value === currentDraft.paymentMethod
      )
    ) {
      nextErrors.paymentMethod =
        "Select a Membership Payment Method.";
    }

    setMembershipAdjustmentErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      setMembershipAdjustmentError(
        "Fix the highlighted membership details before updating."
      );
      return;
    }

    // Keep a final discriminated guard at the GraphQL boundary even though the
    // same error is already rendered above.
    if (updatePaymentScheduleMutationGate.blocked) {
      return;
    }
    const resolvedPaymentSchedule =
      updatePaymentScheduleMutationGate.paymentSchedule;

    if (
      requireChanges && !membershipAdjustmentHasChanges &&
      !isPendingJohnsonGroupSelection
    ) {
      setMembershipAdjustmentError(
        "Make at least one membership change before updating."
      );
      return;
    }

    const normalizedMembershipAdjustmentDraft: MembershipFormState = {
      ...currentDraft,
      paymentSchedule: resolvedPaymentSchedule,
    };
    const previousMembershipLabel =
      previousMembershipModel?.name ||
      detail.member.cmpExtension?.membership?.membershipTypeName ||
      "Current membership";
    const updatedMembershipLabel =
      selectedMembershipModel?.name || "Updated membership";
    const expectedMemberUpdatedAtUtcMilli =
      detail.member.updatedAtUtcMilli || detail.member.createdAtUtcMilli || 0;
    const expectedCurrentInvoiceId =
      detail.member.cmpExtension?.latestInvoiceId || undefined;
    const operationRequestFingerprint = JSON.stringify({
      membership: getMembershipAdjustmentFingerprint(
        normalizedMembershipAdjustmentDraft
      ),
      expectedMemberUpdatedAtUtcMilli,
      expectedCurrentInvoiceId: expectedCurrentInvoiceId || null,
    });

    if (
      membershipAdjustmentOperationRef.current?.requestFingerprint !==
      operationRequestFingerprint
    ) {
      membershipAdjustmentOperationRef.current = {
        requestFingerprint: operationRequestFingerprint,
        operationId: createLocalId("membership-change"),
      };
    }

    const operationId = membershipAdjustmentOperationRef.current.operationId;
    const memberId = memberIdForInput || detail.member.id;
    const adjustmentInput: UpdateMemberMembershipAdjustmentInput = {
      chamberId,
      memberId,
      operationId,
      expectedMemberUpdatedAtUtcMilli,
      expectedCurrentInvoiceId,
      membership: {
        membershipTypeId: normalizedMembershipAdjustmentDraft.membershipTypeId,
        fullTimeEmployees:
          normalizedMembershipAdjustmentDraft.fullTimeEmployees,
        partTimeEmployees:
          normalizedMembershipAdjustmentDraft.partTimeEmployees,
        paymentSchedule: resolvedPaymentSchedule,
        billingResponsibility:
          normalizedMembershipAdjustmentDraft.billingResponsibility,
        thirdPartyPayerName:
          normalizedMembershipAdjustmentDraft.thirdPartyPayerName,
        thirdPartyPaymentNotes:
          normalizedMembershipAdjustmentDraft.thirdPartyPaymentNotes,
        thirdPartyBillingSnapshot: {
          ...normalizedMembershipAdjustmentDraft.thirdPartyBillingSnapshot,
        },
        paymentMethod: normalizedMembershipAdjustmentDraft.paymentMethod,
        membershipDiscount: {
          ...normalizedMembershipAdjustmentDraft.membershipDiscount,
        },
        autoRenew: normalizedMembershipAdjustmentDraft.autoRenew,
      },
    };
    const isJohnsonGroupChange =
      adjustmentInput.membership.billingResponsibility ===
      "chamberGroupInsurance";

    return { adjustmentInput, previousMembershipLabel, updatedMembershipLabel, isJohnsonGroupChange, memberId };
  };

  const buildMembershipMutationInput = (
    adjustmentInput: UpdateMemberMembershipAdjustmentInput,
  ): ChangeCmpMemberMembershipInput => {
            const membership = adjustmentInput.membership;
            const paymentResponsibility = getPaymentResponsibility(
              membership.billingResponsibility
            );
            const effectivePaymentMethod = getEffectiveUpdatePaymentMethod({
              creditCardPaymentsReady,
              manualPaymentMethods:
                chamberSettings?.paymentsAndTaxes.manualPaymentMethods || [],
              paymentMethod: membership.paymentMethod,
            });

            if (
              membership.billingResponsibility === "memberPays" &&
              !effectivePaymentMethod
            ) {
              throw new UserFacingError(
                "Select a Membership Payment Method."
              );
            }

            const paymentChoice = getPaymentChoiceType(
              membership.billingResponsibility,
              effectivePaymentMethod
            );
            const isThirdPartyPaid =
              paymentResponsibility ===
              ChamberMemberProMembershipModelPaymentResponsibilityEnum.ThirdPartyPays;
            const affiliatedPrimaryCompanyName =
              primaryMemberCompanySelectOptions.find(
                (option) =>
                  option.value === formState.association.primaryMemberCompanyId
              )?.label;
            const thirdPartyBilling = getMemberThirdPartyBillingFields({
              affiliatedPrimaryCompanyName,
              billingResponsibility: membership.billingResponsibility,
              configuredInsurancePayerName:
                chamberGroupInsuranceCompanyName,
              savedThirdPartyPayerName: membership.thirdPartyPayerName,
              savedThirdPartyPaymentNotes:
                membership.thirdPartyPaymentNotes,
            });
    return {
                  chamberId: adjustmentInput.chamberId,
                  memberId: adjustmentInput.memberId,
                  operationId: adjustmentInput.operationId,
                  expectedMemberUpdatedAtUtcMilli:
                    adjustmentInput.expectedMemberUpdatedAtUtcMilli,
                  expectedCurrentInvoiceId:
                    adjustmentInput.expectedCurrentInvoiceId,
                  membership: {
                    membershipTypeId: membership.membershipTypeId,
                    paymentSchedule: membership.paymentSchedule,
                    fullTimeEmployees: getNumber(
                      membership.fullTimeEmployees
                    ),
                    partTimeEmployees: getNumber(
                      membership.partTimeEmployees
                    ),
                    membershipDiscount: {
                      enabled: membership.membershipDiscount.enabled,
                      code: toOptionalString(
                        membership.membershipDiscount.code
                      ),
                      description: toOptionalString(
                        membership.membershipDiscount.description
                      ),
                      amountInCents: Math.round(
                        getNumber(membership.membershipDiscount.amount) * 100
                      ),
                    },
                    paymentResponsibility,
                    isFreeLifetime:
                      membership.billingResponsibility === "freeLifetime",
                    ...(membership.billingResponsibility ===
                      "otherThirdParty" ||
                    membership.billingResponsibility ===
                      "affiliatedPrimaryCompany"
                      ? {
                          thirdPartyBillingSnapshot: {
                            customerName:
                              membership.thirdPartyBillingSnapshot.customerName.trim(),
                            contactName:
                              membership.thirdPartyBillingSnapshot.contactName.trim() ||
                              undefined,
                            contactEmail:
                              membership.thirdPartyBillingSnapshot.contactEmail.trim(),
                            contactPhone:
                              membership.thirdPartyBillingSnapshot.contactPhone.trim() ||
                              undefined,
                            addressLine1:
                              membership.thirdPartyBillingSnapshot.addressLine1.trim(),
                            addressLine2:
                              membership.thirdPartyBillingSnapshot.addressLine2.trim() ||
                              undefined,
                            city: membership.thirdPartyBillingSnapshot.city.trim(),
                            province:
                              membership.thirdPartyBillingSnapshot.province.trim(),
                            postalCode:
                              membership.thirdPartyBillingSnapshot.postalCode.trim(),
                            country:
                              membership.thirdPartyBillingSnapshot.country.trim(),
                          },
                        }
                      : {}),
                    thirdPartyPayerName: isThirdPartyPaid
                      ? toOptionalString(thirdPartyBilling.thirdPartyPayerName)
                      : null,
                    thirdPartyPaymentNotes: isThirdPartyPaid
                      ? toOptionalString(
                          thirdPartyBilling.thirdPartyPaymentNotes
                        )
                      : null,
                    paymentChoice: {
                      ...paymentChoice,
                      autoRenew: membership.autoRenew,
                      emailCommunicationConsentConfirmed:
                        formState.membership
                          .emailCommunicationConsentConfirmed,
                    },
                  },
                };
  };

  const executeSaveUpdate = async ({
    submittedFormState,
    confirmedLoginEmail,
    section,
    contactTarget,
  }: {
    submittedFormState: UpdateMemberFormState;
    confirmedLoginEmail?: string;
    section?: CmpMemberSectionEnum;
    contactTarget?: MemberContactSaveTarget;
  }): Promise<boolean> => {
    if (!detail || !chamberId || profileSaveInFlightRef.current) {
      return false;
    }

    // The navigation member id is consumed once from sessionStorage, so it can
    // be empty after a page refresh; fall back to the id of the member the
    // detail query loaded instead of silently skipping the save.
    const memberIdForUpdate = memberIdForInput || detail.member.id;
    const persistedAdminLoginEmail = currentMemberAdminLoginEmailRef.current;
    // B10 + Monday 13385035728 (Moeen, Oct 28 2026): the Administrator is
    // optional for every member. On the old company login the saved
    // Administrator email is the member's sign-in, so a blank email keeps
    // that sign-in email; only a new email changes it.
    const requestedAdminLoginEmail = normalizeMemberLoginEmail(
      section && section !== CmpMemberSectionEnum.Contacts
        ? persistedAdminLoginEmail
        : confirmedLoginEmail || submittedFormState.contacts.administrator.email
    ) || persistedAdminLoginEmail;

    const shouldChangeAdminLoginEmail =
      !confirmedLoginEmail &&
      requestedAdminLoginEmail !== persistedAdminLoginEmail;

    profileSaveInFlightRef.current = true;
    setProfileSaveInFlight(true);
    let profileSaved = false;
    let adminLoginEmailChanged = Boolean(confirmedLoginEmail);
    let resolvedAdminLoginEmail = requestedAdminLoginEmail;
    let detailForSave = detail;
    const revisionBeforeIdentityChange = savedMemberRevisionRef.current ?? detail.member.updatedAtUtcMilli ?? null;

    try {
      if (shouldChangeAdminLoginEmail) {
        const availabilityDecision =
          await checkMemberDashboardLoginAvailability({
            loginEmail: requestedAdminLoginEmail,
            memberId: memberIdForUpdate,
          });

        if (
          currentCredentialMemberIdRef.current !== memberIdForUpdate ||
          editableAdminLoginEmailRef.current !== requestedAdminLoginEmail
        ) {
          throw new UserFacingError(memberLoginAvailabilityStaleError);
        }

        if (availabilityDecision.status === "collision") {
          setPendingMemberLoginCollision({
            action: "save",
            memberId: memberIdForUpdate,
            currentLoginEmail: persistedAdminLoginEmail,
            actionLoginEmail: requestedAdminLoginEmail,
            checkedLoginEmail: availabilityDecision.checkedLoginEmail,
            suggestedLoginEmail: availabilityDecision.suggestedLoginEmail,
            submittedFormState,
            section,
            contactTarget,
          });
          return false;
        }

        if (availabilityDecision.status === "stale") {
          throw new UserFacingError(memberLoginAvailabilityStaleError);
        }

        if (availabilityDecision.status === "invalid") {
          throw new UserFacingError(availabilityDecision.message);
        }

        const emailChangeResult =
          await chamberMemberChangeAdminConsoleLoginEmail({
            variables: {
              chamberMemberId: memberIdForUpdate,
              currentAdminConsoleLoginEmail: persistedAdminLoginEmail,
              newAdminConsoleLoginEmail: requestedAdminLoginEmail,
            },
          });
        const emailChange =
          emailChangeResult.data?.chamberMemberChangeAdminConsoleLoginEmail;
        const changeAvailabilityDecision =
          getMemberLoginChangeAvailabilityDecision({
            changeResult: emailChange,
            expectedLoginEmail: requestedAdminLoginEmail,
          });

        if (changeAvailabilityDecision.status === "collision") {
          setPendingMemberLoginCollision({
            action: "save",
            memberId: memberIdForUpdate,
            currentLoginEmail: persistedAdminLoginEmail,
            actionLoginEmail: requestedAdminLoginEmail,
            checkedLoginEmail: changeAvailabilityDecision.checkedLoginEmail,
            suggestedLoginEmail:
              changeAvailabilityDecision.suggestedLoginEmail,
            submittedFormState,
            section,
            contactTarget,
          });
          return false;
        }

        if (changeAvailabilityDecision.status === "stale") {
          throw new UserFacingError(memberLoginAvailabilityStaleError);
        }

        if (changeAvailabilityDecision.status === "invalid") {
          throw new UserFacingError(changeAvailabilityDecision.message);
        }

        resolvedAdminLoginEmail = getConfirmedMemberLoginEmail({
          changeResult: emailChange,
          expectedLoginEmail: requestedAdminLoginEmail,
        });
        adminLoginEmailChanged = true;
        recordConfirmedIdentity(resolvedAdminLoginEmail, requestedAdminLoginEmail, revisionBeforeIdentityChange);

        if (
          currentCredentialMemberIdRef.current !== memberIdForUpdate ||
          editableAdminLoginEmailRef.current !== requestedAdminLoginEmail
        ) {
          throw new UserFacingError(memberLoginAvailabilityStaleError);
        }
      }

      // Identity writes advance the member revision independently. Do not
      // submit a profile against the revision from before that commit.
      detailForSave = await confirmPendingIdentityReadback() || detailForSave;

      // A blank Administrator email keeps the sign-in email (see above); the
      // saved card then shows that email again from the readback.
      const profileFormState =
        submittedFormState.contacts.administrator.email ===
        resolvedAdminLoginEmail || !submittedFormState.contacts.administrator.email.trim()
          ? submittedFormState
          : {
              ...submittedFormState,
              contacts: {
                ...submittedFormState.contacts,
                administrator: {
                  ...submittedFormState.contacts.administrator,
                  email: resolvedAdminLoginEmail,
                },
              },
            };
      const memberInput = buildMemberInputFromForm({
        currentStatus,
        detail: detailForSave,
        formState: profileFormState,
      });

      if (section === CmpMemberSectionEnum.CustomContacts) {
        memberInput.customContacts = memberInput.customContacts?.map((contact) =>
          (!contactTarget || ("customContactId" in contactTarget && contactTarget.customContactId === contact.id)) &&
          isMemberSectionDirty(savedBaselineRef.current, submittedFormState, section, { customContactId: contact.id })
            ? normalizeCustomContact(contact) : contact);
      }

      // The dedicated mutation above owns any identity change. The profile
      // write receives only the server-confirmed current login email.
      memberInput.adminConsoleLoginEmail =
        resolvedAdminLoginEmail || undefined;

      const result = await updateCmpMember({
        variables: {
          input: {
            chamberId,
            memberId: memberIdForUpdate,
            data: memberInput,
            section,
            expectedUpdatedAtUtcMilli: savedMemberRevisionRef.current ?? detailForSave.member.updatedAtUtcMilli,
            administrativeFields: {
              dateJoinedUtcMilli: getUtcCalendarDateStartUtcMilli(
                profileFormState.administrative.dateJoined
              ),
              renewalDateUtcMilli: profileFormState.administrative.nextRenewalDate
                ? getUtcCalendarDateStartUtcMilli(
                    profileFormState.administrative.nextRenewalDate
                  )
                : null,
              externalMembershipStanding:
                profileFormState.administrative.externalMembershipStanding,
            },
          },
        },
      });
      let nextDetail = result.data?.updateCmpMember.detail;

      if (!result.data?.updateCmpMember.updateSuccessful || !nextDetail) {
        // The save may have reached the server, so staff check before a second try.
        throw new UserFacingError(buildStaffCheckFirstFallback("confirm that your changes were saved", "Refresh the page to check them"));
      }

      profileSaved = true;
      savedMemberRevisionRef.current = nextDetail.member.updatedAtUtcMilli ?? savedMemberRevisionRef.current;
      let detailRefreshFailed = false;

      try {
        const refreshedDetailResult = await refetchDetail();
        const refreshedDetail = refreshedDetailResult.data?.getCmpMemberDetail;

        if (isCurrentMemberReadback(nextDetail, refreshedDetail) && refreshedDetail) {
          nextDetail = refreshedDetail;

          if (
            getPersistedMemberLoginEmail(refreshedDetail.member) ===
            resolvedAdminLoginEmail
          ) {
            setConfirmedAdminLoginEmail(null);
          }
        } else {
          detailRefreshFailed = true;
        }
      } catch {
        detailRefreshFailed = true;
      }

      const nextFormState = mapDetailToFormState(nextDetail, {
        configuredInsurancePayerName: chamberGroupInsuranceCompanyName,
        insuranceOptionAvailable: chamberGroupInsuranceOptionAvailable,
      });

      if (adminLoginEmailChanged) {
        // The dedicated mutation is the persistence confirmation. Keep that
        // committed email visible even if the follow-up detail refresh failed.
        nextFormState.contacts.administrator.email =
          resolvedAdminLoginEmail;
      }

      savedMemberRevisionRef.current = nextDetail.member.updatedAtUtcMilli ?? null;
      setFormStateWithBaseline((currentState) =>
        applySavedMemberForm(currentState, profileFormState, nextFormState, section, contactTarget)
      );
      setCurrentStatus(statusLabels[nextDetail.lifecycleStatus]);
      membershipAdjustmentOperationRef.current = null;
      setMembershipAdjustmentDraft(null);
      setMembershipAdjustmentError("");
      setMembershipAdjustmentErrors({});
      setStatusMessage(detailRefreshFailed ? {
        severity: "warning",
        message: adminLoginEmailChanged
          ? "Member update saved and the Administrator dashboard login email changed, but the latest member details did not refresh automatically. Refresh this page before making another login-email change."
          : "Member update saved, but the latest member details did not refresh automatically. Refresh this page to load the saved member.",
      } : null);
      // A confirmed login-email change keeps its Send Credentials guidance in
      // the design success pop-up instead of an inline success banner.
      if (adminLoginEmailChanged && !detailRefreshFailed) setSuccessDialog({
        ...memberSaveSuccessDialog,
        note: "The Administrator dashboard login email changed. Use Send Credentials to deliver the new login details.",
      });
      const contactLoginNotes = result.data.updateCmpMember.contactLoginNotes || [];
      if (contactLoginNotes.length) {
        contactLoginSaveDialogRef.current = getContactLoginSaveDialog(contactLoginNotes);
        setSuccessDialog(contactLoginSaveDialogRef.current);
      }
      if (!section) scrollToTop();
      return true;
    } catch (error) {
      const message = getUserFacingErrorText(error, buildStaffErrorFallback("save the member"));
      if (section) setSectionErrors((current) => ({ ...current, [memberSectionErrorKey(section, contactTarget)]: message }));
      if (message.includes("This member changed after the page was loaded")) setSuccessDialog({ title: "", tone: "red", body: "Someone else changed this member after you opened it. Refresh the page to see their changes, then make your changes again." });
      setStatusMessage({
        severity: adminLoginEmailChanged && !profileSaved ? "warning" : "error",
        message:
          adminLoginEmailChanged && !profileSaved
            ? `The Administrator dashboard login email changed, but the member update was not saved. ${getUserFacingErrorText(
                error,
                "Review the current member details before you try again."
              )}`
            : message,
      });

      if (adminLoginEmailChanged && !profileSaved) {
        // The login mutation committed before the independent profile write.
        // Advance only the saved login baseline and keep all other edits dirty.
        // recordConfirmedIdentity already advanced the saved email and refs.
        // Keep its revision gate in place if the readback failed.
        if (pendingIdentityReadbackRef.current) {
          try { await confirmPendingIdentityReadback(); } catch { /* Keep the pending revision gate. */ }
        }
      }
      return false;
    } finally {
      profileSaveInFlightRef.current = false;
      setProfileSaveInFlight(false);
    }
  };

  const getMemberSectionFieldErrors = (section: CmpMemberSectionEnum, state: UpdateMemberFormState, contactTarget?: MemberContactSaveTarget): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (section === CmpMemberSectionEnum.MembershipTracking) {
      if (!state.administrative.dateJoined) errors["member-date-joined"] = selectFieldMessage("Date Joined");
      else if (state.administrative.dateJoined > new Date().toISOString().slice(0, 10)) errors["member-date-joined"] = "Date Joined cannot be in the future.";
    }
    if (section === CmpMemberSectionEnum.MemberDirectory) {
      Object.assign(errors, getMemberUrlFieldErrors(state.directory, savedBaselineRef.current.directory));
      if (!state.directory.companyName.trim()) errors["member-name"] = enterFieldMessage("Member Name");
      if (!state.directory.directoryCategoryIds.length || state.directory.directoryCategoryIds.length > 2) errors["member-directory-categories"] = "Select 1 to 2 directory categories.";
      if (state.directory.mainCompanyContactEmail.trim() && !hasEmailShape(state.directory.mainCompanyContactEmail.trim())) errors["main-contact-email"] = EMAIL_FORMAT_MESSAGE;
      Object.assign(errors, getMemberTollFreeFaxErrors({ tollFree: state.directory.tollFreePhone, fax: state.directory.faxNumber, ids: { tollFree: "member-toll-free-phone", fax: "member-fax-number" } }));
    }
    if (section === CmpMemberSectionEnum.SocialLinks) {
      Object.assign(errors, getMemberUrlFieldErrors(state.socialLinks, savedBaselineRef.current.socialLinks));
    }
    if (section === CmpMemberSectionEnum.BillingAddress) {
      const fields = [
        ["streetAddress", "billing-street-address", "Street Address"],
        ["city", "billing-city", "City"],
        ["provinceOrState", "billing-province", "Province/State"],
        ["postalOrZipCode", "billing-postal-code", "Postal/Zip Code"],
        ["country", "billing-country", "Country"],
      ] as const;
      for (const [field, id, label] of fields) {
        if (!state.billingAddress[field].trim()) errors[id] = field === "provinceOrState" || field === "country" ? selectFieldMessage(label) : enterFieldMessage(label);
      }
    }
    if (section === CmpMemberSectionEnum.Contacts) {
      for (const contactSection of contactSections) {
        if (contactTarget && ("customContactId" in contactTarget || contactTarget.role !== contactSection.role)) continue;
        if (!contactTarget && !isMemberSectionDirty(savedBaselineRef.current, state, section, { role: contactSection.role })) continue;
        // The design's billing-contact gate copy; other contacts keep the
        // standard required-field messages.
        const billingContact = contactSection.role === "accountsPayable";
        for (const field of contactSection.requiredFields) {
          if (!state.contacts[contactSection.role][field].trim()) errors[`${contactSection.role}-${field}`] = billingContact
            ? field === "name" ? `${contactSection.nameLabel} is required, or check the box above to add the primary contact.` : "Enter a valid contact email address."
            : enterFieldMessage(field === "name" ? contactSection.nameLabel : "Contact Email");
        }
        const email = state.contacts[contactSection.role].email.trim();
        if (email && !hasEmailShape(email)) errors[`${contactSection.role}-email`] = billingContact ? "Enter a valid contact email address." : EMAIL_FORMAT_MESSAGE;
      }
    }
    if (section === CmpMemberSectionEnum.CustomContacts) {
      Object.assign(errors, getChangedCustomContactErrors({
        contacts: state.customContacts,
        baseline: savedBaselineRef.current.customContacts,
        contactId: contactTarget && "customContactId" in contactTarget ? contactTarget.customContactId : undefined,
      }));
    }
    if (section === CmpMemberSectionEnum.MemberInsights) {
      if (!isValidEstablishedYear(state.companyDetails.yearEstablished)) errors["year-member-was-established"] = "Select the year the member was established. It cannot be a future year.";
      if (!state.insights.communicationPreferences.length) errors.communicationPreferences = "Select at least one communication preference before saving.";
    }
    if (section === CmpMemberSectionEnum.SeoTags) {
      if (state.directory.seoTags.length > 20) errors.seoTags = "You can add up to 20 tags. Remove some tags, then save.";
      if (state.directory.seoTags.some((tag) => tag.trim().length > 60)) errors.seoTags = "Each tag can have up to 60 characters. Shorten the long tag, then save.";
    }
    return errors;
  };
  const memberFieldErrors = Object.assign({},
    ...memberSectionSaveOrder.filter((section) => !!sectionErrors[section]).map((section) => getMemberSectionFieldErrors(section, formState)),
    ...contactSections.filter(({ role }) => !!sectionErrors[memberSectionErrorKey(CmpMemberSectionEnum.Contacts, { role })])
      .map(({ role }) => getMemberSectionFieldErrors(CmpMemberSectionEnum.Contacts, formState, { role })),
  ) as Record<string, string>;

  const getContactValidationInput = (contactSection: typeof contactSections[number], state: UpdateMemberFormState): MemberContactValidationInput => {
    const contact = state.contacts[contactSection.role];
    const primaryEmail = state.contacts.primaryVoting.email.trim().toLowerCase();
    const notPrimary = contactSection.role !== "primaryVoting";
    return {
      section: contactSection.title,
      nameLabel: contactSection.nameLabel,
      name: contact.name,
      email: contact.email,
      required: contactSection.requiredFields.length > 0,
      accessRequested: notPrimary && memberContactAccounts.isAccessChecked(contactSection.role),
      sameAsPrimary: notPrimary && (state.contactSameAs[contactSection.role] ||
        (!!contact.email.trim() && contact.email.trim().toLowerCase() === primaryEmail)),
      // S5: the saved access stays on the member's sign-in email (see the
      // administrator-sign-in-note). A newly ticked box still needs an email.
      keepsSignInEmail: contactSection.role === "administrator" && !!memberAdminEmail &&
        contactAccessDraftsRef.current.administrator === undefined,
    };
  };
  // CMP Dashboard (19) L16583-16595 / Update Member Form L2890-2900: the rows
  // of the MISSING REQUIRED FIELDS pop-up for one Save Changes.
  const getMemberSaveDialogRows = (section: CmpMemberSectionEnum, state: UpdateMemberFormState, contactTarget?: MemberContactSaveTarget): MissingRequiredFieldsRow[] => {
    if (section === CmpMemberSectionEnum.BillingAddress) {
      return getMemberBillingAddressRows(accountsPayableSectionTitle, state.billingAddress);
    }
    if (section !== CmpMemberSectionEnum.Contacts) return [];
    return contactSections.flatMap((contactSection) => {
      if (contactTarget && ("customContactId" in contactTarget || contactTarget.role !== contactSection.role)) return [];
      if (!contactTarget && !isMemberSectionDirty(savedBaselineRef.current, state, section, { role: contactSection.role }) &&
        contactAccessDraftsRef.current[contactSection.role] === undefined) return [];
      return getMemberContactRows(getContactValidationInput(contactSection, state));
    });
  };
  const showMemberSaveDialogRows = (rows: MissingRequiredFieldsRow[], state: UpdateMemberFormState) => {
    if (!rows.length) return false;
    const accessMissingRoles = contactSections
      .filter((contactSection) => isContactAccessDetailsMissing(getContactValidationInput(contactSection, state)))
      .map(({ role }) => role);
    setContactAccessTriedRoles((current) => Array.from(new Set([...current, ...accessMissingRoles])));
    setMissingFieldRows(rows);
    return true;
  };

  const saveMemberSection = async (
    section: CmpMemberSectionEnum,
    source = currentFormRef.current,
    showSuccess = true,
    contactTarget?: MemberContactSaveTarget,
    // The Accounts Payable card checks its contact and address together first.
    dialogChecked = false,
  ): Promise<boolean> => {
    const fieldErrors = getMemberSectionFieldErrors(section, source, contactTarget);
    const dialogRows = dialogChecked ? [] : getMemberSaveDialogRows(section, source, contactTarget);
    if (!isReadOnly && showMemberSaveDialogRows(dialogRows, source)) {
      setSectionErrors((current) => ({ ...current, [memberSectionErrorKey(section, contactTarget)]: Object.values(fieldErrors)[0] || "" }));
      return false;
    }
    const error = Object.values(fieldErrors)[0] || "";
    const contactErrors = !contactTarget && section === CmpMemberSectionEnum.CustomContacts
      ? Object.fromEntries(source.customContacts.map(({ id }) => [`custom:${id}`, fieldErrors[`custom:${id}`] || ""]))
      : !contactTarget && section === CmpMemberSectionEnum.Contacts
        ? Object.fromEntries(contactSections.map(({ role }) => [
            `contact:${role}`, Object.entries(fieldErrors).find(([key]) => key.startsWith(`${role}-`))?.[1] || "",
          ]))
        : {};
    setSectionErrors((current) => ({ ...current, ...contactErrors, [memberSectionErrorKey(section, contactTarget)]: error }));
    if (error || isReadOnly) return false;
    setSavingSection(section);
    try {
      const saved = await executeSaveUpdate({
        section,
        submittedFormState: mergeMemberSection(savedBaselineRef.current, source, section, contactTarget),
        contactTarget,
      });
      if (saved && showSuccess) setSuccessDialog((current) => current && (current === contactLoginSaveDialogRef.current ||
        (current.note && current.body === memberSaveSuccessDialog.body)) ? current : memberSaveSuccessDialog);
      if (saved && section === CmpMemberSectionEnum.Contacts) setContactAccessTriedRoles((current) =>
        contactTarget ? current.filter((role) => !("role" in contactTarget) || role !== contactTarget.role) : []);
      return saved;
    } finally {
      setSavingSection(null);
    }
  };

  const saveUpdate = async (): Promise<boolean> => {
    if (openDraftsRef.current) {
      setFooterError("Save or cancel the open form before leaving this step.");
      return false;
    }
    const source = currentFormRef.current;
    const accessDraftOrder = splitContactAccessDraftIds(contactAccessDraftsRef.current);
    if (!await applyContactAccessDrafts(accessDraftOrder.beforeSave)) return false;
    for (const section of memberSectionSaveOrder) {
      if (isMemberSectionDirty(savedBaselineRef.current, source, section)) {
        if (!await saveMemberSection(section, source, false)) return false;
      }
    }
    const submittedNote = staffNoteDraftRef.current.trim();
    if (submittedNote && !await saveStaffNote(submittedNote)) return false;
    if (!await applyContactAccessDrafts(accessDraftOrder.afterSave)) return false;
    const stillDirty = openDraftsRef.current || !!staffNoteDraftRef.current.trim() ||
      hasUnsavedMemberFormChanges({ baseline: savedBaselineRef.current, current: currentFormRef.current });
    if (stillDirty) setFooterError("More changes were made while saving. Save or discard those changes before leaving this step.");
    return !stillDirty;
  };

  // includeBillingAddress: the Accounts Payable card has ONE footer for its
  // contact and its Billing/Mailing Address (Update Member Form (19) L329-358).
  const renderSectionFooter = (section: CmpMemberSectionEnum, contactTarget?: MemberContactSaveTarget, includeBillingAddress = false) => {
    const accessDraftContactId = section === CmpMemberSectionEnum.Contacts && contactTarget && "role" in contactTarget ? contactTarget.role : undefined;
    const hasAccessDraft = accessDraftContactId !== undefined && contactAccessDrafts[accessDraftContactId] !== undefined;
    const billingSection = CmpMemberSectionEnum.BillingAddress;
    return isReadOnly ? undefined : (
    <SectionSaveReset
      variant="design"
      allowUnchangedSave={section === CmpMemberSectionEnum.Contacts || section === CmpMemberSectionEnum.BillingAddress}
      isDirty={isMemberSectionDirty(savedFormBaseline, formState, section, contactTarget) || hasAccessDraft ||
        (includeBillingAddress && isMemberSectionDirty(savedFormBaseline, formState, billingSection)) ||
        (section === CmpMemberSectionEnum.AffinityPrograms && (affinityDraftHasChanges || editingAffinityEnrollmentHasChanges || !!affinityEnrollmentError))}
      saveDisabled={section === CmpMemberSectionEnum.AffinityPrograms && (affinityDraftHasChanges || editingAffinityEnrollmentHasChanges)}
      saving={savingSection === section || (includeBillingAddress && savingSection === billingSection) || profileSaveInFlight}
      lastSavedLabel={`Last Saved ${memberUpdatedAtUtcMilli ? memberStamp(memberUpdatedAtUtcMilli) : "Never"}`}
      errorText={sectionErrors[memberSectionErrorKey(section, contactTarget)] || (includeBillingAddress ? sectionErrors[billingSection] : undefined)}
      onSave={async () => {
        const source = currentFormRef.current;
        const billingDirty = includeBillingAddress && isMemberSectionDirty(savedBaselineRef.current, source, billingSection);
        const formDirty = isMemberSectionDirty(savedBaselineRef.current, source, section, contactTarget);
        // An address-only change on the Accounts Payable card saves only the
        // address, so an older member without an AP contact can still save it.
        const saveMain = formDirty || hasAccessDraft || !billingDirty;
        // Design: Save Changes first lists every missing or invalid field of
        // the card in the MISSING REQUIRED FIELDS pop-up.
        const dialogRows = [
          ...(saveMain ? getMemberSaveDialogRows(section, source, contactTarget) : []),
          ...(billingDirty ? getMemberSaveDialogRows(billingSection, source) : []),
        ];
        if (showMemberSaveDialogRows(dialogRows, source)) {
          // The fields keep their inline messages under the pop-up.
          setSectionErrors((current) => ({
            ...current,
            ...(saveMain ? { [memberSectionErrorKey(section, contactTarget)]: Object.values(getMemberSectionFieldErrors(section, source, contactTarget))[0] || "" } : {}),
            ...(billingDirty ? { [billingSection]: Object.values(getMemberSectionFieldErrors(billingSection, source))[0] || "" } : {}),
          }));
          return;
        }
        // An access-only change skips the unchanged contact save. Otherwise
        // turning access off runs before the contact save and turning it on
        // runs after it (splitContactAccessDraftIds).
        if (saveMain) {
          if (accessDraftContactId !== undefined && hasAccessDraft && !formDirty) {
            if (!await applyContactAccessDrafts([accessDraftContactId])) return;
            setContactAccessTriedRoles((current) => current.filter((role) => role !== accessDraftContactId));
            if (!billingDirty) { setSuccessDialog(memberSaveSuccessDialog); return; }
          } else {
            const accessDraftOrder = accessDraftContactId !== undefined && hasAccessDraft
              ? splitContactAccessDraftIds(contactAccessDraftsRef.current, [accessDraftContactId])
              : { beforeSave: [], afterSave: [] };
            if (!await applyContactAccessDrafts(accessDraftOrder.beforeSave)) return;
            const saved = await saveMemberSection(section, currentFormRef.current, !billingDirty, contactTarget, true);
            if (!saved) return;
            if (!await applyContactAccessDrafts(accessDraftOrder.afterSave)) return;
          }
        }
        if (billingDirty) await saveMemberSection(billingSection, currentFormRef.current, true, undefined, true);
      }}
      onReset={() => {
        setFormState((current) => {
          const reset = mergeMemberSection(current, savedBaselineRef.current, section, contactTarget);
          return includeBillingAddress ? mergeMemberSection(reset, savedBaselineRef.current, billingSection) : reset;
        });
        if (accessDraftContactId !== undefined) {
          setContactAccessDrafts((current) => {
            const next = { ...current };
            delete next[accessDraftContactId];
            return next;
          });
          setContactAccessTriedRoles((current) => current.filter((role) => role !== accessDraftContactId));
        }
        setSectionErrors((current) => ({ ...current, [memberSectionErrorKey(section, contactTarget)]: "", ...(includeBillingAddress ? { [billingSection]: "" } : {}) }));
        if (section === CmpMemberSectionEnum.AffinityPrograms) {
          setAffinityDraft(createEmptyAffinityDraft());
          setEditingAffinityEnrollment(null);
          setAffinityConfirmOpen(false);
          setAffinityEnrollmentError("");
        }
        setSuccessDialog({ title: "", tone: "green", body: <>The unsaved changes in this {contactTarget ? "contact" : "section"} have been <strong>reset</strong> to the last saved version.</> });
      }}
    />
    );
  };

  const executeDownloadCredentials = async ({
    loginEmail,
    mode,
    format = "csv",
  }: {
    loginEmail: string;
    mode: Exclude<CredentialModalMode, null>;
    format?: "csv" | "html";
  }) => {
    if (credentialActionInFlightRef.current) {
      return;
    }

    const blockedReason = getCredentialActionBlock();
    if (blockedReason) { setCredentialActionError(blockedReason); return; }
    if (currentMemberAdminLoginEmailRef.current !== loginEmail) { setCredentialActionError(memberLoginAvailabilityStaleError); return; }

    setCredentialActionError("");

    if (!loginEmail || !credentialMemberId) {
      setCredentialActionError(
        "Credentials were not created, and no email was sent. This member has no saved sign-in email. Go to Step 2 Member Contacts, enter the Contact Email under Member Administrator Contact, and select Save Changes. Then try again."
      );
      return;
    }

    credentialActionInFlightRef.current = true;

    try {
      const result = await setNewTemporaryChamberMemberPassword({
        variables: {
          adminConsoleLoginEmail: loginEmail,
          chamberMemberId: credentialMemberId,
        },
      });
      const credentialResult =
        result.data?.setNewTemporaryChamberMemberPassword;
      const availabilityDecision = getMemberLoginAvailabilityDecision({
        availability: credentialResult?.loginAvailability,
        expectedLoginEmail: loginEmail,
      });

      if (availabilityDecision.status === "collision") {
        if (
          currentCredentialMemberIdRef.current !== credentialMemberId ||
          currentMemberAdminLoginEmailRef.current !== loginEmail
        ) {
          throw new UserFacingError(memberLoginAvailabilityStaleError);
        }

        setPendingMemberLoginCollision({
          action: "downloadCredentials",
          memberId: credentialMemberId,
          currentLoginEmail: loginEmail,
          actionLoginEmail: loginEmail,
          checkedLoginEmail: availabilityDecision.checkedLoginEmail,
          suggestedLoginEmail: availabilityDecision.suggestedLoginEmail,
          credentialMode: mode,
          credentialFormat: format,
        });
        return;
      }

      if (availabilityDecision.status === "stale") {
        throw new UserFacingError(memberLoginAvailabilityStaleError);
      }

      if (availabilityDecision.status === "invalid") {
        throw new UserFacingError(availabilityDecision.message);
      }

      const password = credentialResult?.password || "";

      if (!password) {
        // failureReason is server text for staff and is shown as it is.
        throw credentialResult?.failureReason
          ? new UserFacingError(credentialResult.failureReason)
          : new Error("No temporary password was returned.");
      }

      const rows = [
        ["Member Company", "Contact Name", "Email", "Temporary Password"],
        [
          formState.directory.companyName,
          memberAdminName,
          loginEmail,
          password,
        ],
      ];
      const csv = rows.map((row) => row.map(csvEscape).join(",")).join("\n");
      const companyName = member?.name || "Member";
      const filename = format === "html" ? `${companyName} - Member Dashboard Welcome Email.html` : `${companyName}-dashboard-credentials.csv`;
      const content = format === "html" ? credentialResult?.emailHtml : csv;
      if (!content) throw new Error("The welcome email was not returned. Try the download again.");
      downloadTextFile({ filename, contentType: format === "html" ? "text/html;charset=utf-8" : "text/csv;charset=utf-8", content });
      setSuccessDialog({
        title: "", tone: "green",
        body: format === "html"
          ? <>The Member Dashboard welcome email for <strong>{memberAdminName}</strong> has been downloaded and is ready to print.</>
          : <>The login credentials for <strong>{memberAdminName}</strong> have been downloaded successfully.</>,
        note: credentialFollowUpNote,
      });
      setPreviewEmailMode(null);
      setCredentialModalMode(null);
      setCredentialActionError("");

      void refreshMemberDetailAfterConfirmedCredentialAction({
        refetch: refetchCredentialMemberDetail,
        onRefreshFailed: () => {
          setSuccessDialog((currentDialog) =>
            currentDialog
              ? {
                  ...currentDialog,
                  refreshWarning: getCredentialRefreshWarning({
                    action: "download",
                  }),
                }
              : currentDialog
          );
        },
      });
    } catch (error) {
      setCredentialActionError(
        getMemberCredentialErrorMessage({
          action: "download",
          error,
        })
      );
    } finally {
      credentialActionInFlightRef.current = false;
    }
  };

  const downloadCredentials = async (format: "csv" | "html" = "csv") => {
    setCredentialActionError("");

    if (hasUnsavedAdminLoginEmailChange) {
      setCredentialActionError(
        "Select Save Changes in Member Administrator Contact first. Then you can send or reset credentials."
      );
      return;
    }

    await executeDownloadCredentials({
      loginEmail: memberAdminEmail,
      mode: credentialModalMode || "sendCredentials",
      format,
    });
  };

  const executeSendCredentialsEmail = async ({
    loginEmail,
    sendingMode,
  }: {
    loginEmail: string;
    sendingMode: Exclude<CredentialModalMode, null>;
  }) => {
    if (credentialActionInFlightRef.current) {
      return;
    }

    const blockedReason = getCredentialActionBlock();
    if (blockedReason) { setCredentialActionError(blockedReason); return; }
    if (currentMemberAdminLoginEmailRef.current !== loginEmail) { setCredentialActionError(memberLoginAvailabilityStaleError); return; }

    setCredentialActionError("");

    if (!loginEmail || !credentialMemberId) {
      setCredentialActionError(
        "The credentials email was not sent. This member has no saved sign-in email. Go to Step 2 Member Contacts, enter the Contact Email under Member Administrator Contact, and select Save Changes. Then try again."
      );
      return;
    }

    credentialActionInFlightRef.current = true;

    try {
      const result = await sendLoginInfoEmailToChamberMember({
        variables: {
          adminConsoleLoginEmail: loginEmail,
          chamberMemberId: credentialMemberId,
          type:
            sendingMode === "resetPassword"
              ? LoginInfoEmailTypes.ResetPassword
              : LoginInfoEmailTypes.BrandNew,
        },
      });
      const sendResult = result.data?.sendLoginInfoEmailToChamberMember;
      const availabilityDecision = getMemberLoginAvailabilityDecision({
        availability: sendResult?.loginAvailability,
        expectedLoginEmail: loginEmail,
      });

      if (availabilityDecision.status === "collision") {
        if (
          currentCredentialMemberIdRef.current !== credentialMemberId ||
          currentMemberAdminLoginEmailRef.current !== loginEmail
        ) {
          throw new UserFacingError(memberLoginAvailabilityStaleError);
        }

        setPendingMemberLoginCollision({
          action: "sendCredentials",
          memberId: credentialMemberId,
          currentLoginEmail: loginEmail,
          actionLoginEmail: loginEmail,
          checkedLoginEmail: availabilityDecision.checkedLoginEmail,
          suggestedLoginEmail: availabilityDecision.suggestedLoginEmail,
          credentialMode: sendingMode,
        });
        return;
      }

      if (availabilityDecision.status === "stale") {
        throw new UserFacingError(memberLoginAvailabilityStaleError);
      }

      if (availabilityDecision.status === "invalid") {
        throw new UserFacingError(availabilityDecision.message);
      }

      if (!sendResult?.loginInfoEmailSentSuccessfully) {
        throw new Error("The email service did not confirm the send.");
      }

      const confirmedSentAtUtcMilli =
        sendResult.credentialsSentAtUtcMilli;

      if (
        typeof confirmedSentAtUtcMilli === "number" &&
        Number.isFinite(confirmedSentAtUtcMilli) &&
        confirmedSentAtUtcMilli > 0
      ) {
        setConfirmedCredentialsSentAtUtcMilli(confirmedSentAtUtcMilli);
      }

      setCredentialModalMode(null);
      setPreviewEmailMode(null);
      setConfirmationModal(null);
      setCredentialActionError("");
      setSuccessDialog({ title: "", tone: "green", body: sendingMode === "resetPassword"
        ? <>The member dashboard password has been reset for <strong>{memberAdminName}</strong> <strong>only</strong> — no other contact’s login is affected. New login details have been emailed to {loginEmail}. This contact will show as not logged in until they sign in with their new password.</>
        : <>The Member Dashboard welcome email has been sent successfully to <strong>{memberAdminName} ({loginEmail})</strong>!</>,
        note: sendingMode === "resetPassword" ? undefined : credentialFollowUpNote,
        refreshWarning: sendResult.trackingWarning || undefined,
      });

      void refreshMemberDetailAfterConfirmedCredentialAction({
        refetch: refetchCredentialMemberDetail,
        onRefreshFailed: () => {
          setSuccessDialog((currentDialog) =>
            currentDialog
              ? {
                  ...currentDialog,
                  refreshWarning: [currentDialog.refreshWarning, getCredentialRefreshWarning({
                    action: "send",
                  })].filter(Boolean).join(" "),
                }
              : currentDialog
          );
        },
      });
    } catch (error) {
      setCredentialActionError(
        getMemberCredentialErrorMessage({
          action: "send",
          error,
        })
      );
    } finally {
      credentialActionInFlightRef.current = false;
    }
  };

  const sendCredentialsEmail = async () => {
    setCredentialActionError("");

    if (hasUnsavedAdminLoginEmailChange) {
      setCredentialActionError(
        "Select Save Changes in Member Administrator Contact first. Then you can send or reset credentials."
      );
      return;
    }

    await executeSendCredentialsEmail({
      loginEmail: memberAdminEmail,
      sendingMode: credentialModalMode || "sendCredentials",
    });
  };

  // After the collision save, go to the step staff chose. Ask again when other
  // edits are still unsaved, and wait when another collision is open.
  const resumeStepAfterMemberLoginCollision = () => {
    const step = stepAfterMemberLoginCollisionRef.current;
    if (step === null || memberLoginCollisionRef.current) return;
    stepAfterMemberLoginCollisionRef.current = null;
    const stillDirty = openDraftsRef.current || !!staffNoteDraftRef.current.trim() || membershipAdjustmentHasChanges ||
      Object.keys(contactAccessDraftsRef.current).length > 0 ||
      hasUnsavedMemberFormChanges({ baseline: savedBaselineRef.current, current: currentFormRef.current });
    if (stillDirty) setPendingStep(step);
    else navigateToStep(step);
  };

  const handleUseAlternativeMemberLogin = async () => {
    const pendingCollision = memberLoginCollisionRef.current;

    if (!pendingCollision) {
      return;
    }

    if (!isPendingMemberLoginCollisionCurrent(pendingCollision)) {
      clearPendingMemberLoginCollision();
      setMemberLoginActionError(
        pendingCollision.action,
        memberLoginAvailabilityStaleError
      );
      return;
    }

    await memberLoginCollisionSingleFlightRef.current.run(async () => {
      setMemberLoginCollisionLoading(true);

      try {
        const revisionBeforeIdentityChange = savedMemberRevisionRef.current ?? member?.updatedAtUtcMilli ?? null;
        const emailChangeResult =
          await chamberMemberChangeAdminConsoleLoginEmail({
            variables: {
              chamberMemberId: pendingCollision.memberId,
              currentAdminConsoleLoginEmail:
                pendingCollision.currentLoginEmail,
              // The suggestion is an opaque server value. Send it back
              // exactly as returned instead of rebuilding it in the client.
              newAdminConsoleLoginEmail:
                pendingCollision.suggestedLoginEmail,
            },
          });

        const emailChange =
          emailChangeResult.data?.chamberMemberChangeAdminConsoleLoginEmail;
        const changeAvailabilityDecision =
          getMemberLoginChangeAvailabilityDecision({
            changeResult: emailChange,
            expectedLoginEmail: pendingCollision.suggestedLoginEmail,
          });

        if (changeAvailabilityDecision.status === "collision") {
          setPendingMemberLoginCollision({
            ...pendingCollision,
            checkedLoginEmail: changeAvailabilityDecision.checkedLoginEmail,
            suggestedLoginEmail:
              changeAvailabilityDecision.suggestedLoginEmail,
          });
          return;
        }

        if (changeAvailabilityDecision.status === "stale") {
          throw new UserFacingError(memberLoginAvailabilityStaleError);
        }

        if (changeAvailabilityDecision.status === "invalid") {
          throw new UserFacingError(changeAvailabilityDecision.message);
        }

        const confirmedLoginEmail = getConfirmedMemberLoginEmail({
          changeResult: emailChange,
          expectedLoginEmail: pendingCollision.suggestedLoginEmail,
        });

        const collisionIsCurrent = isPendingMemberLoginCollisionCurrent(pendingCollision);
        recordConfirmedIdentity(confirmedLoginEmail, pendingCollision.actionLoginEmail, revisionBeforeIdentityChange);
        if (!collisionIsCurrent) throw new UserFacingError(memberLoginAvailabilityStaleError);
        if (pendingCollision.action !== "save") await confirmPendingIdentityReadback();

        memberLoginCollisionSequenceRef.current += 1;
        memberLoginCollisionRef.current = null;
        setMemberLoginCollision(null);

        await continueMemberLoginCollisionAction({
          action: pendingCollision.action,
          onSave: async () => {
            if (!pendingCollision.submittedFormState) {
              throw new UserFacingError(
                "The sign-in email was changed, but your other changes were not saved. Make them again, then select Save Changes."
              );
            }

            await executeSaveUpdate({
              submittedFormState: pendingCollision.submittedFormState,
              section: pendingCollision.section,
              contactTarget: pendingCollision.contactTarget,
              confirmedLoginEmail,
            });
          },
          onApproveWebsiteApplication: async () => {
            await handleBoardApproval(confirmedLoginEmail);
          },
          onDownloadCredentials: async () => {
            await executeDownloadCredentials({
              loginEmail: confirmedLoginEmail,
              mode: pendingCollision.credentialMode || "sendCredentials",
              format: pendingCollision.credentialFormat,
            });
          },
          onSendCredentials: async () => {
            await executeSendCredentialsEmail({
              loginEmail: confirmedLoginEmail,
              sendingMode:
                pendingCollision.credentialMode || "sendCredentials",
            });
          },
        });
        resumeStepAfterMemberLoginCollision();
      } catch (error) {
        stepAfterMemberLoginCollisionRef.current = null;
        if (memberLoginCollisionRef.current?.id === pendingCollision.id) {
          memberLoginCollisionSequenceRef.current += 1;
          memberLoginCollisionRef.current = null;
          setMemberLoginCollision(null);
        }

        setMemberLoginActionError(
          pendingCollision.action,
          getUserFacingErrorText(
            error,
            buildStaffCheckFirstFallback("change the sign-in email", "Refresh the page and check the Member Administrator Contact email")
          )
        );
      } finally {
        setMemberLoginCollisionLoading(false);
      }
    });
  };

  const renderStatusPanel = () => {
    if (!detail || !member) {
      return null;
    }

    const paymentReceived = !!statusActions?.paymentReceived;
    const pendingPaymentActions = getPendingMemberPaymentActionState({
      canRecordManualPayment: !!statusActions?.canRecordManualPayment,
      canResendPaymentRequest: !!statusActions?.canResendPaymentRequest,
      currentInvoiceId: detail.currentInvoice?.id,
      paymentReceived,
    });
    const boardApproved =
      !statusActions?.boardApprovalRequired || !!statusActions?.boardApproved;
    const approvedOnDate = statusActions?.boardApprovalRequired
      ? getMemberApprovedOnDate({
          approvedAtUtcMilli: member.cmpExtension?.boardApprovedAtUtcMilli,
          joinedAtUtcMilli: member.cmpExtension?.dateJoinedUtcMilli || member.createdAtUtcMilli,
          timeZone: memberTimeZone,
        })
      : null;
    // CMP Dashboard (22) L15803-15811 (Monday 13335776351): no payment row when
    // the application was submitted with Step 5 payment not required. Step 6
    // option 2 before approval has no invoice yet: the row shows "Not
    // Invoiced" with no actions. The row keeps the title from Monday
    // 13304510190 ("Pending Membership Payment"; design (22) says "Pending
    // Member Payment", open question to Moeen).
    const hidePendingPaymentRow = statusActions?.paymentRequiredForActivation === false;
    const invoiceAwaitingApproval = !detail.currentInvoice && !boardApproved;
    // BILL-003: the add-on invoice is an independent receivable. Its action is
    // bound to the exact server capability for that invoice, never the primary
    // membership invoice's paid state or action flags.
    const detailWithPaymentRequestCapabilities = detail as typeof detail & {
      paymentRequestCapabilities?: MemberInvoicePaymentRequestCapability[];
    };
    const unpaidAddOnPaymentRequestRows =
      getMemberUnpaidAddOnPaymentRequestRows({
        capabilities:
          detailWithPaymentRequestCapabilities.paymentRequestCapabilities || [],
        invoices: detail.invoices || [],
        lifecycleStatus: detail.lifecycleStatus,
      });
    const persistedPastPaymentSurfaceFeedback =
      currentStatus === "Past"
        ? getMemberPastTransitionPaymentSurfaceFeedback(detail.invoices)
        : null;
    const persistedPastRefundEvidence =
      currentStatus === "Past"
        ? getMemberPastTransitionRefundEvidence({
            invoices: detail.invoices,
            memberExtension: detail.member.cmpExtension,
          })
        : null;
    const paymentSurfaceCleanupBlocksRestart =
      !!persistedPastPaymentSurfaceFeedback &&
      persistedPastPaymentSurfaceFeedback.severity !== "success";
    const restartApplicationGuard = getMemberRestartApplicationGuard({
      canStartNewApplication: !!statusActions?.canStartNewApplication,
      hasUnsavedChanges: isDirty || membershipAdjustmentHasChanges,
      paymentSurfaceCleanupBlocksRestart,
    });

    return (
      <section className="mb-[24px] flex flex-col gap-[18px] leading-[normal] text-[#0A0A0A]">
          <h2 className="text-[22px] font-bold">Member Status</h2>
          {currentStatus === "Pending" && (
            <p className="mt-[-8px] text-[14px] leading-[1.6]">
              Member Status will be updated according to member payment.
              Members requiring {getCmpBoardWording(noun).ApprovalBy} approval will need to be
              manually selected to have member status updated.
            </p>
          )}

          {currentStatus === "Pending" && (
            <>
              <StatusChecklistRow
                title={
                  boardApproved ? "Member Approval" : "Pending Membership Approval"
                }
                description={boardApproved ? `Application has received ${getCmpBoardWording(noun).approvalByAndOr} approval.` : `Application pending ${getCmpBoardWording(noun).approvalByAndOr} approval.`}
                pill={
                  boardApproved ? (
                    <div className="flex flex-wrap items-center gap-[10px]">
                      <StatusPill tone="green">Approved</StatusPill>
                      {approvedOnDate && (
                        <span className="text-[14px] text-[#0A0A0A]">
                          Approved on <span className="font-semibold">{approvedOnDate}</span>
                        </span>
                      )}
                    </div>
                  ) : (
                    <StatusPill tone="red">Not Approved</StatusPill>
                  )
                }
                action={
                  statusActions?.canApproveBoard && !boardApproved ? (
                    <FormControlLabel
                      sx={{
                        ...choiceControlLabelSx,
                        "&&&": { display: "flex", margin: 0, gap: "12px", alignItems: "center" },
                        "&&& .MuiFormControlLabel-label": {
                          fontSize: "15px",
                          lineHeight: "normal",
                          letterSpacing: "normal",
                          padding: 0,
                        },
                      }}
                      control={
                        <Checkbox
                          checked={confirmationModal === "approveBoard"}
                          disabled={actionLoading}
                          onChange={(event) => {
                            if (event.target.checked) {
                              setConfirmationModal("approveBoard");
                            }
                          }}
                          sx={{ ...checkboxControlSx, "&&&": { margin: 0, padding: 0 } }}
                        />
                      }
                      label={
                        <span className="text-[15px] leading-[normal] text-[#0A0A0A]">
                          Check box to provide {getCmpBoardWording(noun).approvalByAndOr} approval of
                          pending member.
                        </span>
                      }
                    />
                  ) : undefined
                }
              />
              {hidePendingPaymentRow ? null : invoiceAwaitingApproval ? (
                <StatusChecklistRow
                  title="Pending Membership Payment"
                  description="No invoice is generated until the application is approved. Once approved, the membership invoice is generated and sent automatically."
                  pill={<StatusPill tone="gray">Not Invoiced</StatusPill>}
                />
              ) : <StatusChecklistRow
                title={paymentReceived ? "Member Payment" : "Pending Membership Payment"}
                description={
                  paymentReceived
                    ? "Member payment has been received. (Automatically handled when payment is applied to invoice)"
                    : paymentRequestWasSent
                      ? "Invoice has been sent member payment has not been received."
                      // Ajax (Hastings, Oct 15 2026): say plainly that the
                      // invoice was never emailed and what to click.
                      : pendingPaymentActions.canSendPaymentRequest &&
                          detail?.currentInvoice &&
                          !detail.currentInvoice.lastPaymentRequestSentAtUtcMilli
                        ? `${detail.currentInvoice.invoiceNumber ? `Invoice ${detail.currentInvoice.invoiceNumber}` : "The invoice"} has not been emailed yet. Click Send Invoice to email it.`
                        : "The invoice is unpaid. We cannot confirm that it was emailed to the member. Check the invoice in Invoices & Payments > Manage all Invoices & Payments."
                }
                pill={
                  paymentReceived ? (
                    <StatusPill tone="green">Paid</StatusPill>
                  ) : (
                    <StatusPill tone="red">Unpaid</StatusPill>
                  )
                }
                action={
                  pendingPaymentActions.canApplyPayment ||
                  pendingPaymentActions.canSendPaymentRequest ? (
                    <div className="flex flex-wrap gap-[12px]">
                      {pendingPaymentActions.canApplyPayment ? (
                        <Button
                          variant="contained"
                          startIcon={<PaymentsOutlinedIcon sx={{ width: 14, height: 14 }} />}
                          disabled={actionLoading}
                          onClick={handleOpenMemberManualPayment}
                          sx={statusPanelGoldButtonSx}
                        >
                          Apply Payment
                        </Button>
                      ) : null}
                      {pendingPaymentActions.canSendPaymentRequest ? (
                        <div className="flex w-fit flex-col gap-[5px]">
                        <Button
                          variant="contained"
                          startIcon={<StatusActionIcon icon="send" />}
                          disabled={actionLoading}
                          onClick={() => handleSendInvoice()}
                          sx={statusPanelGoldButtonSx}
                        >
                          {sendingInvoice
                            ? "Sending Invoice..."
                            : paymentRequestWasSent
                              ? "Resend Invoice"
                              : "Send Invoice"}
                        </Button>
                        {paymentRequestWasSent && <span className="text-center text-[12px]">Last Sent: {detail.currentInvoice?.lastPaymentRequestSentAtUtcMilli ? memberStamp(detail.currentInvoice.lastPaymentRequestSentAtUtcMilli) : "-"}</span>}
                        </div>
                      ) : null}
                    </div>
                  ) : undefined
                }
              />}
              <StatusChecklistRow
                title="Active Member"
                description={
                  statusActions?.canActivate
                    ? "Payment and any required approval are complete. Activate this member now."
                    : "The member becomes Active after payment is received and, if needed, approval is given."
                }
                icon={<StatusCheckIcon checked={false} />}
                action={
                  statusActions?.canActivate ? (
                    <Button
                      variant="contained"
                      disabled={actionLoading}
                      onClick={() => setConfirmationModal("activate")}
                      sx={statusPanelGreenButtonSx}
                    >
                      Activate Member
                    </Button>
                  ) : undefined
                }
              />
              <div className="flex flex-col items-start gap-[8px]">
                <Button
                  variant="contained"
                  startIcon={<StatusActionIcon icon="trash" />}
                  disabled={!statusActions?.canArchive || actionLoading}
                  onClick={() => setConfirmationModal("delete")}
                  sx={statusPanelRedButtonSx}
                >
                  Delete Member
                </Button>
                <DeleteMemberBlockedReason reason={deleteBlockedReason} />
              </div>
            </>
          )}

          {unpaidAddOnPaymentRequestRows.map((row) => (
            <StatusChecklistRow
              key={row.invoice.id}
              title="Pending Add-on Benefits Payment"
              description={[
                "Add-on benefits are invoiced separately at full amount and cannot be paid in installments. Member payment has not been received.",
                row.reason,
              ]
                .filter(Boolean)
                .join(" ")}
              pill={<StatusPill tone="red">Unpaid</StatusPill>}
              action={
                row.actionAvailable ? (
                  <Button
                    variant="contained"
                    startIcon={<StatusActionIcon icon="send" />}
                    disabled={actionLoading}
                    onClick={() =>
                      handleSendInvoice(row.invoice.id, row.isResend)
                    }
                    sx={statusPanelGoldButtonSx}
                  >
                    {sendingInvoice
                      ? "Sending Invoice..."
                      : row.isResend
                        ? "Resend Invoice"
                        : "Send Invoice"}
                  </Button>
                ) : undefined
              }
            />
          ))}

          {statusModelPanelStatus && statusModelPanelStatus !== CmpMemberStatusEnum.Cancelled &&
            statusModelPanelStatus !== CmpMemberStatusEnum.Deleted && statusModelPanelStatus !== CmpMemberStatusEnum.Past && (
            <MemberStatusModelPanel
              memberStatus={statusModelPanelStatus}
              statusDateLabel={formatStatusDateKey(detail.memberStatusDateKey)}
              recallUntilLabel={formatStatusDateKey(detail.memberRecallUntilDateKey)}
              startDateLabel={formatUtcCalendarDate(detail.dateJoinedUtcMilli)}
              renewalDateLabel={statusRenewalDateLabel}
              renewalSentLabel={renewalStatusCard.sentLabel}
              autoRenewEnabled={!!(member.cmpExtension?.membership?.paymentChoice?.autoRenew && chamberSettings?.membership.allowAutoRenewal)}
              noCharge={memberNoCharge}
              suspendedOnLabel={suspendedOnUtcMilli ? formatLongDate(suspendedOnUtcMilli) : null}
              endedByCancel={false}
              actionLoading={actionLoading}
              canSuspend={!!statusActions?.canSuspend}
              canUnsuspend={!!statusActions?.canActivate}
              canDelete={!!statusActions?.canArchive}
              deleteBlockedReason={deleteBlockedReason}
              onSuspend={() => setConfirmationModal("suspend")}
              onUnsuspend={() => setConfirmationModal("unsuspend")}
              onCancelRenewal={() => setConfirmationModal("cancelRenewal")}
              onUncancelRenewal={() => setConfirmationModal("uncancelRenewal")}
              onDelete={() => setConfirmationModal("delete")}
            />
          )}

          {!statusModel && currentStatus === "Active" && (
            <>
              <div className="flex items-start gap-[14px]">
                <StatusCheckIcon color="#10B981" />
                <div className="flex min-w-0 flex-col gap-[12px]">
                  <h3 className="text-[16px] font-bold">Active Member</h3>
                  <div className="flex flex-wrap gap-[36px]">
                    <div className="flex items-center gap-[9px]">
                      <svg aria-hidden="true" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0"><rect width="18" height="18" x="3" y="4" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
                      <span className="text-[14px]">Membership Start Date: <strong className="text-[16px] font-semibold">{formatUtcCalendarDate(detail.dateJoinedUtcMilli)}</strong></span>
                    </div>
                    <div className="flex flex-col gap-[6px]">
                      <div className="flex items-center gap-[9px]">
                        <svg aria-hidden="true" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0"><path d="M3 12a9 9 0 0 1 15.36-6.36L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15.36 6.36L3 16" /><path d="M3 21v-5h5" /></svg>
                        <span className="text-[14px]">Membership Renewal Date: <strong className="text-[16px] font-semibold">{formatUtcCalendarDate(member.cmpExtension?.membership?.pricingSnapshot.renewalDateUtcMilli)}</strong></span>
                      </div>
                      {renewalStatusCard.sentLabel ? (
                        <div className="flex items-center gap-[9px]">
                          <svg aria-hidden="true" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="#F0202E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0"><path d="m22 2-7 20-4-9-9-4Z" /><path d="M22 2 11 13" /></svg>
                          <span className="text-[14px] font-semibold text-[#F0202E]">{renewalStatusCard.sentLabel}</span>
                        </div>
                      ) : null}
                    </div>
                    <span className={`flex w-fit items-center gap-[10px] rounded-[10px] border px-[14px] py-[8px] ${member.cmpExtension?.membership?.paymentChoice?.autoRenew && chamberSettings?.membership.allowAutoRenewal ? "border-[#10B981] bg-[#F0FBF7]" : "border-[#9E9E9E] bg-[#F7F7F8]"}`}>
                      <svg aria-hidden="true" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke={member.cmpExtension?.membership?.paymentChoice?.autoRenew && chamberSettings?.membership.allowAutoRenewal ? "#10B981" : "#727272"} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0"><path d="M21 12a9 9 0 1 1-3-6.7" /><path d="M21 3v5h-5" /></svg>
                      <span className="whitespace-nowrap text-[14px] font-semibold">{renewalStatusCard.autoRenewLabel}</span>
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-start gap-[14px]">
                <Button variant="contained" startIcon={<StatusActionIcon icon="suspend" size={15} />} disabled={!statusActions?.canSuspend || actionLoading} onClick={() => setConfirmationModal("suspend")} sx={statusPanelGrayButtonSx}>Suspend Member</Button>
                <Button variant="contained" startIcon={<StatusActionIcon icon="trash" />} disabled={!statusActions?.canArchive || actionLoading} onClick={() => setConfirmationModal("delete")} sx={statusPanelRedButtonSx}>Delete Member</Button>
              </div>
              <DeleteMemberBlockedReason reason={deleteBlockedReason} />
            </>
          )}

          {!statusModel && currentStatus === "Suspended" && (
            <>
              <div className="flex items-start gap-[14px]">
                <StatusCheckIcon />
                <div>
                  <p className="text-[16px] font-bold">
                    Suspend Member
                  </p>
                  <p className="mt-[4px] text-[14px] leading-[1.6]">
                    This member is currently suspended. Member Dashboard access is suspended for ALL of this member’s contacts, along with {noun.singular} benefits. Unsuspending the member automatically restores dashboard access for every contact that had it enabled. To reactivate member click “<strong>Unsuspend Member</strong>” button or click “<strong>Delete Member</strong>” button and the member information will be stored as a <strong>Past Member</strong>.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-[14px]">
                <Button
                  variant="contained"
                  startIcon={<StatusActionIcon icon="unsuspend" size={15} />}
                  disabled={!statusActions?.canActivate || actionLoading}
                  onClick={() => setConfirmationModal("unsuspend")}
                  sx={statusPanelGreenButtonSx}
                >
                  Unsuspend Member
                </Button>
                <Button
                  variant="contained"
                  startIcon={<StatusActionIcon icon="trash" />}
                  disabled={!statusActions?.canArchive || actionLoading}
                  onClick={() => setConfirmationModal("delete")}
                  sx={statusPanelRedButtonSx}
                >
                  Delete Member
                </Button>
              </div>
              <DeleteMemberBlockedReason reason={deleteBlockedReason} />
            </>
          )}

          {(currentStatus === "Past" || currentStatus === "Prospect") && (
            <>
              {statusModelPanelStatus ? (
                <MemberStatusModelPanel
                  memberStatus={statusModelPanelStatus}
                  statusDateLabel={formatStatusDateKey(detail.memberStatusDateKey)}
                  recallUntilLabel={formatStatusDateKey(detail.memberRecallUntilDateKey)}
                  startDateLabel={formatUtcCalendarDate(detail.dateJoinedUtcMilli)}
                  renewalDateLabel={statusRenewalDateLabel}
                  autoRenewEnabled={false}
                  endedByCancel={(member.cmpExtension?.archiveReason || "").trim() === "Cancelled at renewal"}
                  actionLoading={actionLoading}
                  canSuspend={false}
                  canUnsuspend={false}
                  canDelete={false}
                  onSuspend={() => undefined}
                  onUnsuspend={() => undefined}
                  onCancelRenewal={() => undefined}
                  onUncancelRenewal={() => undefined}
                  onDelete={() => undefined}
                />
              ) : (
              <div className="flex items-start gap-[14px]">
                <StatusCheckIcon />
                <div>
                  <p className="text-[16px] font-bold">
                    Deleted Member
                  </p>
                  <p className="mt-[4px] text-[14px]">
                    This member is deleted and no longer has member login or
                    member benefits. Member Dashboard access is <strong>permanently denied</strong> for all of this member’s contacts. All information is stored as <strong>Past Member.</strong>
                  </p>
                </div>
              </div>
              )}
              {currentStatus === "Past" &&
                (persistedPastPaymentSurfaceFeedback ||
                  persistedPastRefundEvidence) && (
                <div className="max-w-5xl">
                  <MemberPastTransitionCleanupReplay
                    canReplayCleanup={canManageCmpMembers(
                      memberPagePermissions
                    )}
                    detail={detail}
                    statusLabel={
                      statusModelPanelStatus === CmpMemberStatusEnum.Deleted
                        ? "Deleted"
                        : undefined
                    }
                    executeReplay={async (input) => {
                      const result = await archiveCmpMember({
                        variables: { input },
                      });
                      const action = result.data?.archiveCmpMember;

                      if (!action?.actionSuccessful || !action.detail) {
                        throw new Error(
                          "Chamber Pro did not confirm the cleanup replay."
                        );
                      }

                      return action;
                    }}
                    onCommittedDetail={async (nextDetail) => {
                      setCurrentStatus(statusLabels[nextDetail.lifecycleStatus]);
                      try {
                        const refreshed = await refetchDetail();

                        if (!refreshed.data?.getCmpMemberDetail) {
                          throw new Error(
                            "No refreshed member detail was returned."
                          );
                        }

                        return undefined;
                      } catch {
                        return PAYMENT_CLEANUP_PAGE_NOT_UPDATED_TEXT;
                      }
                    }}
                  />
                </div>
              )}
              <div className="member-update-restart">
                <MemberRestartApplicationAction
                  actionLoading={actionLoading}
                  backendCanStartNewApplication={
                    restartApplicationGuard.allowed
                  }
                  blockedReason={restartApplicationGuard.blockedReason}
                  onStart={() =>
                    setConfirmationModal("restartApplication")
                  }
                  permissions={memberPagePermissions}
                />
              </div>
            </>
          )}
      </section>
    );
  };

  const {
    mode: idaMode,
    description: idaDescription,
    setDescription: setIdaDescription,
    dialogOpen: idaDialogOpen,
    setDialogOpen: setIdaDialogOpen,
    error: idaError,
    categoryFeedback: idaCategoryFeedback,
    generate: generateMemberContent,
  } = useMemberIdaGeneration({
    name: formState.directory.companyName,
    description: formState.directory.companyDescription,
    websiteUrl: formState.directory.companyWebsite,
    disabled: isReadOnly,
    onCategories: (ids) => {
      updateSection("directory", { directoryCategoryIds: ids });
      setSectionErrors((current) => ({ ...current, [CmpMemberSectionEnum.MemberDirectory]: "" }));
    },
  });

  const saveStaffNote = async (body: string): Promise<boolean> => {
    if (!member || isReadOnly) return false;
    if (!staffNoteOperation.current || staffNoteOperation.current.body !== body) {
      staffNoteOperation.current = { body, operationId: createLocalId("staff-note") };
    }
    setStaffNoteError("");
    try {
      const result = await addStaffNoteMutation({ variables: { input: {
        chamberId, memberId: member.id, body,
        operationId: staffNoteOperation.current.operationId,
      } } });
      if (!result.data?.addCmpMemberStaffNote) throw new Error("The note was not saved.");
      setLocalStaffNotes(result.data.addCmpMemberStaffNote.notes);
      if (staffNoteDraftRef.current.trim() === body) {
        staffNoteDraftRef.current = "";
        setStaffNoteDraft("");
      }
      staffNoteOperation.current = null;
      return true;
    } catch (error) {
      setStaffNoteError(getUserFacingErrorText(error, buildStaffErrorFallback("save the note")));
      return false;
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <SectionShell
        id="member-directory-information" footer={renderSectionFooter(CmpMemberSectionEnum.MemberDirectory)}
        title="Member Directory Information"
        description="Enter the member information as it will appear on both the website and the Chamber Perks App™ directories" descriptionLineHeight="normal"
        variant="design" contentGap={20}
      >
        {/* Not in the design; kept by the Sept 2 2026 decision (D4) so staff can hide a member without suspending it. */}
        <FormControlLabel
          sx={choiceControlLabelSx}
          control={
            <Checkbox
              checked={!formState.directory.showInDirectory}
              disabled={isReadOnly}
              onChange={(event) =>
                updateSection("directory", {
                  showInDirectory: !event.target.checked,
                })
              }
              sx={{ ...checkboxControlSx, ...changedChoiceControlSx(formState.directory.showInDirectory !== savedFormBaseline.directory.showInDirectory) }}
            />
          }
          label={
            <span className="text-sm leading-6 text-black">
              Don't show in member directory
            </span>
          }
        />
        <FormTextField
          id="member-name" error={memberFieldErrors["member-name"]}
          label="Member Name"
          value={formState.directory.companyName}
            containerClassName={`mb-0 min-w-0 ${formState.directory.companyName !== savedFormBaseline.directory.companyName ? "member-field-dirty" : ""}`}
          onChange={(value) =>
            updateSection("directory", { companyName: value })
          }
          placeholder="Enter Member Name as it will Appear in our directories"
          required
          disabled={isReadOnly}
          readOnly={!canEditCompanyName}
          title={canEditCompanyName ? undefined : "Requires the Change Existing Member Company Name permission"}
          helperText={canEditCompanyName ? undefined : "Only users with the Change Existing Member Company Name permission can change the member name."}
        />
        {/* CMP Dashboard (21) Update Member Form :101-105 (IF11 MB3): not
            locked by the company name permission (Q31). */}
        <FormTextField
          id="numbered-company"
          label="Numbered Company"
          description="This field will not be shown in member directory listings."
          value={formState.companyDetails.numberedCompany}
          containerClassName={`mb-0 min-w-0 ${formState.companyDetails.numberedCompany !== savedFormBaseline.companyDetails.numberedCompany ? "member-field-dirty" : ""}`}
          onChange={(numberedCompany) =>
            updateSection("companyDetails", { numberedCompany })
          }
          placeholder="If applicable, enter your numbered company"
          disabled={isReadOnly}
        />
        <div className="grid grid-cols-1 gap-x-5 gap-y-5 md:grid-cols-2">
          <FormTextField
            id="member-website"
            error={memberFieldErrors.companyWebsite}
            label="Member Website"
            value={formState.directory.companyWebsite}
            containerClassName={`mb-0 min-w-0 ${formState.directory.companyWebsite !== savedFormBaseline.directory.companyWebsite ? "member-field-dirty" : ""}`}
            onChange={(value) =>
              updateSection("directory", { companyWebsite: value })
            }
            placeholder="Enter Company Website URL"
            disabled={isReadOnly}
          />
          <FormTextField
            id="member-google-review-url"
            error={memberFieldErrors.googleReviewUrl}
            label="Member Google Review URL"
            value={formState.directory.googleReviewUrl}
            containerClassName={`mb-0 min-w-0 ${formState.directory.googleReviewUrl !== savedFormBaseline.directory.googleReviewUrl ? "member-field-dirty" : ""}`}
            onChange={(value) =>
              updateSection("directory", { googleReviewUrl: value })
            }
            placeholder="Enter Google Review URL"
            disabled={isReadOnly}
          />
        </div>
        <div className="grid grid-cols-1 gap-x-5 gap-y-5 md:grid-cols-[1fr_1fr_0.8fr_76px]">
          <FormTextField
            id="main-contact-name"
            label="Main Contact Name"
            value={formState.directory.mainCompanyContactName}
            containerClassName={`mb-0 min-w-0 ${formState.directory.mainCompanyContactName !== savedFormBaseline.directory.mainCompanyContactName ? "member-field-dirty" : ""}`}
            onChange={(value) =>
              updateSection("directory", {
                mainCompanyContactName: value,
              })
            }
            placeholder="Enter Main Contact Name"
            disabled={isReadOnly}
          />
          <FormTextField
            id="main-contact-email" error={memberFieldErrors["main-contact-email"]}
            label="Main Contact Email"
            value={formState.directory.mainCompanyContactEmail}
            containerClassName={`mb-0 min-w-0 ${formState.directory.mainCompanyContactEmail !== savedFormBaseline.directory.mainCompanyContactEmail ? "member-field-dirty" : ""}`}
            onChange={(value) =>
              updateSection("directory", {
                mainCompanyContactEmail: value,
              })
            }
            placeholder="Enter Company Contact Email"
            disabled={isReadOnly}
          />
          <FormPhoneField
            id="main-phone"
            label="Main Phone"
            value={formState.directory.mainCompanyPhone}
            containerClassName={`mb-0 min-w-0 ${formState.directory.mainCompanyPhone !== savedFormBaseline.directory.mainCompanyPhone ? "member-field-dirty" : ""}`}
            onChange={(value) =>
              updateSection("directory", { mainCompanyPhone: value })
            }
            placeholder="Enter Company Phone"
            disabled={isReadOnly}
          />
          <FormTextField
            id="main-phone-ext"
            label="Ext."
            value={formState.directory.mainCompanyPhoneExt}
            containerClassName={`mb-0 min-w-0 ${formState.directory.mainCompanyPhoneExt !== savedFormBaseline.directory.mainCompanyPhoneExt ? "member-field-dirty" : ""}`}
            onChange={(value) =>
              updateSection("directory", { mainCompanyPhoneExt: value })
            }
            placeholder="0000"
            disabled={isReadOnly}
          />
        </div>
        {/* Update Member Form (21) :134-142 (IF11 MB4): Toll-Free Phone and Fax Number under Main Phone. */}
        <div className="grid grid-cols-1 gap-x-5 gap-y-5 md:grid-cols-[1fr_1fr_0.8fr_76px]">
          <FormTextField
            id="member-toll-free-phone" error={memberFieldErrors["member-toll-free-phone"]}
            label="Toll-Free Phone"
            value={formState.directory.tollFreePhone}
            containerClassName={`mb-0 min-w-0 ${formState.directory.tollFreePhone !== savedFormBaseline.directory.tollFreePhone ? "member-field-dirty" : ""}`}
            onChange={(value) => updateSection("directory", { tollFreePhone: formatStaffTollFreeInput(value) })}
            placeholder="Enter Toll-Free Phone"
            inputMode="tel"
            disabled={isReadOnly}
          />
          <FormTextField
            id="member-fax-number" error={memberFieldErrors["member-fax-number"]}
            label="Fax Number"
            value={formState.directory.faxNumber}
            containerClassName={`mb-0 min-w-0 ${formState.directory.faxNumber !== savedFormBaseline.directory.faxNumber ? "member-field-dirty" : ""}`}
            onChange={(value) => updateSection("directory", { faxNumber: formatStaffFaxInput(value) })}
            placeholder="Enter Fax Number"
            inputMode="tel"
            disabled={isReadOnly}
          />
        </div>
        <FormTextField
          id="member-description"
          label="Member Description"
          value={formState.directory.companyDescription}
            containerClassName={`mb-0 min-w-0 member-update-description ${formState.directory.companyDescription !== savedFormBaseline.directory.companyDescription ? "member-field-dirty" : ""}`}
          onChange={(value) =>
            updateSection("directory", { companyDescription: value })
          }
          placeholder="Enter Member Details About Your Company"
          multiline
          rows={4}
          maxLength={descriptionMaxLength}
          characterCountAlign="left"
          disabled={isReadOnly}
          characterCountAccessory={<IdaGenerateControl onClick={() => void generateMemberContent(IdaGenerateMode.CompanyDescription)} loading={idaMode === IdaGenerateMode.CompanyDescription} disabled={isReadOnly || Boolean(idaMode)} />}
          descriptionClassName="mb-[12px] flex flex-col gap-[6px] text-[13px] leading-[1.5] text-[#0A0A0A]"
          description={<><p>Complete your company description to improve your visibility in both the website and Chamber Perks App™ directories. A detailed, keyword-rich profile helps customers, members, and AI-powered search tools better understand your business, improving search ability and increasing your chances of being found online.</p><p className="mt-[4px]"><strong>Need help?</strong> Click Generate and let IDA, our AI-powered assistant, create a professional, SEO and AI-friendly description based on your member's name, website URL, and the contents of your description field. Review and edit the suggested description before using it.</p></>}
        />
        <div className="grid grid-cols-1 gap-7 lg:grid-cols-2">
          <div className={hasUnsavedFormChanges({ baseline: savedFormBaseline.logo, current: formState.logo }) ? "rounded-lg outline outline-2 outline-[#E1A731] outline-offset-[-1px]" : ""}>
          <LogoUploadField
            id="update-member-logo"
            fileName={formState.logo.fileName}
            previewUrl={formState.logo.previewUrl}
            error={logoError}
            disabled={isReadOnly}
            onUpload={handleLogoUpload}
            onRemove={handleLogoDelete}
          />
          </div>
          <div>
            <label className="mb-[6px] block text-sm font-bold leading-5 text-gray-950">
              Website and Chamber Perks App™ Directory Categories{" "}
              <span className="text-red-500">*</span>
            </label>
            <p className="mb-[12px] text-[13px] leading-[1.5] text-[#0A0A0A]">
              Select 1 to 2 categories from the dropdown that best represent your member.<br />
              <strong>Need help?</strong> Click Generate and let IDA AI-powered search tool to recommend the most relevant categories, ensuring your member is grouped with similar companies for better visibility.
            </p>
            <SearchableSelectField
              className={haveSameMemberSelectionValues(formState.directory.directoryCategoryIds, savedFormBaseline.directory.directoryCategoryIds) ? "" : "member-field-dirty"}
              id="member-directory-categories" error={memberFieldErrors["member-directory-categories"]} multiple maxSelected={2}
              options={categories.map((category) => ({ value: category.id, label: category.name }))}
              value={formState.directory.directoryCategoryIds}
              onChange={(value) => updateSection("directory", { directoryCategoryIds: value as ChamberMemberCategory[] })}
              placeholder="Select Directory Categories" searchPlaceholder="Search Categories" disabled={isReadOnly}
            />
            <div className="mt-[10px] flex justify-end"><IdaGenerateControl onClick={() => void generateMemberContent(IdaGenerateMode.DirectoryCategories)} loading={idaMode === IdaGenerateMode.DirectoryCategories} disabled={isReadOnly || Boolean(idaMode)} /></div>
            {idaCategoryFeedback && <p role="status" className="mt-2 text-sm">{idaCategoryFeedback}</p>}
          </div>
        </div>
      </SectionShell>

      <SectionShell id="member-address" footer={renderSectionFooter(CmpMemberSectionEnum.MemberAddress)} title="Member Address" description="Provide your company address. This will appear in both our website and the Chamber Perks App™ directories. Unless you click box below to not show." descriptionLineHeight="normal" variant="design" contentGap={20}>
        <div className="grid grid-cols-1 gap-x-5 gap-y-5 md:grid-cols-2">
          <FormTextField
            onAddressSelect={(address) => updateSection("address", googleAddressToMemberFields(address))}
            // A hidden address needs only city and province (the directories show no more).
            id="street-address" required={!formState.address.hideStreetAddressInDirectories}
            label="Street Address"
            value={formState.address.streetAddress}
            containerClassName={`mb-0 min-w-0 md:col-span-2 ${formState.address.streetAddress !== savedFormBaseline.address.streetAddress ? "member-field-dirty" : ""}`}
            onChange={(value) =>
              updateSection("address", { streetAddress: value })
            }
            placeholder="Enter Street Address, Example: 123 Maple E.g."
            disabled={isReadOnly}
          />
          <FormTextField
            id="city" required
            label="City"
            value={formState.address.city}
            containerClassName={`mb-0 min-w-0 ${formState.address.city !== savedFormBaseline.address.city ? "member-field-dirty" : ""}`}
            onChange={(value) => updateSection("address", { city: value })}
            placeholder="Enter City Name"
            disabled={isReadOnly}
          />
          <SearchableSelectField
            id="province" required
            label="Province/State"
            value={formState.address.provinceOrState}
            className={`mb-0 min-w-0 ${formState.address.provinceOrState !== savedFormBaseline.address.provinceOrState ? "member-field-dirty" : ""}`}
            onChange={(value) =>
              updateSection("address", { provinceOrState: value })
            }
            options={getMemberProvinceOptions(formState.address.provinceOrState)}
            placeholder="Select Province/State"
            disabled={isReadOnly}
          />
          <FormTextField
            id="postal-code" required={!formState.address.hideStreetAddressInDirectories}
            label="Postal/Zip Code"
            value={formState.address.postalOrZipCode}
            containerClassName={`mb-0 min-w-0 ${formState.address.postalOrZipCode !== savedFormBaseline.address.postalOrZipCode ? "member-field-dirty" : ""}`}
            onChange={(value) =>
              updateSection("address", { postalOrZipCode: value })
            }
            placeholder="Enter the Postal/Zip Code"
            disabled={isReadOnly}
          />
          <FormCountryField
            id="country" required
            label="Country"
            value={formState.address.country}
            containerClassName={`mb-0 min-w-0 ${formState.address.country !== savedFormBaseline.address.country ? "member-field-dirty" : ""}`}
            onChange={(value) => updateSection("address", { country: value })}
            disabled={isReadOnly}
          />
        </div>
        <FormControlLabel
          sx={choiceControlLabelSx}
          control={
            <Checkbox
              checked={formState.address.hideStreetAddressInDirectories}
              disabled={isReadOnly}
              onChange={(event) =>
                updateSection("address", {
                  hideStreetAddressInDirectories: event.target.checked,
                })
              }
              sx={{ ...checkboxControlSx, ...changedChoiceControlSx(formState.address.hideStreetAddressInDirectories !== savedFormBaseline.address.hideStreetAddressInDirectories) }}
            />
          }
          label={
            <span className="text-sm text-black">
              Check this box if you don’t want the address to appear in any {noun.singular} directories. City and province fields will only show.
            </span>
          }
        />
      </SectionShell>

      <SectionShell
        id="links-to-social-media" footer={renderSectionFooter(CmpMemberSectionEnum.SocialLinks)}
        title="Links to Social Media"
        description="Enter the member’s social media links to display on both our website and the Chamber Perks App™ directories. Copy and paste your URL from each social media account below." descriptionLineHeight="normal"
        variant="design" contentGap={20}
      >
        <div className="grid grid-cols-1 gap-x-5 gap-y-5 md:grid-cols-2">
          {[
            ["facebookUrl", "Facebook Business Page URL", "Enter Facebook URL"],
            ["instagramUrl", "Instagram Account URL", "Enter Instagram URL"],
            ["threadsUrl", "Threads Account URL", "Enter Threads URL"],
            ["linkedinUrl", "LinkedIn Page URL", "Enter LinkedIn URL"],
            ["xTwitterUrl", "X (Twitter) Account URL", "Enter X (Twitter) URL"],
            ["tikTokUrl", "TikTok Account URL", "Enter TikTok URL"],
            ["youtubeUrl", "Youtube Channel URL", "Enter Youtube URL"],
          ].map(([field, label, placeholder]) => (
            <FormTextField
              key={field}
              id={field}
              error={memberFieldErrors[field]}
              label={label}
              containerClassName={`min-w-0 ${formState.socialLinks[field as keyof typeof formState.socialLinks] !== savedFormBaseline.socialLinks[field as keyof typeof formState.socialLinks] ? "member-field-dirty" : ""}`}
              value={
                formState.socialLinks[field as keyof typeof formState.socialLinks]
              }
              onChange={(value) =>
                updateSection("socialLinks", {
                  [field]: value,
                } as Partial<UpdateMemberFormState["socialLinks"]>)
              }
              placeholder={placeholder}
              disabled={isReadOnly}
            />
          ))}
        </div>
      </SectionShell>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      {memberContactAccounts.feedback}
      <div><h3 className="text-[17px] font-bold leading-[normal]">Get to Know Your Member</h3><p className="mt-[8px] text-sm leading-[1.55]">Enter all the required and optional member contacts, so our {noun.singular} can connect with the right people, improve communication, and provide more relevant support and value for this member. This information helps your {noun.singular} better communicate with and serve your member.</p><p className="mt-[10px] text-sm font-bold leading-[1.55]">The below contacts are provided for internal use by our {noun.singular} and will not be made public or shared on any directories unless entered in as the Main Member contact.</p></div>
      {contactSections.map((section) => {
        const contact = formState.contacts[section.role];
        const requiredFields = section.requiredFields;
        // Design red border on the name and email after a failed save with
        // the access box ticked (Update Member Form (19) L2347-2357).
        const accessMissing = contactAccessTriedRoles.includes(section.role) &&
          isContactAccessDetailsMissing(getContactValidationInput(section, formState));
        const accessNameMissing = accessMissing && !contact.name.trim();
        const accessEmailMissing = accessMissing && !hasEmailShape(contact.email.trim());

        return (
          <SectionShell id={`member-contact-${section.role}`} key={section.role} title={section.title} variant="design" contentGap={16} footer={renderSectionFooter(CmpMemberSectionEnum.Contacts, { role: section.role }, section.role === "accountsPayable")}>
            {/* The design places the intro under the title at the card's 16px gap. */}
            <div className="flex flex-col gap-[8px]">
              {applyCmpOrganizationNoun(section.description, noun).split("\n").map((paragraph) => <p key={paragraph} className="text-sm leading-[1.55] text-[#0A0A0A]">{paragraph}</p>)}
            </div>
            <p className="text-sm font-bold leading-[1.55] text-black">
              This contact information will NOT appear on our website and the
              Chamber Perks App™ directories, unless it is entered as the main
              company contact.
            </p>
            {section.role === "accountsPayable" && savedInvoiceRecipient && (
              <p className="text-sm leading-[1.55] text-[#0A0A0A]" data-testid="member-invoice-recipient">
                Invoices are sent to:{" "}
                <span className="font-bold">
                  {savedInvoiceRecipient.name ? `${savedInvoiceRecipient.name} (${savedInvoiceRecipient.email})` : savedInvoiceRecipient.email}
                </span>
                {` from the ${savedInvoiceRecipient.sourceLabel}.`}
              </p>
            )}
            <FormControlLabel
              className="m-0"
              sx={choiceControlLabelSx}
              control={
                <Checkbox
                  checked={formState.contactSameAs[section.role]}
                  disabled={isReadOnly}
                  onChange={(event) =>
                    handleContactSameAsToggle(
                      section.role,
                      event.target.checked
                    )
                  }
                  sx={{ ...checkboxControlSx, ...changedChoiceControlSx(formState.contactSameAs[section.role] !== savedFormBaseline.contactSameAs[section.role]) }}
                />
              }
              label={
                <span className="text-sm leading-6 text-black">
                  {section.sameAsLabel}
                </span>
              }
            />
            {contactSameAsNudge === section.role && (
              <p role="alert" className="mt-[-8px] text-[13px] font-semibold leading-[1.5] text-[#E24C4C]">
                {section.role === "primaryVoting"
                  ? "Main Contact details have not been entered yet."
                  : "Contact details have not been entered in the Primary Contact."}
              </p>
            )}
            {memberContactAccounts.renderAccessGrant(
              section.role,
              // Claude Design (11) sameAsPrimary: the box is ticked or the email matches the Primary Contact.
              getContactValidationInput(section, formState).sameAsPrimary,
              accessMissing,
            )}
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <FormTextField autoComplete="off"
                id={`${section.role}-name`} error={memberFieldErrors[`${section.role}-name`]}
                label={section.nameLabel}
                value={contact.name} containerClassName={`min-w-0 ${contact.name !== savedFormBaseline.contacts[section.role].name ? "member-field-dirty" : ""} ${accessNameMissing ? "member-field-access-missing" : ""}`}
                onChange={(value) => updateContact(section.role, "name", value)}
                placeholder={section.role === "accountsPayable" ? "Enter Contact Name" : section.role === "administrator" ? "Enter Primary Contact Name" : `Enter ${section.nameLabel}`}
                required={requiredFields.includes("name")}
                disabled={isReadOnly}
              />
              <FormTextField autoComplete="off"
                id={`${section.role}-title`}
                label="Title or Designation"
                value={contact.title} containerClassName={`min-w-0 ${contact.title !== savedFormBaseline.contacts[section.role].title ? "member-field-dirty" : ""}`}
                onChange={(value) => updateContact(section.role, "title", value)}
                placeholder="Enter Title or Designation"
                disabled={isReadOnly}
              />
            </div>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-[1.2fr_1fr_76px_1fr]">
              <FormTextField autoComplete="off"
                id={`${section.role}-email`} error={memberFieldErrors[`${section.role}-email`]}
                label="Contact Email"
                value={contact.email} containerClassName={`min-w-0 ${contact.email !== savedFormBaseline.contacts[section.role].email ? "member-field-dirty" : ""} ${accessEmailMissing ? "member-field-access-missing" : ""}`}
                onChange={(value) => updateContact(section.role, "email", value)}
                placeholder={section.role === "primaryVoting" ? "Enter Primary Contact Email" : section.role === "administrator" ? "Enter Primary Contact Email" : section.role === "accountsPayable" ? "Enter Billing Contact Email" : "Enter Contact Email"}
                required={requiredFields.includes("email")}
                disabled={isReadOnly}
              />
              <FormPhoneField autoComplete="off"
                id={`${section.role}-phone`}
                label={section.phoneLabel}
                value={contact.companyPhone} containerClassName={`min-w-0 ${contact.companyPhone !== savedFormBaseline.contacts[section.role].companyPhone ? "member-field-dirty" : ""}`}
                onChange={(value) =>
                  updateContact(section.role, "companyPhone", value)
                }
                placeholder="Enter Contact Number"
                disabled={isReadOnly}
              />
              <FormTextField autoComplete="off"
                id={`${section.role}-ext`}
                label="Ext."
                value={contact.ext} containerClassName={`min-w-0 ${contact.ext !== savedFormBaseline.contacts[section.role].ext ? "member-field-dirty" : ""}`}
                onChange={(value) => updateContact(section.role, "ext", value)}
                placeholder="0000"
                disabled={isReadOnly}
              />
              <FormPhoneField autoComplete="off" id={`${section.role}-mobile`} label="Mobile Phone Number" value={contact.mobilePhoneNumber} containerClassName={`min-w-0 ${contact.mobilePhoneNumber !== savedFormBaseline.contacts[section.role].mobilePhoneNumber ? "member-field-dirty" : ""}`} onChange={(value) => updateContact(section.role, "mobilePhoneNumber", value)} placeholder="Enter Mobile Phone Number" disabled={isReadOnly} />
            </div>
            {section.role === "administrator" && !!memberAdminEmail && !contact.email.trim() && (
              <p data-testid="administrator-sign-in-note" className="text-[13px] leading-[1.5] text-[#0A0A0A]">
                This member signs in to the Member Dashboard with {memberAdminEmail}.
                If you leave Contact Email blank and click Save Changes, this
                sign-in email stays and shows again in Contact Email.
              </p>
            )}
            {memberContactAccounts.renderCredentials(section.role, JSON.stringify(contact) !== JSON.stringify(savedFormBaseline.contacts[section.role]))}
            {section.role === "accountsPayable" && (
              <>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <h4 className="mt-1.5 text-[15px] font-bold md:col-span-2">Billing/Mailing Address</h4>
                <FormControlLabel className="m-0 md:col-span-2" sx={choiceControlLabelSx}
                  control={<Checkbox disabled={isReadOnly} sx={checkboxControlSx}
                    checked={billingAddressCopySelected !== false && Boolean(formState.address.streetAddress) && (["streetAddress", "city", "provinceOrState", "postalOrZipCode", "country"] as const).every((key) => formState.billingAddress[key] === formState.address[key])}
                    onChange={(event) => { setBillingAddressCopySelected(event.target.checked); if (event.target.checked) updateSection("billingAddress", {
                      streetAddress: formState.address.streetAddress, city: formState.address.city,
                      provinceOrState: formState.address.provinceOrState, postalOrZipCode: formState.address.postalOrZipCode, country: formState.address.country,
                    }); }} />}
                  label={<span className="text-sm leading-6">Check here if same as Member Company Address</span>} />
                <FormTextField autoComplete="off"
                  onAddressSelect={(address) => updateSection("billingAddress", googleAddressToMemberFields(address))}
                  id="billing-street-address" required error={memberFieldErrors["billing-street-address"]}
                  label="Street Address"
                  value={formState.billingAddress.streetAddress}
            containerClassName={`mb-0 min-w-0 md:col-span-2 ${formState.billingAddress.streetAddress !== savedFormBaseline.billingAddress.streetAddress ? "member-field-dirty" : ""}`}
                  onChange={(value) =>
                    updateSection("billingAddress", { streetAddress: value })
                  }
                  placeholder="Enter Street Address, Example 123 Maple E.g."
                  disabled={isReadOnly}
                />
                <FormTextField autoComplete="off"
                  id="billing-city" required error={memberFieldErrors["billing-city"]}
                  label="City"
                  value={formState.billingAddress.city}
            containerClassName={`mb-0 min-w-0 ${formState.billingAddress.city !== savedFormBaseline.billingAddress.city ? "member-field-dirty" : ""}`}
                  onChange={(value) =>
                    updateSection("billingAddress", { city: value })
                  }
                  placeholder="Enter City Name"
                  disabled={isReadOnly}
                />
                <SearchableSelectField
                  id="billing-province" required error={memberFieldErrors["billing-province"]}
                  label="Province/State"
                  value={formState.billingAddress.provinceOrState}
                  options={getMemberProvinceOptions(formState.billingAddress.provinceOrState)}
            className={`mb-0 min-w-0 ${formState.billingAddress.provinceOrState !== savedFormBaseline.billingAddress.provinceOrState ? "member-field-dirty" : ""}`}
                  onChange={(value) =>
                    updateSection("billingAddress", {
                      provinceOrState: value,
                    })
                  }
                  placeholder="Select Province/State"
                  disabled={isReadOnly}
                />
                <FormTextField autoComplete="off"
                  id="billing-postal-code" required error={memberFieldErrors["billing-postal-code"]}
                  label="Postal/Zip Code"
                  value={formState.billingAddress.postalOrZipCode}
            containerClassName={`mb-0 min-w-0 ${formState.billingAddress.postalOrZipCode !== savedFormBaseline.billingAddress.postalOrZipCode ? "member-field-dirty" : ""}`}
                  onChange={(value) =>
                    updateSection("billingAddress", {
                      postalOrZipCode: value,
                    })
                  }
                  placeholder="Enter the Postal/Zip Code"
                  disabled={isReadOnly}
                />
                <FormCountryField id="billing-country" required error={memberFieldErrors["billing-country"]} label="Country" value={formState.billingAddress.country}
            containerClassName={`mb-0 min-w-0 ${formState.billingAddress.country !== savedFormBaseline.billingAddress.country ? "member-field-dirty" : ""}`} onChange={(value) => updateSection("billingAddress", { country: value })} disabled={isReadOnly} />
              </div>
              </>
            )}
          </SectionShell>
        );
      })}
      {memberContactAccounts.unmatchedLegacyAccounts.map((account, index) => <SectionShell key={account.contactId} id={`existing-member-dashboard-account-${index}`} title="Existing Member Dashboard Account" variant="design" description="This Member Dashboard sign-in does not match any contact saved on this member. Check its access below.">
        <p className="text-sm">{account.name}<br />{account.email}</p>
        {memberContactAccounts.renderCredentials(account.contactId, hasUnsavedAdminLoginEmailChange)}
      </SectionShell>)}
      <CustomContactCards renderCredentials={(contact, saved) => memberContactAccounts.renderCredentials(`custom:${contact.id}`, !saved || JSON.stringify(contact) !== JSON.stringify(savedFormBaseline.customContacts.find(item => item.id === contact.id)))} key={additionalContactResetKey} baseline={savedFormBaseline.customContacts} onDraftDirtyChange={setAdditionalContactDraftDirty} value={formState.customContacts} onChange={(customContacts) => setFormState((current) => ({ ...current, customContacts }))} onSave={async (customContacts, customContactId) => {
        return saveMemberSection(CmpMemberSectionEnum.CustomContacts, { ...currentFormRef.current, customContacts }, false, customContactId ? { customContactId } : undefined);
      }} renderFooter={(contact) => renderSectionFooter(CmpMemberSectionEnum.CustomContacts, { customContactId: contact.id })} contactError={(id) => sectionErrors[memberSectionErrorKey(CmpMemberSectionEnum.CustomContacts, { customContactId: id })]} disabled={isReadOnly} saving={profileSaveInFlight} error={sectionErrors[CmpMemberSectionEnum.CustomContacts]} />
    </div>
  );

  // Monday 13252201858: a Step 5 save waits as the pending membership change.
  const memberSizeDisplay = getMemberSizeDisplayValues({
    pendingNewMembership: ((renewalDetail || detail)?.renewal?.pendingMembershipChange ||
      (renewalDetail || detail)?.member.cmpExtension?.pendingMembershipChange)?.newMembership,
    current: savedFormBaseline.membership,
  });
  const renderStep3 = () => (
    <div className="space-y-6">
      <SectionShell
        id="member-insights" footer={renderSectionFooter(CmpMemberSectionEnum.MemberInsights)}
        title="Member Insights"
        description={
          <span className="flex flex-col gap-[6px]">
            <span>Enter the details below to help your {noun.singular} build stronger relationships, improve member retention, identify interests, and measure member participation.</span>
            <span className="font-bold leading-[normal]">This section is for internal use only.</span>
          </span>
        }
        variant="design" contentGap={18}
      >
        {/* Update Member Form (15) D:503-517, Monday D17/D26: read-only Member
            Size from the saved Step 5 numbers (join and renewals). */}
        <div className="flex flex-col gap-[6px]" data-testid="member-insights-member-size">
          <p className="text-[14px] font-bold leading-[normal] text-[#0A0A0A]">Member Size</p>
          <p className="text-[13px] leading-[1.5] text-[#0A0A0A]">
            {/* Decision B4: the fields show the stored numbers, so the text says where they come from. */}
            These numbers come from the member&apos;s current membership, or from the member size saved in Step 5: Membership Renewal. They cannot be edited here; to change them, update the member size in Step 5: Membership Renewal and save.
          </p>
          <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-[20px]">
            {([
              ["member-size-full-time", "Full-Time Employees", memberSizeDisplay.fullTimeEmployees],
              ["member-size-part-time", "Part-Time Employees", memberSizeDisplay.partTimeEmployees],
            ] as const).map(([fieldId, fieldLabel, fieldValue]) => (
              <div className="flex min-w-0 flex-col gap-[6px]" key={fieldId}>
                <label className="text-[13px] font-semibold leading-[normal] text-[#0A0A0A]" htmlFor={fieldId}>{fieldLabel}</label>
                <input
                  className="box-border w-full cursor-not-allowed rounded-[8px] border border-[#ECECEC] bg-[#F7F7F8] px-[12px] py-[11px] text-[14px] text-[#0A0A0A]"
                  disabled
                  id={fieldId}
                  readOnly
                  type="text"
                  value={String(fieldValue ?? "").trim()}
                />
              </div>
            ))}
          </div>
        </div>
        {showCharityRegistrationNumber && (
          <FormTextField
            id={MEMBER_CHARITY_REGISTRATION_NUMBER_FIELD_ID}
            label={MEMBER_CHARITY_REGISTRATION_NUMBER_LABEL}
            placeholder={MEMBER_CHARITY_REGISTRATION_NUMBER_PLACEHOLDER}
            value={formState.companyDetails.charityRegistrationNumber}
            containerClassName={`mb-0 min-w-0 ${formState.companyDetails.charityRegistrationNumber !== savedFormBaseline.companyDetails.charityRegistrationNumber ? "member-field-dirty" : ""}`}
            onChange={(charityRegistrationNumber) =>
              updateSection("companyDetails", { charityRegistrationNumber })
            }
            disabled={isReadOnly}
          />
        )}
        <SearchableSelectField
          id="year-member-was-established"
          error={memberFieldErrors["year-member-was-established"]}
          label="Year member was established"
          value={formState.companyDetails.yearEstablished}
          className={formState.companyDetails.yearEstablished !== savedFormBaseline.companyDetails.yearEstablished ? "member-field-dirty" : ""}
          onChange={(yearEstablished) => updateSection("companyDetails", { yearEstablished })}
          options={getMemberEstablishedYearOptions(formState.companyDetails.yearEstablished)}
          placeholder="Select year member was established"
          searchPlaceholder="Search Year"
          disabled={isReadOnly}
        />
        <MemberOwnershipDiversityField
          selected={formState.companyDetails.memberOwnershipDiversity}
          baselineSelected={savedFormBaseline.companyDetails.memberOwnershipDiversity}
          onChange={(memberOwnershipDiversity) => updateSection("companyDetails", { memberOwnershipDiversity })}
          disabled={isReadOnly}
        />
        <SearchableSelectField
          id="how-did-member-hear-about-us"
          label="How did member hear about us?"
          value={formState.companyDetails.howDidYouHearAboutUs.length === 1 ? formState.companyDetails.howDidYouHearAboutUs[0] : ""}
          helperText={formState.companyDetails.howDidYouHearAboutUs.length > 1 ? `Saved answers: ${formState.companyDetails.howDidYouHearAboutUs.join(", ")}. Select one answer to replace them.` : undefined}
          className={!haveSameMemberSelectionValues(formState.companyDetails.howDidYouHearAboutUs, savedFormBaseline.companyDetails.howDidYouHearAboutUs) ? "member-field-dirty" : ""}
          onChange={(selected) =>
            updateSection("companyDetails", {
              howDidYouHearAboutUs: selected ? [selected] : [],
            })
          }
          options={[
            ...(formState.companyDetails.howDidYouHearAboutUs.length === 1
              ? [{ value: "", label: "Not specified" }]
              : []),
            ...hearAboutOptions.map((option) => ({ value: option, label: option })),
          ]}
          searchPlaceholder="Search"
          placeholder="Select how you heard about us"
          disabled={isReadOnly}
        />

        <SearchableSelectField
          id="primary-reason-member-joined"
          label="Primary reason member joined"
          value={formState.companyDetails.primaryReasonsForJoining.length === 1 ? formState.companyDetails.primaryReasonsForJoining[0] : ""}
          helperText={formState.companyDetails.primaryReasonsForJoining.length > 1 ? `Saved answers: ${formState.companyDetails.primaryReasonsForJoining.join(", ")}. Select one answer to replace them.` : undefined}
          className={!haveSameMemberSelectionValues(formState.companyDetails.primaryReasonsForJoining, savedFormBaseline.companyDetails.primaryReasonsForJoining) ? "member-field-dirty" : ""}
          onChange={(selected) =>
            updateSection("companyDetails", {
              primaryReasonsForJoining: selected ? [selected] : [],
            })
          }
          options={reasonForJoiningOptions.map((option) => ({ value: option, label: option }))}
          searchPlaceholder="Search"
          placeholder="Select your reason for joining"
          disabled={isReadOnly}
        />

        <MultiOptionGroup
          label="The social media platform this member is active on?"
          selected={formState.companyDetails.activeSocialPlatforms}
          baselineSelected={savedFormBaseline.companyDetails.activeSocialPlatforms}
          onChange={(selected) =>
            updateSection("companyDetails", {
              activeSocialPlatforms: updateMemberSocialPlatforms(formState.companyDetails.activeSocialPlatforms, selected),
            })
          }
          options={socialPlatformOptions}
          columns="inline"
          disabled={isReadOnly}
        />
        <RadioChoiceGroup
          label="Have you heard about the Chamber Perks App™?"
          options={yesNoOptions}
          value={formState.insights.chamberPerksAppHeardAbout}
          baseline={savedFormBaseline.insights.chamberPerksAppHeardAbout}
          onChange={(value) =>
            updateSection("insights", { chamberPerksAppHeardAbout: value })
          }
          disabled={isReadOnly}
        />
        <RadioChoiceGroup
          label="Have you downloaded the Chamber Perks App™?"
          options={yesNoOptions}
          value={formState.insights.chamberPerksAppDownloaded}
          baseline={savedFormBaseline.insights.chamberPerksAppDownloaded}
          onChange={(value) =>
            updateSection("insights", { chamberPerksAppDownloaded: value })
          }
          disabled={isReadOnly}
        />
        <InterestOptionMatrix
          label={`Select if member has shown interest in applying to become a ${getCmpBoardWording(noun).boardDirector} or join a committee`}
          disabled={isReadOnly}
          rows={[
            {
              id: "board-director-interest",
              label: `Interested in becoming a ${getCmpBoardWording(noun).boardDirector}`,
              affirmativeValue: boardDirectorInterestAffirmative,
              value: formState.insights.boardDirectorInterest,
              baseline: savedFormBaseline.insights.boardDirectorInterest,
              onChange: (value) =>
                updateSection("insights", { boardDirectorInterest: value }),
            },
            {
              id: "committee-interest",
              label: committeeInterestAffirmative,
              affirmativeValue: committeeInterestAffirmative,
              value: formState.insights.committeeInterest,
              baseline: savedFormBaseline.insights.committeeInterest,
              onChange: (value) =>
                updateSection("insights", { committeeInterest: value }),
            },
          ]}
        />
        <MultiOptionGroup
          label={`Select the method(s) this member wishes to receive communication from our ${noun.singular}.`}
          error={memberFieldErrors.communicationPreferences}
          selected={formState.insights.communicationPreferences}
          baselineSelected={savedFormBaseline.insights.communicationPreferences}
          onChange={(selected) =>
            updateSection("insights", {
              communicationPreferences: selected,
            })
          }
          options={communicationPreferenceOptions}
          columns="single"
          required
          disabled={isReadOnly}
        />
      </SectionShell>

      <SectionShell
        id="member-referral-tracking" footer={renderSectionFooter(CmpMemberSectionEnum.ReferralTracking)}
        title="Member Referral Tracking"
        description={`Track member referrals, monitor conversion rates, and recognize members who help grow your ${noun.singular} community.`} descriptionLineHeight="normal"
        variant="design" contentGap={18}
      >
        <div className="space-y-4">
          {formState.referralTracking.map((referral) => {
            const baseline = savedFormBaseline.referralTracking.find((saved) => saved.id === referral.id);
            const fieldClass = (field: keyof ReferralTrackingInfo) => referral[field] !== (baseline?.[field] || "") ? "member-field-dirty" : "";
            return (
              <div key={referral.id} className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div className={fieldClass("referralDate")}>
                  <FormCalendarDateField
                    id={`${referral.id}-referral-date`}
                    label="Member Referral Date"
                    value={referral.referralDate}
                    onChange={(value) => updateReferral(referral.id, "referralDate", value)}
                    placeholder="Enter Referral Date"
                    iconPosition="right"
                    disabled={isReadOnly}
                  />
                </div>
                <div className={fieldClass("becameMemberDate")}>
                  <FormCalendarDateField
                    id={`${referral.id}-became-member-date`}
                    label="Became A Member Date (Will Automatically Populate If Member Joins)"
                    value={referral.becameMemberDate}
                    onChange={(value) => updateReferral(referral.id, "becameMemberDate", value)}
                    placeholder="Date the Member Referral Joined"
                    iconPosition="right"
                    disabled={isReadOnly}
                  />
                </div>
                <FormTextField
                  id={`${referral.id}-company-referral-name`}
                  label="Company Referral Name"
                  value={referral.companyReferralName}
                  containerClassName={fieldClass("companyReferralName")}
                  onChange={(value) => updateReferral(referral.id, "companyReferralName", value)}
                  placeholder="Enter Company Referral Name"
                  required
                  disabled={isReadOnly}
                />
                <FormTextField
                  id={`${referral.id}-contact-name`}
                  label="Contact Name"
                  value={referral.contactName}
                  containerClassName={fieldClass("contactName")}
                  onChange={(value) => updateReferral(referral.id, "contactName", value)}
                  placeholder="Enter Contact Name"
                  disabled={isReadOnly}
                />
                <div className="grid grid-cols-1 gap-5 md:col-span-2 sm:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_76px]">
                  <FormTextField
                    id={`${referral.id}-email`}
                    label="Email"
                    value={referral.email}
                    containerClassName={fieldClass("email")}
                    onChange={(value) => updateReferral(referral.id, "email", value)}
                    placeholder="Enter Contact Email"
                    disabled={isReadOnly}
                  />
                  <FormPhoneField
                    id={`${referral.id}-contact-number`}
                    label="Contact Number"
                    value={referral.contactNumber}
                    containerClassName={fieldClass("contactNumber")}
                    onChange={(value) => updateReferral(referral.id, "contactNumber", value)}
                    placeholder="Enter Contact Number"
                    disabled={isReadOnly}
                  />
                  <FormTextField
                    id={`${referral.id}-extension`}
                    label="Ext."
                    value={referral.contactNumberExtension}
                    containerClassName={fieldClass("contactNumberExtension")}
                    onChange={(value) => updateReferral(referral.id, "contactNumberExtension", value)}
                    placeholder="0000"
                    disabled={isReadOnly}
                  />
                </div>
              </div>
            );
          })}
        </div>
        <Button
          type="button"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={addReferralTracking}
          disabled={isReadOnly}
          sx={wizardGoldButtonSx}
        >
          Add Another Referral
        </Button>
      </SectionShell>

      <SectionShell
        id="associate-member-company" footer={renderSectionFooter(CmpMemberSectionEnum.AssociateMember)}
        title="Associate Member Company"
        description="Select if this member is an Associated Member Company and select the Primary Member Company from drop-down below." descriptionLineHeight="normal"
        variant="design" contentGap={16}
      >
        <FormControlLabel
          sx={{ alignItems: "flex-start", margin: 0 }}
          control={
            <Checkbox
              checked={formState.association.isAssociateCompanyMember}
              disabled={isReadOnly}
              onChange={(event) =>
                updateSection("association", {
                  isAssociateCompanyMember: event.target.checked,
                })
              }
              sx={{ ...checkboxControlSx, ...changedChoiceControlSx(formState.association.isAssociateCompanyMember !== savedFormBaseline.association.isAssociateCompanyMember) }}
            />
          }
          label={
            <span>
              <span className="block text-sm font-bold text-gray-950">
                Associate Member Company
              </span>
              <span className="mt-1 block max-w-5xl text-[13px] leading-[1.5] text-black">
                Associate Members must be affiliated with an active Primary
                Member company. The Associate company must have a minimum of
                50% common ownership with the Primary Member company and must
                fall under the lower of the two membership fee categories.
              </span>
            </span>
          }
        />
        <div className="[&_label]:!font-normal">
          <SearchableSelectField
            id="primary-member-company"
            searchPlaceholder="Search Member Company"
            label="Please select the affiliated Primary Member company below."
            value={formState.association.primaryMemberCompanyId}
            className={`min-w-0 ${formState.association.primaryMemberCompanyId !== savedFormBaseline.association.primaryMemberCompanyId ? "member-field-dirty" : ""}`}
            onChange={(value) =>
              updateSection("association", { primaryMemberCompanyId: value })
            }
            options={primaryMemberCompanySelectOptions}
            placeholder={
              primaryCompaniesLoading
                ? "Loading member companies"
                : "Select the Primary Member Company"
            }
            helperText={
              primaryCompaniesError
                ? getUserFacingErrorText(
                    primaryCompaniesError,
                    buildStaffErrorFallback("load the member companies")
                  )
                : affiliatedPrimaryMemberError
                  ? getUserFacingErrorText(
                      affiliatedPrimaryMemberError,
                      buildStaffErrorFallback(
                        "load the billing details of the Primary Member company"
                      )
                    )
                  : !primaryCompaniesLoading &&
                      primaryMemberCompanySelectOptions.length === 0
                    ? "No members to choose from. Only Active members can be a Primary Member Company."
                    : undefined
            }
            disabled={
              isReadOnly ||
              primaryCompaniesLoading ||
              !formState.association.isAssociateCompanyMember ||
              primaryMemberCompanySelectOptions.length === 0
            }
          />
        </div>
      </SectionShell>

      <SectionShell
        id="affiliated-chamber-member-company" footer={renderSectionFooter(CmpMemberSectionEnum.AffiliatedChamber)}
        title="Affiliated Chamber Member Company"
        description="Select if this member is an Affiliated with another Chamber and enter the chamber name in the box below" descriptionLineHeight="normal"
        variant="design" contentGap={16}
      >
        <FormControlLabel
          sx={{ alignItems: "flex-start", margin: 0 }}
          control={
            <Checkbox
              checked={
                formState.affiliatedChamberMember.isAffiliatedChamberMember
              }
              disabled={isReadOnly}
              onChange={(event) =>
                updateSection("affiliatedChamberMember", {
                  isAffiliatedChamberMember: event.target.checked,
                })
              }
              sx={{ ...checkboxControlSx, ...changedChoiceControlSx(formState.affiliatedChamberMember.isAffiliatedChamberMember !== savedFormBaseline.affiliatedChamberMember.isAffiliatedChamberMember) }}
            />
          }
          label={
            <span>
              <span className="block text-sm font-bold text-gray-950">
                Affiliated Chamber Member
              </span>
              <span className="mt-1 block max-w-5xl text-[13px] leading-[1.5] text-black">
                This member is affiliated with the chamber below and therefore
                maybe eligible for special membership pricing with your
                {noun.singular} if enabled.
              </span>
            </span>
          }
        />
        <div className="[&_label]:!font-normal">
          <FormTextField
            id="affiliated-chamber-name"
            label="Enter the Affiliated Chamber of Commerce Name"
            value={formState.affiliatedChamberMember.chamberName}
            containerClassName={`mb-0 min-w-0 ${formState.affiliatedChamberMember.chamberName !== savedFormBaseline.affiliatedChamberMember.chamberName ? "member-field-dirty" : ""}`}
            onChange={(value) =>
              updateSection("affiliatedChamberMember", { chamberName: value })
            }
            placeholder="Chamber of Commerce Name"
            disabled={
              isReadOnly ||
              !formState.affiliatedChamberMember.isAffiliatedChamberMember
            }
          />
        </div>
      </SectionShell>

      <SectionShell
        id="affinity-program-partner" footer={renderSectionFooter(CmpMemberSectionEnum.AffinityPrograms)}
        title="Affinity Program Partner"
        description="Track the Affinity programs this member is enrolled in, including program identification numbers, enrollment dates, billing preferences, and participation status."
        variant="design" contentGap={18}
      >
        {affinityProgramsError && (
          <Alert severity="warning" className="mb-5">
            Unable to load Affinity Program Partners from {noun.Singular} Set Up. Try
            refreshing before adding a program.
          </Alert>
        )}
        {!affinityProgramsLoading &&
          !affinityProgramsError &&
          affinityProgramOptions.length === 0 && (
            <Alert severity="info" className="mb-5">
              {`No Affinity Program Partners are set up yet. To add one, go to ${noun.Singular} Set Up > Affinity Program Set Up.`}
            </Alert>
          )}
        <div className="grid grid-cols-1 gap-x-4 gap-y-[5px] lg:grid-cols-[minmax(0,1.1fr)_1fr_1fr_1fr] lg:grid-rows-[auto_auto_auto]">
          <AffinityDraftField label="Affinity Program Partner" description="Select from the available programs">
            <SearchableSelectField
              id="affinity-program"
              label="Affinity Program Partner"
              value={affinityDraft.affinityProgramPartnerId}
              className={affinityDraft.affinityProgramPartnerId ? "member-field-dirty" : ""}
              onChange={(value) => {
                setAffinityEnrollmentError("");
                setAffinityDraft((current) => ({ ...current, affinityProgramPartnerId: value }));
              }}
              options={affinityProgramOptions.map((program) => ({ value: program.id, label: program.companyName }))}
              placeholder={affinityProgramsLoading ? "Loading affinity programs" : "Select an Affinity program"}
              searchPlaceholder="Search Programs"
              error={affinityEnrollmentError}
              disabled={isReadOnly || affinityProgramsLoading}
            />
          </AffinityDraftField>
          <AffinityDraftField label="ID Number" description="Enter the program identification number.">
            <FormTextField
              id="affinity-id-number"
              label="ID Number"
              value={affinityDraft.idNumber}
              containerClassName={affinityDraft.idNumber ? "member-field-dirty" : ""}
              onChange={(value) => setAffinityDraft((current) => ({ ...current, idNumber: value }))}
              placeholder="Enter ID number"
              disabled={isReadOnly}
            />
          </AffinityDraftField>
          <AffinityDraftField label="Start Date" description="Enter the program participation started.">
            <div className={affinityDraft.startDate ? "member-field-dirty" : ""}>
              <FormCalendarDateField
                id="affinity-start-date"
                label="Start Date"
                value={affinityDraft.startDate}
                onChange={(value) => setAffinityDraft((current) => ({ ...current, startDate: value }))}
                placeholder="Select Date"
                iconPosition="right"
                disabled={isReadOnly}
              />
            </div>
          </AffinityDraftField>
          <AffinityDraftField label="Billing Option" description="How this program will be billed to members">
            <SearchableSelectField
              id="affinity-billing-option"
              label="Billing Option"
              value={affinityDraft.billingOption}
              className={affinityDraft.billingOption ? "member-field-dirty" : ""}
              onChange={(value) => setAffinityDraft((current) => ({ ...current, billingOption: value }))}
              options={affinityBillingOptions.map((option) => ({ value: option, label: option }))}
              placeholder="Select billing option"
              searchPlaceholder="Search"
              disabled={isReadOnly}
            />
          </AffinityDraftField>
        </div>
        <FormTextField
          id="affinity-notes"
          label={
            <>
              Notes <span className="font-normal">(Optional)</span>
            </>
          }
          description="Additional information about this program."
          value={affinityDraft.notes} containerClassName={affinityDraft.notes.trim() ? "member-field-dirty" : ""}
          onChange={(value) =>
            setAffinityDraft((current) => ({
              ...current,
              notes: value,
            }))
          }
          placeholder="Enter any notes about this program"
          multiline
          rows={3}
          disabled={isReadOnly}
        />
        <div className="flex flex-wrap items-center justify-between gap-5">
          <p className="text-sm leading-[1.55] text-black">
            Click <strong>Save</strong> to display details below in the
            Enrolled Affinity Programs table where you can update status,
            billing options and notes.
          </p>
          <Button
            type="button"
            variant="contained"
            disabled={
              isReadOnly ||
              affinityProgramsLoading ||
              affinityProgramOptions.length === 0
            }
            onClick={handleStartAffinityEnrollmentSave}
            sx={{ ...wizardGoldButtonSx, borderRadius: "8px", fontSize: "14px", fontWeight: 600, padding: "11px 34px" }}
          >
            Save
          </Button>
        </div>


      <div><h4 className="mt-1.5 text-[17px] font-bold">Enrolled Affinity Programs</h4><p className="mb-3 mt-1.5 text-sm leading-[1.55]">Select edit to update and review all Affinity programs this member is currently enrolled in. {noun.Plural} should adjust start date to reflect actual start date for this member if previously enrolled.</p>
        {formState.affinityProgramEnrollments.length === 0 ? (
          <div className="rounded-lg border border-dashed border-[#ECECEC] bg-gray-50 px-5 py-6 text-sm text-black">
            No Affinity Programs have been enrolled for this member yet.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-[10px] border border-[#ECECEC]">
            <table className="block min-w-[900px] w-full text-left text-[13px] text-black">
              <thead className="block">
                <tr className={`${affinityTableColumns} bg-[#F7F7F8] text-[13px] font-bold text-gray-950`}>
                  <th className="px-3.5 py-3">Affinity Program Partner</th>
                  <th className="px-3.5 py-3">ID Number</th>
                  <th className="px-3.5 py-3">Start Date</th>
                  <th className="px-3.5 py-3">Billing Options</th>
                  <th className="px-3.5 py-3">Status</th>
                  <th className="px-3.5 py-3">Notes</th>
                  <th className="px-3.5 py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="block">
                {formState.affinityProgramEnrollments.map((enrollment) => {
                  const baseline = savedFormBaseline.affinityProgramEnrollments.find((saved) => saved.id === enrollment.id);
                  const fieldStyle = (field: keyof AffinityProgramEnrollment) => enrollment[field] !== (baseline?.[field] || "") ? { outline: "2px solid #E1A731", outlineOffset: "-1px" } : undefined;
                  return (
                  <tr
                    key={enrollment.id}
                    className={`${affinityTableColumns} border-t border-[#ECECEC] align-top`}
                  >
                    <td className="px-3.5 py-[13px] font-semibold" style={fieldStyle("affinityProgramPartnerName")}>
                      {enrollment.affinityProgramPartnerName}
                    </td>
                    <td className="px-3.5 py-[13px]" style={fieldStyle("idNumber")}>{enrollment.idNumber || "-"}</td>
                    <td className="px-3.5 py-[13px]" style={fieldStyle("startDate")}>
                      {formatDateInputValueLong(enrollment.startDate)}
                    </td>
                    <td className="px-3.5 py-[13px]" style={fieldStyle("billingOption")}>
                      {enrollment.billingOption
                        ? renderAffinityBillingOptionLabel(
                            enrollment.billingOption
                          )
                        : "-"}
                    </td>
                    <td className="px-3.5 py-[13px]" style={fieldStyle("status")}>
                      <AffinityEnrollmentStatusIndicator
                        status={enrollment.status}
                      />
                    </td>
                    <td className="px-3.5 py-[13px]" style={fieldStyle("notes")}>{enrollment.notes || "-"}</td>
                    <td className="px-3.5 py-[13px] text-center">
                      <Button
                        type="button"
                        variant="contained"
                        size="small"
                        startIcon={<EditOutlinedIcon sx={{ fontSize: "12px !important" }} />}
                        disabled={isReadOnly}
                        onClick={() => setEditingAffinityEnrollment(enrollment)}
                        sx={{ ...wizardGoldButtonSx, borderRadius: "6px", fontSize: "12px", fontWeight: 600, padding: "7px 14px", minWidth: 0, "& .MuiButton-startIcon": { marginRight: "6px", marginLeft: 0 } }}
                      >
                        Edit
                      </Button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </SectionShell>
      <SectionShell id="member-seo-tags" title="SEO and AIO Tags" description="Add tags that help this member's directory listing get found by search engines and AI tools. Type a tag and press Enter, or select from the suggestions." variant="design" contentGap={16} footer={renderSectionFooter(CmpMemberSectionEnum.SeoTags)}>
        <TagInputField suggestions={updateMemberSeoTagSuggestions} id="member-seo-tags-input" value={formState.directory.seoTags} onChange={(seoTags) => updateSection("directory", { seoTags })} disabled={isReadOnly} helperFontSize={14} dirty={isMemberSectionDirty(formState, savedFormBaseline, CmpMemberSectionEnum.SeoTags)} error={memberFieldErrors.seoTags} />
      </SectionShell>

      <GovernanceInvolvementCard
        id="member-governance-involvement"
        governance={governanceSettingsData?.getChamberMemberProSettings.governance}
        selected={formState.insights.governanceGroupIds}
        onChange={(governanceGroupIds) => updateSection("insights", { governanceGroupIds })}
        disabled={isReadOnly || savingSection === CmpMemberSectionEnum.MemberInsights || profileSaveInFlight}
        loading={governanceSettingsLoading}
        error={
                  governanceSettingsError
                    ? getUserFacingErrorText(
                        governanceSettingsError,
                        buildStaffErrorFallback("load the governance groups")
                      )
                    : undefined
                }
        footer={renderSectionFooter(CmpMemberSectionEnum.MemberInsights)}
      />
      <MemberActivitySections chamberId={chamberId} memberId={detail?.member.id || ""} timeZone={chamberSettings?.setup.timeZone} memberUpdatedAtUtcMilli={memberUpdatedAtUtcMilli}>
        {(emails) => (
      <MemberStaffNotesCard chamberId={chamberId} communicationLog emails={emails} notes={localStaffNotes || detail?.staffNotes || []} draftComment={staffNoteDraft} onDraftCommentChange={setStaffNoteDraft} onAdd={saveStaffNote} authorDisplayName={[authenticatedUser?.firstName, authenticatedUser?.lastName].map((part) => part?.trim()).filter(Boolean).join(" ")} authorUserId={authenticatedUser?.id || ""} chamberTimeZone={chamberSettings?.setup.timeZone} loading={addingStaffNote} error={staffNoteError} readOnly={isReadOnly} highlightDraftChanges />
        )}
      </MemberActivitySections>
    </div>
  );

  const renderBillingFields = (renewalOptions?: { previewTotal?: string; disabled: boolean }) => (
    <MemberBillingStep
      appearance="design"
      affiliatedPayerFromPrimaryMember
      renewal={renewalOptions ? { isTierPricing: membershipModels.some(isTierMembershipModel), previewTotal: renewalOptions.previewTotal } : undefined}
      onUpdateCreditCard={renewalOptions ? () => setCreditCardDialogOpen(true) : undefined}
      addOnsReadOnly
      value={membershipBillingValue}
      membershipOptions={showSavedBillingSnapshot && persistedMembership
        ? membershipOptions.map((option) => option.value === persistedMembership.membershipTypeId
          ? { ...option, label: persistedMembership.membershipTypeName }
          : option)
        : membershipOptions}
      selectedMembershipSummary={{
        code:
          showSavedBillingSnapshot
            ? savedBillingSnapshot?.pricingKey || "-"
            : baseMembershipPrice.code !== "-"
            ? baseMembershipPrice.code
            : persistedPricingSnapshot?.pricingKey || "-",
        description:
          selectedMembershipModel?.description ||
          (isShowingPersistedUnavailableMembership
            ? `This membership type is no longer offered in ${noun.Singular} Set Up. It stays on this member for reference. To change it, go to Step 5, Membership Renewal.`
            : !membershipBillingValue.membershipTypeId
              ? "No membership is currently assigned to this member."
              : "Investment amount is automatically populated based on the membership selected."),
        amountLabel: showSavedBillingSnapshot
          ? formatSavedBillingAmount(savedBillingSnapshot?.baseAmountInCents)
          : renewalOptions && !selectedMembershipModel && membershipBillingValue.membershipTypeId
            ? "Amount unavailable"
            : formatCurrency(effectiveMembershipAmountInCents),
      }}
      paymentScheduleOptions={showSavedBillingSnapshot
        ? paymentScheduleOptions.map((option) => ({ ...option, label: option.label.replace(" (Current - unavailable)", "") }))
        : paymentScheduleOptions}
      paymentMethodOptions={showSavedBillingSnapshot
        ? allowedMembershipPaymentMethodOptions.map((option) => option.value === membershipBillingValue.paymentMethod
          ? { ...option, label: persistedMembership?.paymentChoice.manualPaymentMethodLabel || (option.value === "creditCard" ? "Credit Card" : option.value === "invoice" ? "Invoice Me" : option.label.replace(" (Current - unavailable)", "")) }
          : option)
        : allowedMembershipPaymentMethodOptions}
      paymentMethodTitle="Membership Payment Method"
      paymentMethodDescription={showSavedBillingSnapshot
        ? "The saved payment method for this member's membership invoice."
        : `Select how this member's replacement or future renewal membership invoice will be paid. Available methods come from ${noun.Singular} Set Up.`}
      billingOptions={visibleMembershipBillingOptions}
      addOns={displayedMembershipAddOns.map((addOn) => ({
        id: addOn.id,
        code: addOn.code,
        name: addOn.name,
        description: addOn.description,
        amountLabel:
          showSavedBillingSnapshot && selectedAdditionalCostIds.has(addOn.id)
            ? formatSavedBillingAmount(savedBillingSnapshot?.addOnAmountsInCents.get(addOn.id))
            : addOn.annualAmountInDollars === null
            ? "Amount unavailable"
            : formatCurrency(Math.round(addOn.annualAmountInDollars * 100)),
      }))}
      addOnsTotalLabel={
        showSavedBillingSnapshot
          ? formatSavedBillingAmount(savedBillingSnapshot?.addOnSubtotalInCents)
          : selectedAddOnAmountUnavailable
          ? "Amount unavailable"
          : formatCurrency(selectedAddOnTotalInCents)
      }
      invoiceTotalLabel={showSavedBillingSnapshot
        ? formatSavedBillingAmount(savedBillingSnapshot?.membershipInvoiceTotalInCents)
        : formatCurrency(invoiceTotalInCents)}
      isMonthlySchedule={isMonthlySchedule}
      membershipInvoiceTotalLabel={showSavedBillingSnapshot
        ? formatSavedBillingAmount(savedBillingSnapshot?.membershipInvoiceTotalInCents)
        : formatCurrency(membershipInvoiceTotalInCents)}
      addOnInvoiceTotalLabel={showSavedBillingSnapshot
        ? formatSavedBillingAmount(savedBillingSnapshot?.addOnInvoiceTotalInCents)
        : formatCurrency(addOnInvoiceTotalInCents)}
      showPaymentSchedule={showSavedBillingSnapshot ? !!persistedMembership?.paymentSchedule : shouldShowPaymentSchedule}
      showPaymentMethod={
        membershipBillingValue.billingResponsibility === "memberPays"
      }
      paymentScheduleDescription={
        !showSavedBillingSnapshot && hasUnavailableMonthlySchedule
          ? `The saved Monthly schedule is unavailable because credit card payments are disabled for this ${noun.singular}. Yearly will be used for this membership change.`
          : undefined
      }
      monthlyPaymentsAvailable={showSavedBillingSnapshot || creditCardPaymentsReady}
      membershipDiscountEnabled={membershipDiscountEnabled}
      membershipDiscountAvailable={
        showSavedBillingSnapshot || !!chamberSettings?.paymentsAndTaxes.membershipDiscount.enabled
      }
      autoRenewAvailable={showSavedBillingSnapshot || !!chamberSettings?.membership.allowAutoRenewal}
      scheduledMembershipChange={
        !renewalOptions && pendingMembershipChange
          ? {
              statusLabel:
                pendingMembershipChange.status === "ACTION_REQUIRED"
                  ? "Action required"
                  : "Pending",
              currentMembershipName:
                pendingMembershipChange.oldMembership.membershipTypeName,
              scheduledMembershipName:
                pendingMembershipChange.newMembership.membershipTypeName,
              effectiveDateLabel: formatUtcCalendarDate(
                pendingMembershipChange.effectiveAtUtcMilli
              ),
              actionRequiredReason:
                pendingMembershipChange.actionRequiredReason || undefined,
            }
          : undefined
      }
      membershipPlaceholder={renewalOptions ? "Select Membership Type" : membershipPresentation.placeholder}
      disabled={renewalOptions ? renewalOptions.disabled || isReadOnly || chamberSettingsLoading : true}
      errors={showSavedBillingSnapshot ? {} : {
        ...membershipAdjustmentErrors,
        paymentSchedule:
          getMemberPaymentScheduleDisplayError({
            formError: membershipAdjustmentErrors.paymentSchedule,
            resolution: paymentScheduleResolution,
          }, noun),
      }}
      thirdPartyBillingErrors={getInvoicePartyFieldErrors(
        membershipAdjustmentErrors,
        "thirdPartyBillingSnapshot."
      )}
      onFullTimeEmployeesChange={(value) =>
        updateMembershipAdjustmentDraft({ fullTimeEmployees: value })
      }
      onPartTimeEmployeesChange={(value) =>
        updateMembershipAdjustmentDraft({ partTimeEmployees: value })
      }
      onMembershipTypeChange={(value) =>
        updateMembershipAdjustmentDraft({
          membershipTypeId: value,
        })
      }
      onPaymentScheduleChange={(value) => {
        setMembershipAdjustmentErrors((current) => {
          const nextErrors = { ...current };
          delete nextErrors.paymentSchedule;
          return nextErrors;
        });
        updateMembershipAdjustmentDraft({
          paymentSchedule: value,
        });
      }}
      onPaymentMethodChange={(value) =>
        updateMembershipAdjustmentDraft({
          paymentMethod: value as PaymentMethodType,
        })
      }
      onMembershipDiscountChange={(membershipDiscount) =>
        updateMembershipAdjustmentDraft({
          membershipDiscount: {
            ...membershipBillingValue.membershipDiscount,
            ...membershipDiscount,
          },
        })
      }
      onAdditionalCostToggle={() => undefined}
      onBillingResponsibilityChange={(value) => {
        const billingResponsibility = value as BillingResponsibilityOption;

        // Design (13) Step 5: the affiliated option is chosen only through its
        // pop-up; without a Step 3 Primary Member company it stays unselected.
        if (billingResponsibility === "affiliatedPrimaryCompany") {
          setAffiliatePrompt(
            formState.association.isAssociateCompanyMember &&
              formState.association.primaryMemberCompanyId
              ? "confirm"
              : "none"
          );
          return;
        }

        updateMembershipAdjustmentDraft({ billingResponsibility });
      }}
      onThirdPartyBillingSnapshotChange={(snapshot) => {
        setMembershipAdjustmentErrors((currentErrors) =>
          Object.fromEntries(
            Object.entries(currentErrors).filter(
              ([key]) => !key.startsWith("thirdPartyBillingSnapshot.")
            )
          )
        );
        updateMembershipAdjustmentDraft({
          thirdPartyBillingSnapshot: snapshot,
          thirdPartyPayerName: snapshot.customerName,
        });
      }}
      onAutoRenewChange={(checked) =>
        updateMembershipAdjustmentDraft({ autoRenew: checked })
      }
      onEmailConsentChange={(checked) =>
        updateSection("membership", {
          emailCommunicationConsentConfirmed: checked,
        })
      }
    />
  );

  const renderStep4 = () => (
    <><div className="mb-6"><h3 className="text-[22px] font-semibold leading-[normal]">Membership Details – <span className="text-[#FF0000]">View Only</span></h3><p className="mt-1.5 text-sm">This section is view only and cannot be edited. To make changes to this member's membership, go to Step 5: Membership Renewal, and update fields as required.</p></div>
    {renderBillingFields()}</>
  );

  const saveRenewalConsent = async () => {
    if (!detail) throw new UserFacingError("The member details are not loaded yet. Refresh the page and try again.");
    const consent = currentFormRef.current.membership.emailCommunicationConsentConfirmed;
    // Consent is stored on the membership. A member with no saved membership
    // gets it from the renewal invoice command, which creates the membership.
    if (!detail.member.cmpExtension?.membership ||
      consent === savedBaselineRef.current.membership.emailCommunicationConsentConfirmed) {
      return { detail: getLatestMemberRenewalDetail(detail, confirmedRenewalDetailRef.current), consentSaved: false };
    }
    const baseline = savedBaselineRef.current;
    const submitted = mergeMemberSection(baseline, {
      ...baseline,
      membership: { ...baseline.membership, emailCommunicationConsentConfirmed: consent },
    }, CmpMemberSectionEnum.MemberInsights);
    const confirmedDetail = await confirmPendingIdentityReadback() || getLatestMemberRenewalDetail(detail, confirmedRenewalDetailRef.current);
    const response = await updateCmpMember({ variables: { input: {
      chamberId,
      memberId: confirmedDetail.member.id,
      section: CmpMemberSectionEnum.MemberInsights,
      expectedUpdatedAtUtcMilli: savedMemberRevisionRef.current ?? confirmedDetail.member.updatedAtUtcMilli,
      data: buildMemberInputFromForm({ currentStatus, detail: confirmedDetail, formState: submitted }),
    } } });
    const result = response.data?.updateCmpMember;
    if (!result?.updateSuccessful || !result.detail || !isCurrentMemberReadback(confirmedDetail, result.detail)) {
      throw new UserFacingError("We could not confirm that the email consent was saved. Refresh the page before you try again.");
    }
    const nextForm = mapDetailToFormState(result.detail, {
      configuredInsurancePayerName: chamberGroupInsuranceCompanyName,
      insuranceOptionAvailable: chamberGroupInsuranceOptionAvailable,
    });
    const savedConsent = nextForm.membership.emailCommunicationConsentConfirmed;
    if (savedConsent !== consent) throw new UserFacingError("We could not confirm that the email consent was saved. Refresh the page before you try again.");
    confirmedRenewalDetailRef.current = result.detail;
    savedMemberRevisionRef.current = result.detail.member.updatedAtUtcMilli ?? null;
    savedBaselineRef.current = { ...savedBaselineRef.current, membership: { ...savedBaselineRef.current.membership, emailCommunicationConsentConfirmed: savedConsent } };
    setFormStateWithBaseline((current) => ({
      current: applySavedMemberSection(current.current, submitted, nextForm, CmpMemberSectionEnum.MemberInsights),
      savedBaseline: { ...current.savedBaseline, membership: { ...current.savedBaseline.membership, emailCommunicationConsentConfirmed: savedConsent } },
    }));
    return { detail: result.detail, consentSaved: true };
  };

  const renderStep5 = () => renewalDetail ? (
    <MembershipRenewalStep
      detail={renewalDetail}
      chamber={chamber}
      timeZone={chamberSettings?.setup.timeZone}
      taxLabel={[
        chamberSettings?.paymentsAndTaxes.invoiceTaxSettings.membershipInvoices.firstTaxRateLabel,
        chamberSettings?.paymentsAndTaxes.invoiceTaxSettings.membershipInvoices.secondTaxRateLabel,
      ].filter(Boolean).join(" + ") || "Tax"}
      taxRegistrationNumber={chamberSettings?.paymentsAndTaxes.taxRegistrationNumber}
      manualPaymentMethods={enabledManualPaymentMethods}
      draftFingerprint={JSON.stringify({ membership: membershipBillingValue, revision: renewalDetail.member.updatedAtUtcMilli })}
      formError={membershipAdjustmentError}
      autoRenewSelected={membershipBillingValue.autoRenew}
      getMembershipInput={() => {
        const prepared = prepareMembershipAdjustment(false);
        return prepared ? buildMembershipMutationInput(prepared.adjustmentInput).membership : null;
      }}
      renderFields={(previewTotal, disabled) => renderBillingFields({ previewTotal, disabled })}
      createOperationId={() => createLocalId("member-renewal")}
      onBusyChange={setMembershipAdjustmentSubmitting}
      onBeforePersist={saveRenewalConsent}
      onCompleted={async (nextDetail) => {
        confirmedRenewalDetailRef.current = nextDetail;
        savedMemberRevisionRef.current = nextDetail.member.updatedAtUtcMilli ?? savedMemberRevisionRef.current;
        applyMembershipAdjustmentResult(nextDetail);
        const next = getRenewalFormState(nextDetail);
        setRenewalFormBaseline(next);
        setMembershipAdjustmentDraft(cloneMembershipFormState(next));
        setFormStateWithBaseline((current) => ({
          current: { ...current.current, membership: { ...current.current.membership, emailCommunicationConsentConfirmed: next.emailCommunicationConsentConfirmed } },
          savedBaseline: { ...current.savedBaseline, membership: { ...current.savedBaseline.membership, emailCommunicationConsentConfirmed: next.emailCommunicationConsentConfirmed } },
        }));
        await refetchDetail();
      }}
    />
  ) : null;

  const renderActiveStep = () => {
    if (activeStep === 1) {
      return renderStep1();
    }

    if (activeStep === 2) {
      return renderStep2();
    }

    if (activeStep === 3) {
      return renderStep3();
    }

    return activeStep === 5 ? renderStep5() : renderStep4();
  };

  const credentialEmailPreview = resolveMemberCredentialEmailPreview({
    data: previewEmailData?.getSampleLoginInfoEmail,
    loading: previewEmailLoading,
    failed: Boolean(previewEmailError),
    blocked: !previewEmailMode || Boolean(getCredentialActionBlock()),
  });

  const renderConfirmationDialog = () => {
    const modal = confirmationModal;
    if (!modal) return null;
    const statusReasonKind = getMemberStatusReasonKind(modal);
    const company = member?.name || formState.directory.companyName;
    const copy: Record<NonNullable<ConfirmationModal>, { title: string; body: React.ReactNode; action: string }> = {
      resetPassword: { title: "Reset Password", body: <>Are you sure you want to reset the member dashboard password for <strong>{memberAdminName}</strong>? The password is reset for this contact only, and they will show as not logged in until they sign in with their new password.</>, action: "Reset" },
      approveBoard: { title: "Approve Member", body: <>Are you sure you want to provide {getCmpBoardWording(noun).approvalByAndOr} approval for <strong>{company}</strong>?</>, action: "Yes, Approve" },
      suspend: { title: "Suspend Member", body: statusModel
        ? <>Are you sure you want to suspend <strong>{company}</strong>? They will lose all member privileges, directory listings and access to the Member Dashboard until they are unsuspended.</>
        : <>Are you sure you want to suspend <strong>{company}</strong>? They will lose access to the member dashboard and member benefits until they are unsuspended.</>, action: "Yes, Suspend" },
      // CD:15711-15712 (IF11 MB8 Phase B).
      cancelRenewal: { title: "Cancel Member Renewal", body: <>Are you sure you want to cancel the membership renewal for <strong>{company}</strong>? The membership will be cancelled on the renewal date, {statusRenewalDateLabel}. All member privileges, directory listing and Member Dashboard access will be disabled. No Renewal invoice will be Sent.{detail?.memberStatus === CmpMemberStatusEnum.Suspended
        // IF11 Q35 (design way): Cancel on a Suspended member unsuspends it first (backend activates, then sets the cancel).
        ? <> This member is suspended. Cancelling the renewal unsuspends the member now, so member access returns until the renewal date.</>
        : null}</>, action: "Cancel Renewal" },
      uncancelRenewal: { title: "Uncancel Member Renewal", body: <>Are you sure you want to uncancel the membership renewal for <strong>{company}</strong>? The member returns to Active status{memberNoCharge ? "." : <> and the renewal invoice will be sent automatically before {statusRenewalDateLabel}.</>}</>, action: "Uncancel Renewal" },
      unsuspend: { title: "Unsuspend Member", body: <>Are you sure you want to unsuspend <strong>{company}</strong>? Member access and benefits will be restored.</>, action: "Yes, Unsuspend" },
      delete: { title: "Delete Member", body: <DeleteMemberMessage company={company} statusModel={statusModel} />, action: "Delete" },
      activate: { title: "Activate Member", body: "This member has paid and, if needed, been approved. Activating the member turns on their member benefits and, if set to show, their directory listing. Do you want to activate this member?", action: "Activate" },
      restartApplication: { title: "Submit New Company Membership Application", body: "This will open Create Member with this past member's information prefilled so a new membership application can be submitted.", action: "Continue" },
    };
    const deleteAutoRenewWarning = modal === "delete" && shouldWarnAutoRenewOnDelete({
      autoRenewEnabled: !!(member?.cmpExtension?.membership?.paymentChoice?.autoRenew && chamberSettings?.membership.allowAutoRenewal),
      lifecycleStatus: detail?.lifecycleStatus,
      memberStatus: detail?.memberStatus,
    });
    return <DesignDialog open onClose={() => setConfirmationModal(null)} busy={actionLoading || credentialOperationLoading} title={copy[modal].title} message={copy[modal].body}
      beforeMessage={deleteAutoRenewWarning ? <AutoRenewDeleteWarning company={company} /> : undefined}
      icon={modal === "resetPassword" ? "lock" : modal === "suspend" ? "pause" : modal === "delete" ? "trash" : "question"}
      iconColor={modal === "approveBoard" ? "#2196F3" : modal === "unsuspend" ? "#12B981" : modal === "cancelRenewal" || modal === "uncancelRenewal" ? "#E1A731" : undefined}
      actions={<><DesignDialogButton tone={modal === "delete" ? "red" : modal === "resetPassword" ? "dark" : modal === "unsuspend" ? "green" : modal === "approveBoard" ? "blue" : "gold"} style={modal === "suspend" ? { backgroundColor: "#9E9E9E" } : undefined} disabled={actionLoading || credentialOperationLoading} onClick={() => void confirmAction()}>{copy[modal].action}</DesignDialogButton>{modal !== "resetPassword" && <DesignDialogButton tone="dark" disabled={actionLoading} onClick={() => setConfirmationModal(null)}>Cancel</DesignDialogButton>}</>}
    >{statusReasonKind && <MemberStatusReasonField kind={statusReasonKind} value={statusReason} error={statusReasonError} disabled={actionLoading}
        onChange={(value) => { setStatusReason(value); setStatusReasonError(false); }} />}
      {(modal === "resetPassword" ? credentialActionError : memberActionError) && <p role="alert" className="mt-3 text-sm text-red-600">{modal === "resetPassword" ? credentialActionError : memberActionError}</p>}</DesignDialog>;
  };

  const retainSavingMemberForm = (profileSaveInFlight || membershipAdjustmentSubmitting) &&
    member?.id === memberIdForInput && member.chamberId === chamberId;
  if (!chamberId || (detailLoading && !retainSavingMemberForm)) {
    return (
      <div className="flex min-h-[320px] w-full items-center justify-center">
        <div className="flex items-center gap-3 text-black">
          <CircularProgress size={24} />
          <span>Loading member details...</span>
        </div>
      </div>
    );
  }

  if (detailError || !detail || !member) {
    return (
      <div className="w-full pb-10">
        <Button
          type="button"
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(RoutePaths.chamberAdmin_members_manage)}
          sx={{ color: "#111827", fontWeight: 700 }}
        >
          Back To Manage Members
        </Button>
        <Alert severity="error" className="mt-5">
          {getUserFacingErrorText(detailError, buildStaffErrorFallback("load this member"))}
        </Alert>
      </div>
    );
  }

  return (
    <MemberDesignSurface className="w-full pb-10">
      <style>{`
        .member-update-page > div:first-child > div { gap: 24px; flex-wrap: wrap; padding-bottom: 16px; border-bottom: 1px solid #ECECEC; }
        .member-update-page > div:first-child > div > div:first-child { min-width: 280px; flex: 1; }
        .member-update-page > div:last-child { padding-top: 24px; }
        .member-update-page .member-update-description > .text-xs { margin-top: 6px; }
        .member-update-page .member-update-description > .text-xs > span:first-child { font-size: 14px; line-height: normal; color: #727272; }
        .member-update-page .member-update-restart .MuiButton-root { min-width: 0; max-width: 100%; width: fit-content; font-size: 15px; font-weight: 600; line-height: normal; padding: 14px 40px; }
      `}</style>
      <UnsavedChangesPrompt
        when={isDirty || membershipAdjustmentHasChanges}
        onSave={
          membershipAdjustmentHasChanges ||
          additionalContactDraftDirty ||
          affinityDraftHasChanges ||
          editingAffinityEnrollmentHasChanges
            ? undefined
            : saveUpdate
        }
        saving={
          actionLoading ||
          updatingMember ||
          membershipAdjustmentSubmitting
        }
      />

      {statusMessage && (
        <div className="mb-5 w-full">
          <Alert
            severity={statusMessage.severity}
            onClose={() => setStatusMessage(null)}
          >
            {statusMessage.message}
          </Alert>
        </div>
      )}

      {detail.renewalBlockedReason ? (
        <div className="mb-5 w-full">
          <Alert severity="error">
            <strong>Renewal blocked:</strong> {detail.renewalBlockedReason}
          </Alert>
        </div>
      ) : detail.renewalBillingWarning ? (
        <div className="mb-5 w-full">
          <Alert severity="warning">
            <strong>Billing address missing:</strong>{" "}
            {detail.renewalBillingWarning}
          </Alert>
        </div>
      ) : null}

      {/* Not in design (Oct 24 2026, Moeen): the automatic login at activation
          stopped without sending anything; the backend gives the next step. */}
      {detail.dashboardAccessIssue ? (
        <div className="mb-5 w-full">
          <Alert severity="warning">{detail.dashboardAccessIssue}</Alert>
        </div>
      ) : null}

      {renderStatusPanel()}

      <MemberStepProgress
        activeStep={activeStep}
        ariaLabel="Update member steps"
        steps={updateMemberWorkflowSteps}
        desktopGridColsClassName="lg:grid-cols-[1.2fr_1fr_1fr_1fr_1.2fr]"
        onStepClick={goToStep}
      />

      <main className="w-full pt-[24px]">
        <h2 id={`update-member-step-${activeStep}`} className="sr-only">
          {updateMemberWorkflowSteps[activeStep - 1].title}
        </h2>
        <section
          aria-labelledby={`update-member-step-${activeStep}`}
          className="space-y-6"
        >
          {renderActiveStep()}
        </section>

        <StepFooter
          activeStep={activeStep}
          error={footerError}
          isActive={currentStatus === "Active"}
          loading={
            profileSaveInFlight ||
            updatingMember ||
            actionLoading ||
            membershipAdjustmentSubmitting
          }
          onBack={() => activeStep === 1 ? navigate(RoutePaths.chamberAdmin_members_manage) : goToStep(activeStep - 1)}
          onNext={handleNextStep}
        />
      </main>

      <MissingRequiredFieldsDialog open={missingFieldRows.length > 0} rows={missingFieldRows} onClose={() => setMissingFieldRows([])} />
      <DesignDialog open={pendingStep !== null} onClose={() => setPendingStep(null)} variant="unsaved" icon="warning" title="UNSAVED CHANGES" message="Save changes before leaving?" busy={profileSaveInFlight || addingStaffNote} actions={<><DesignDialogButton disabled={profileSaveInFlight || addingStaffNote || openDraftsRef.current} onClick={async () => { if (await saveUpdate() && pendingStep !== null) { navigateToStep(pendingStep); setPendingStep(null); } }}>Save Changes</DesignDialogButton><DesignDialogButton tone="dark" disabled={profileSaveInFlight || addingStaffNote} onClick={() => { discardOpenDrafts(); if (pendingStep !== null) navigateToStep(pendingStep); setPendingStep(null); }}>Leave</DesignDialogButton></>} />
      <DesignDialog open={renewalUnavailable} onClose={() => setRenewalUnavailable(false)} icon="warning" iconColor="#E1A731" title="Membership Renewal Not Available" message={currentStatus === "Suspended" ? "This member is currently suspended. The Membership Renewal step will become available once the member is unsuspended and active again." : currentStatus === "Past" ? "This member is a past member and no longer active. The Membership Renewal step is only available for active members." : "This member is still in pending status and is not an active member yet. The Membership Renewal step will become available once the member is approved and active."} actions={<DesignDialogButton onClick={() => setRenewalUnavailable(false)}>OK</DesignDialogButton>} />
      <IdaGenerateDialog open={idaDialogOpen} value={idaDescription} onChange={setIdaDescription} onUse={() => { updateSection("directory", { companyDescription: idaDescription.slice(0, 2000) }); setIdaDialogOpen(false); }} onRegenerate={() => void generateMemberContent(IdaGenerateMode.CompanyDescription)} onClose={() => setIdaDialogOpen(false)} loading={idaMode === IdaGenerateMode.CompanyDescription} error={idaError} />
      {renderConfirmationDialog()}

      <MemberLoginEmailConflictDialog
        open={Boolean(memberLoginCollision)}
        loading={memberLoginCollisionLoading}
        checkedLoginEmail={memberLoginCollision?.checkedLoginEmail}
        suggestedLoginEmail={memberLoginCollision?.suggestedLoginEmail || ""}
        onCancel={clearPendingMemberLoginCollision}
        onUseAlternative={() => {
          void handleUseAlternativeMemberLogin();
        }}
      />

      <DesignDialog open={manualPaymentDialogOpen} busy={recordingMemberPayment} onClose={() => { setManualPaymentDialogOpen(false); setManualPaymentError(""); }} size="wide" title="Record Member Payment" message={`Record a payment that the ${noun.singular} received another way, such as a cheque or an e-transfer. The member becomes Active when the payment and any needed approval are complete.`} actions={<><DesignDialogButton tone="dark" disabled={recordingMemberPayment} onClick={() => { setManualPaymentDialogOpen(false); setManualPaymentError(""); }}>Cancel</DesignDialogButton><DesignDialogButton disabled={recordingMemberPayment} onClick={() => void handleRecordMemberManualPayment()}>{recordingMemberPayment ? "Recording Payment..." : "Confirm and Record Payment"}</DesignDialogButton></>}>
        <div className="mt-4 w-full space-y-5 text-left">
          <TextField
            label="Invoice"
            value={detail.currentInvoice?.invoiceNumber || ""}
            fullWidth
            disabled
          />
          <TextField
            label="Payment Amount"
            value={formatCurrency(
              detail.currentInvoice
                ? getPendingMemberPaymentAmountInCents({
                    amountDueInCents: detail.currentInvoice.amountDueInCents,
                    nextInstallmentAmountInCents:
                      detail.currentInvoice.paymentPlan
                        ?.nextInstallmentAmountInCents,
                  })
                : 0
            )}
            fullWidth
            disabled
          />
          {detail.currentInvoice?.paymentChoice.type !==
          CmpPaymentChoiceTypeEnum.ThirdParty ? (
            <TextField
              select
              SelectProps={{ native: true }}
              label="Payment Method"
              value={manualPaymentDraft.manualPaymentMethodId}
              onChange={(event) =>
                setManualPaymentDraft((current) => ({
                  ...current,
                  manualPaymentMethodId: event.target.value,
                }))
              }
              fullWidth
              disabled={recordingMemberPayment}
            >
              {enabledManualPaymentMethods.map((method) => (
                <option key={method.id} value={method.id}>
                  {method.label}
                </option>
              ))}
            </TextField>
          ) : null}
          <div>
            <label
              htmlFor="member-manual-payment-date"
              className="mb-1.5 block text-sm font-bold leading-5 text-[#0A0A0A]"
            >
              Payment Date
            </label>
            <CalendarDateInput
              id="member-manual-payment-date"
              ariaLabel="Payment Date"
              mode="fullDate"
              iconPosition="left"
              displayFormat="long"
              value={manualPaymentDraft.receivedDate}
              maxDate={getMemberManualPaymentLatestDate({
                timeZone: chamberSettings?.setup.timeZone,
              })}
              onChange={(receivedDate) =>
                setManualPaymentDraft((current) => ({
                  ...current,
                  receivedDate,
                }))
              }
              disabled={recordingMemberPayment}
            />
          </div>
          <TextField
            label="Reference"
            value={manualPaymentDraft.reference}
            onChange={(event) =>
              setManualPaymentDraft((current) => ({
                ...current,
                reference: event.target.value,
              }))
            }
            fullWidth
            disabled={recordingMemberPayment}
          />
          {manualPaymentError ? (
            <Alert severity="error">{manualPaymentError}</Alert>
          ) : null}
        </div>
      </DesignDialog>

      <SendCredentialsDialog
        open={Boolean(credentialModalMode || previewEmailMode)}
        mode={previewEmailMode ? "preview" : "options"}
        disabled={Boolean(getCredentialActionBlock())}
        loading={credentialOperationLoading || Boolean(previewEmailMode && previewEmailLoading)}
        error={credentialActionError || getCredentialActionBlock() || credentialEmailPreview.error}
        subject={credentialEmailPreview.subject}
        html={credentialEmailPreview.html}
        recipientName={memberAdminName}
        recipientEmail={memberAdminEmail}
        onDownloadCsv={() => void downloadCredentials()}
        onDownloadEmail={() => void downloadCredentials("html")}
        onSend={() => void sendCredentialsEmail()}
        onPreview={() => {
          const blockedReason = getCredentialActionBlock();
          if (blockedReason) { setCredentialActionError(blockedReason); return; }
          setCredentialActionError("");
          setPreviewEmailMode(credentialModalMode || "sendCredentials");
        }}
        onClose={() => {
          if (previewEmailMode) setPreviewEmailMode(null);
          else handleCloseCredentialModal();
        }}
      />

      <MemberActionSuccessDialog
        state={successDialog}
        onClose={() => setSuccessDialog(null)}
      />

      {member && (
        <UpdateCreditCardDialog
          chamberId={chamberId}
          memberId={member.id}
          memberName={member.name || "This member"}
          open={creditCardDialogOpen}
          onClose={() => setCreditCardDialogOpen(false)}
          onSaved={({ shortLabel }) => {
            setCreditCardDialogOpen(false);
            setSuccessDialog({ ...getUpdateCreditCardSuccessDialog(shortLabel), tone: "green" });
            // The card update advances the member revision; reload it so a
            // later Step 5 save does not report a changed member.
            void refetchDetail();
          }}
        />
      )}

      <DesignDialog
        open={affiliatePrompt === "none"}
        onClose={() => setAffiliatePrompt(null)}
        title="No Affiliated Member Selected"
        message={getNoAffiliatedMemberMessage(memberDisplayName)}
        highlights={[memberDisplayName]}
        actions={<DesignDialogButton tone="dark" onClick={() => setAffiliatePrompt(null)}>OK</DesignDialogButton>}
      />
      <DesignDialog
        open={affiliatePrompt === "confirm"}
        onClose={() => setAffiliatePrompt(null)}
        title="Invoice Affiliated Primary Company"
        message={getAffiliatedPrimaryCompanyConfirmMessage({ memberName: memberDisplayName, primaryCompanyName: affiliatedPrimaryCompanyName })}
        highlights={[affiliatedPrimaryCompanyName, memberDisplayName]}
        actions={<>
          <DesignDialogButton tone="dark" style={{ backgroundColor: "white", color: "#0A0A0A", border: "1px solid #9E9E9E" }} onClick={() => setAffiliatePrompt(null)}>Cancel</DesignDialogButton>
          <DesignDialogButton tone="dark" onClick={() => {
            setAffiliatePrompt(null);
            setMembershipAdjustmentErrors((currentErrors) =>
              Object.fromEntries(
                Object.entries(currentErrors).filter(
                  ([key]) => key !== "billingResponsibility" && !key.startsWith("thirdPartyBillingSnapshot.")
                )
              )
            );
            updateMembershipAdjustmentDraft({
              billingResponsibility: "affiliatedPrimaryCompany",
              thirdPartyBillingSnapshot:
                getAffiliatedPrimaryCompanyBillingSnapshot(affiliatedPrimaryMemberData?.getCmpMemberDetail.member) || {
                  ...emptyMemberThirdPartyBillingSnapshot(),
                  customerName: affiliatedPrimaryCompanyName,
                },
              thirdPartyPayerName: affiliatedPrimaryCompanyName,
            });
          }}>Confirm</DesignDialogButton>
        </>}
      />
      <DesignDialog open={affinityConfirmOpen} onClose={() => setAffinityConfirmOpen(false)} icon="question" title="Affinity Program Partner" message="Do you want to add this Affinity Program to this member?" actions={<><DesignDialogButton disabled={isReadOnly} onClick={handleConfirmAffinityEnrollment}>Proceed</DesignDialogButton><DesignDialogButton tone="dark" onClick={() => setAffinityConfirmOpen(false)}>Cancel</DesignDialogButton></>} />

      <DesignDialog open={!!editingAffinityEnrollment} onClose={() => setEditingAffinityEnrollment(null)} size="wide" title={`Update ${editingAffinityEnrollment?.affinityProgramPartnerName || ""}`} actions={<><DesignDialogButton tone="dark" style={{ backgroundColor: "white", color: "#0A0A0A", border: "1px solid #C7C7C7" }} onClick={() => setEditingAffinityEnrollment(null)}>Cancel</DesignDialogButton><DesignDialogButton disabled={isReadOnly} onClick={handleSaveEditingAffinityEnrollment}>Update</DesignDialogButton></>}>
        <div ref={affinityDialogContentRef} className="w-full text-left">
          {editingAffinityEnrollment && (
            <div className="space-y-4 pt-2">
              <RadioChoiceGroup
                label="Billing Options"
                options={affinityBillingOptions}
                value={editingAffinityEnrollment.billingOption}
                baseline={formState.affinityProgramEnrollments.find((enrollment) => enrollment.id === editingAffinityEnrollment.id)?.billingOption}
                disabled={isReadOnly}
                onChange={(value) =>
                  handleUpdateEditingAffinityEnrollment("billingOption", value)
                }
                columns="wide"
                renderOptionLabel={renderAffinityBillingOptionLabel}
              />
              <FormSelectField
                id="edit-affinity-status"
                label="Status"
                menuContainer={() => affinityDialogContentRef.current}
                value={editingAffinityEnrollment.status}
                containerClassName={editingAffinityEnrollment.status !== formState.affinityProgramEnrollments.find((enrollment) => enrollment.id === editingAffinityEnrollment.id)?.status ? "member-field-dirty" : ""}
                disabled={isReadOnly}
                onChange={(value) =>
                  handleUpdateEditingAffinityEnrollment("status", value)
                }
                options={affinityEnrollmentStatusOptions.map((option) => ({
                  value: option,
                  label: <AffinityEnrollmentStatusIndicator status={option} />,
                }))}
                placeholder="Select status"
                selectRenderValue={(value) =>
                  value ? (
                    <AffinityEnrollmentStatusIndicator status={value} />
                  ) : (
                    "Select status"
                  )
                }
              />
              <FormTextField
                id="edit-affinity-notes"
                label="Notes"
                value={editingAffinityEnrollment.notes}
                containerClassName={editingAffinityEnrollment.notes !== formState.affinityProgramEnrollments.find((enrollment) => enrollment.id === editingAffinityEnrollment.id)?.notes ? "member-field-dirty" : ""}
                disabled={isReadOnly}
                onChange={(value) =>
                  handleUpdateEditingAffinityEnrollment("notes", value)
                }
                placeholder="Enter Notes"
                multiline
                rows={5}
              />
            </div>
          )}
        </div>
      </DesignDialog>
    </MemberDesignSurface>
  );
};

export default UpdateMember;
